import React, { useState, useEffect } from 'react';
import { Download, Plus, ArrowLeft, RefreshCw, RotateCcw, Trash2, Sparkles, Eye, EyeOff, CheckCircle2, Search, ExternalLink, Building2, Check, Copy, Zap, AlertTriangle, Wrench } from 'lucide-react';
import { KeywordResearchProject, SeedKeywordItem, KeywordVariationItem, CompetitorKeywordItem, MasterKeywordItem, AuditInstance, WebsiteContentKeywordItem, DecisionLogEntry, ToolKeywordItem } from '../../types';
import { KeywordResearchStorageService } from '../../services/keywordResearchStorageService';
import { KeywordPdfService } from '../../services/keywordPdfService';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { KeywordStepGuide } from './KeywordStepGuide';
import { LiveReportPanel } from './LiveReportPanel';
import { ServicePageWorkstation } from './ServicePageWorkstation';
import { SeoMetadataWorkstation } from './SeoMetadataWorkstation';
import { ContentPlacementWorkstation } from './ContentPlacementWorkstation';
import { QuestionAeoWorkstation } from './QuestionAeoWorkstation';
import { FaqSchemaWorkstation } from './FaqSchemaWorkstation';
import { DualReportExportWorkstation } from './DualReportExportWorkstation';
import { KeywordCapsule } from '../UI/KeywordCapsule';

interface KeywordResearchWorkspaceProps {
  project: KeywordResearchProject;
  audits: AuditInstance[];
  onSaveProject: (project: KeywordResearchProject) => void;
  onBackToDashboard: () => void;
}

export const KeywordResearchWorkspace: React.FC<KeywordResearchWorkspaceProps> = ({
  project,
  audits,
  onSaveProject,
  onBackToDashboard,
}) => {
  const [activeStep, setActiveStep] = useState<number>(project.currentPhase || 0);
  const [localProject, setLocalProject] = useState<KeywordResearchProject>(project);
  const [showLiveReport, setShowLiveReport] = useState<boolean>(false);
  const [importedStatus, setImportedStatus] = useState(false);
  const [seedIntentVerified, setSeedIntentVerified] = useState<boolean>(false);
  const [step5AiModalMode, setStep5AiModalMode] = useState<'serp' | 'webpage' | null>(null);
  const [step5PastedText, setStep5PastedText] = useState('');
  const [step5Copied, setStep5Copied] = useState(false);
  const [step5ImportStatus, setStep5ImportStatus] = useState('');
  const checkedSerpTerms = localProject.checkedSerpTerms || [];
  const checkedToolTerms = localProject.checkedToolTerms || [];

  const toggleCheckedSerpTerm = (term: string) => {
    const current = localProject.checkedSerpTerms || [];
    const updated = current.includes(term)
      ? current.filter(t => t !== term)
      : [...current, term];
    updateProject({ ...localProject, checkedSerpTerms: updated });
  };

  const toggleCheckedToolTerm = (term: string) => {
    const current = localProject.checkedToolTerms || [];
    const updated = current.includes(term)
      ? current.filter(t => t !== term)
      : [...current, term];
    updateProject({ ...localProject, checkedToolTerms: updated });
  };

  // Step 9 AI Geo-Targeting & Competitor Cleaning State
  const [showStep9GeoModal, setShowStep9GeoModal] = useState(false);
  const [step9GeoTargetLoc, setStep9GeoTargetLoc] = useState(localProject.businessProfile.primaryLocation || 'Scottsdale, AZ');
  const [step9ExcludeCompetitors, setStep9ExcludeCompetitors] = useState('Sunbros Cleaning, Sunbros');
  const [step9TargetNeighborhoods, setStep9TargetNeighborhoods] = useState('Old Town Scottsdale, North Scottsdale');
  const [step9AiPastedResponse, setStep9AiPastedResponse] = useState('');
  const [step9AiImportStatus, setStep9AiImportStatus] = useState('');
  const [step9PromptCopied, setStep9PromptCopied] = useState(false);

  // Form State Buffers
  const [confirmedServicesText, setConfirmedServicesText] = useState<string>(
    project.businessProfile.confirmedServices.join(', ')
  );
  const [newServiceInput, setNewServiceInput] = useState<string>('');
  const [showBulkServices, setShowBulkServices] = useState<boolean>(false);
  const [singleToolVol, setSingleToolVol] = useState('');
  
  // Step 6 Dual Box State Buffers
  const [box1ToolName, setBox1ToolName] = useState('SEMrush');
  const [box1CompName, setBox1CompName] = useState('');
  const [box1CompUrl, setBox1CompUrl] = useState('');
  const [box1Kw, setBox1Kw] = useState('');
  const [box1Intent, setBox1Intent] = useState('Commercial');

  const [box2ToolName, setBox2ToolName] = useState('SEMrush Keyword Magic Tool');
  const [box2SeedKw, setBox2SeedKw] = useState('');
  const [box2Location, setBox2Location] = useState(
    project.businessProfile.primaryLocation || project.businessProfile.confirmedServices[0] || 'United States'
  );
  const [box2Kw, setBox2Kw] = useState('');
  const [box2Intent, setBox2Intent] = useState('Commercial');

  const [singleAutoLetter, setSingleAutoLetter] = useState('A');
  const [singleRelatedSource, setSingleRelatedSource] = useState('Bottom of SERP');
  const [step7Skipped, setStep7Skipped] = useState<boolean>(false);
  const [step8Skipped, setStep8Skipped] = useState<boolean>(false);
  const [copyBatchIndex, setCopyBatchIndex] = useState<number>(0);
  const [copyBatchStatus, setCopyBatchStatus] = useState<string>('');
  const [kePastedText, setKePastedText] = useState<string>('');
  const [keImportNotice, setKeImportNotice] = useState<string>('');

  // Step 10 3-Stage Guided Workflow State
  const [step10Stage, setStep10Stage] = useState<1 | 2 | 3>(1);
  const [step10SearchQuery, setStep10SearchQuery] = useState<string>('');
  const [step10SelectedPrimary, setStep10SelectedPrimary] = useState<string>('');
  const [step10SelectedSecondary, setStep10SelectedSecondary] = useState<string>('');
  const [step10SelectedTertiary, setStep10SelectedTertiary] = useState<string>('');



  // Steps 11–18 Workstation State

  useEffect(() => {
    setLocalProject(project);
    if (project.currentPhase !== undefined) {
      setActiveStep(project.currentPhase);
    }
    setConfirmedServicesText(project.businessProfile.confirmedServices.join(', '));
  }, [project.id]);

  const linkedAudit = audits.find(a => a.id === localProject.linkedAuditId);

  // Helper to sanitize markdown links like [https://domain.com](https://domain.com) into clean URLs/domains
  const sanitizeUrlOrDomain = (raw?: string): string => {
    if (!raw) return '';
    let s = raw.trim();

    // Match markdown link syntax: [text](https://...) or [https://...](https://...)
    const mdMatch = s.match(/\[.*?\]\((https?:\/\/[^\s\)]+)\)/i);
    if (mdMatch && mdMatch[1]) {
      return mdMatch[1].trim();
    }

    // Match bracketed URL: [https://...]
    const bracketMatch = s.match(/\[(https?:\/\/[^\s\]]+)\]/i);
    if (bracketMatch && bracketMatch[1]) {
      return bracketMatch[1].trim();
    }

    // Remove any remaining leading/trailing brackets or parens
    s = s.replace(/^\[+/, '').replace(/\]+$/, '').replace(/^\(+/, '').replace(/\)+$/, '').trim();

    return s;
  };

  // Helper to accumulate keywords up to a specific step with case-insensitive deduplication
  const getAccumulatedKeywords = (upToStep: number) => {
    const all: { keyword: string; stepName: string; originalStep: string; intent?: string }[] = [];

    // Step 2
    if (upToStep >= 2) {
      localProject.seedKeywords.forEach(s => {
        if (s.seedKeyword && s.seedKeyword.trim()) {
          all.push({ keyword: s.seedKeyword.trim(), stepName: 'Step 2: Seed Keywords', originalStep: 'Step 2', intent: 'Commercial' });
        }
      });
    }

    // Step 3
    if (upToStep >= 3) {
      localProject.variations.forEach(v => {
        if (v.keyword && v.keyword.trim() && !v.reason?.includes('Google SERP')) {
          all.push({ keyword: v.keyword.trim(), stepName: 'Step 3: Keyword Variations', originalStep: 'Step 3', intent: v.intent });
        }
      });
    }

    // Step 4
    if (upToStep >= 4) {
      localProject.websiteContentKeywords.forEach(w => {
        if (w.keyword && w.keyword.trim()) {
          all.push({ keyword: w.keyword.trim(), stepName: `Step 4: Client Website (${w.sourcePage || 'Page'})`, originalStep: 'Step 4', intent: w.intent });
        }
      });
    }

    // Step 5
    if (upToStep >= 5) {
      localProject.competitorKeywords.forEach(c => {
        if (c.keyword && c.keyword.trim()) {
          const stepLabel = c.service.startsWith('SERP') || ['Google SERP', 'People Also Ask', 'Related Searches'].includes(c.competitorName)
            ? 'Step 5: Discovered SERP Keyword'
            : `Step 5: Competitor Webpage (${c.competitorName})`;
          all.push({ keyword: c.keyword.trim(), stepName: stepLabel, originalStep: 'Step 5', intent: c.searchIntent });
        }
      });
    }

    // Step 6
    if (upToStep >= 6) {
      localProject.toolKeywords.forEach(t => {
        if (t.keyword && t.keyword.trim()) {
          const compLabel = t.competitorName ? ` (${t.competitorName})` : '';
          all.push({ keyword: t.keyword.trim(), stepName: `Step 6: SEMrush${compLabel}`, originalStep: 'Step 6', intent: t.intent });
        }
      });
    }

    // Step 7
    if (upToStep >= 7) {
      localProject.autocompleteKeywords.forEach(a => {
        if (a && a.trim()) {
          all.push({ keyword: a.trim(), stepName: 'Step 7: Google Autocomplete', originalStep: 'Step 7', intent: 'Commercial' });
        }
      });
    }

    // Step 8
    if (upToStep >= 8) {
      localProject.variations.forEach(v => {
        if (v.keyword && v.keyword.trim() && v.reason?.includes('Google SERP')) {
          all.push({ keyword: v.keyword.trim(), stepName: 'Step 8: Related Searches / PAA', originalStep: 'Step 8', intent: v.intent });
        }
      });
    }

    // Deduplicate case-insensitively
    const seen = new Set<string>();
    const uniqueList: typeof all = [];
    let duplicatesCount = 0;

    all.forEach(item => {
      const key = item.keyword.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        uniqueList.push(item);
      } else {
        duplicatesCount++;
      }
    });

    return { uniqueList, totalRawCount: all.length, duplicatesCount };
  };

  // Helper to get raw un-deduplicated list of all keywords accumulated from Steps 1-8 for Section 1
  const getRawAccumulatedKeywords = () => {
    const all: { keyword: string; stepName: string; originalStep: string; intent?: string }[] = [];

    // Step 2
    localProject.seedKeywords.forEach(s => {
      if (s.seedKeyword && s.seedKeyword.trim()) {
        all.push({ keyword: s.seedKeyword.trim(), stepName: 'Step 2: Seed Keywords', originalStep: 'Step 2', intent: 'Commercial' });
      }
    });

    // Step 3
    localProject.variations.forEach(v => {
      if (v.keyword && v.keyword.trim() && !v.reason?.includes('Google SERP')) {
        all.push({ keyword: v.keyword.trim(), stepName: 'Step 3: Keyword Variations', originalStep: 'Step 3', intent: v.intent });
      }
    });

    // Step 4
    localProject.websiteContentKeywords.forEach(w => {
      if (w.keyword && w.keyword.trim()) {
        all.push({ keyword: w.keyword.trim(), stepName: `Step 4: Client Website (${w.sourcePage || 'Page'})`, originalStep: 'Step 4', intent: w.intent });
      }
    });

    // Step 5
    localProject.competitorKeywords.forEach(c => {
      if (c.keyword && c.keyword.trim()) {
        const stepLabel = c.service.startsWith('SERP') || ['Google SERP', 'People Also Ask', 'Related Searches'].includes(c.competitorName)
          ? 'Step 5: Discovered SERP Keyword'
          : `Step 5: Competitor Webpage (${c.competitorName})`;
        all.push({ keyword: c.keyword.trim(), stepName: stepLabel, originalStep: 'Step 5', intent: c.searchIntent });
      }
    });

    // Step 6
    localProject.toolKeywords.forEach(t => {
      if (t.keyword && t.keyword.trim()) {
        const compLabel = t.competitorName ? ` (${t.competitorName})` : '';
        all.push({ keyword: t.keyword.trim(), stepName: `Step 6: SEMrush${compLabel}`, originalStep: 'Step 6', intent: t.intent });
      }
    });

    // Step 7
    localProject.autocompleteKeywords.forEach(a => {
      if (a && a.trim()) {
        all.push({ keyword: a.trim(), stepName: 'Step 7: Google Autocomplete', originalStep: 'Step 7', intent: 'Commercial' });
      }
    });

    // Step 8
    localProject.variations.forEach(v => {
      if (v.keyword && v.keyword.trim() && v.reason?.includes('Google SERP')) {
        all.push({ keyword: v.keyword.trim(), stepName: 'Step 8: Related Searches / PAA', originalStep: 'Step 8', intent: v.intent });
      }
    });

    return all;
  };

  // Helper to look up search volume status for any keyword across project datasets
  const getVolumeForKeyword = (kw: string) => {
    const kLower = kw.toLowerCase();

    // Check variations
    const vMatch = localProject.variations.find(v => v.keyword.toLowerCase() === kLower);
    if (vMatch && vMatch.searchVolumeStatus === 'collected') {
      return { volume: vMatch.searchVolume, status: 'collected' as const };
    }

    // Check seedKeywords
    const sMatch = localProject.seedKeywords.find(s => s.seedKeyword.toLowerCase() === kLower);
    if (sMatch && (sMatch as any).searchVolumeStatus === 'collected') {
      return { volume: (sMatch as any).searchVolume, status: 'collected' as const };
    }

    // Check websiteContentKeywords
    const wMatch = localProject.websiteContentKeywords.find(w => w.keyword.toLowerCase() === kLower);
    if (wMatch && (wMatch as any).searchVolumeStatus === 'collected') {
      return { volume: (wMatch as any).searchVolume, status: 'collected' as const };
    }

    // Check competitorKeywords
    const cMatch = localProject.competitorKeywords.find(c => c.keyword.toLowerCase() === kLower);
    if (cMatch && (cMatch as any).searchVolumeStatus === 'collected') {
      return { volume: (cMatch as any).searchVolume, status: 'collected' as const };
    }

    // Check toolKeywords
    const tMatch = localProject.toolKeywords.find(t => t.keyword.toLowerCase() === kLower);
    if (tMatch && (tMatch as any).searchVolumeStatus === 'collected') {
      return { volume: (tMatch as any).searchVolume, status: 'collected' as const };
    }

    return { volume: undefined, status: 'not_collected' as const };
  };

  // Helper to manually update individual keyword volume
  const handleUpdateIndividualVolume = (kw: string, rawVal: string) => {
    const valNum = rawVal !== '' ? parseInt(rawVal, 10) : undefined;
    const kLower = kw.toLowerCase();

    let foundInVar = false;
    const updatedVar = localProject.variations.map(v => {
      if (v.keyword.toLowerCase() === kLower) {
        foundInVar = true;
        return { ...v, searchVolume: valNum, searchVolumeStatus: (valNum !== undefined ? 'collected' : 'not_collected') as any };
      }
      return v;
    });

    const updatedSeeds = localProject.seedKeywords.map(s => {
      if (s.seedKeyword.toLowerCase() === kLower) {
        return { ...s, searchVolume: valNum, searchVolumeStatus: (valNum !== undefined ? 'collected' : 'not_collected') as any };
      }
      return s;
    });

    const updatedWeb = localProject.websiteContentKeywords.map(w => {
      if (w.keyword.toLowerCase() === kLower) {
        return { ...w, searchVolume: valNum, searchVolumeStatus: (valNum !== undefined ? 'collected' : 'not_collected') as any };
      }
      return w;
    });

    const updatedComp = localProject.competitorKeywords.map(c => {
      if (c.keyword.toLowerCase() === kLower) {
        return { ...c, searchVolume: valNum, searchVolumeStatus: (valNum !== undefined ? 'collected' : 'not_collected') as any };
      }
      return c;
    });

    const updatedTool = localProject.toolKeywords.map(t => {
      if (t.keyword.toLowerCase() === kLower) {
        return { ...t, searchVolume: valNum, searchVolumeStatus: (valNum !== undefined ? 'collected' : 'not_collected') as any };
      }
      return t;
    });

    let finalVars = updatedVar;
    if (!foundInVar && valNum !== undefined) {
      const newVarItem: KeywordVariationItem = {
        id: `vol-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
        keyword: kw,
        variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Service',
        searchVolume: valNum,
        searchVolumeStatus: 'collected',
        intent: 'Commercial',
        serpSimilarity: 'High',
        separateTarget: false,
        reason: 'Search volume collected',
        verificationStatus: 'user_verified'
      };
      finalVars = [...updatedVar, newVarItem];
    }

    updateProject({
      ...localProject,
      seedKeywords: updatedSeeds,
      variations: finalVars,
      websiteContentKeywords: updatedWeb,
      competitorKeywords: updatedComp,
      toolKeywords: updatedTool,
    });
  };

  // Helper to mark a list of keywords as copied: auto-fills searchVolume to 0 by default if pending, and ticks them permanently
  const handleMarkBatchCopied = (keywords: string[]) => {
    if (!keywords || keywords.length === 0) return;
    const kLowers = new Set(keywords.map(k => k.toLowerCase().trim()).filter(Boolean));

    // Persistent checked terms tracking
    const currentChecked = localProject.checkedToolTerms || [];
    const newChecked = Array.from(new Set([...currentChecked, ...keywords.map(k => k.trim())]));

    const updatedVar = localProject.variations.map(v => {
      if (kLowers.has(v.keyword.toLowerCase())) {
        const vol = v.searchVolume !== undefined ? v.searchVolume : 0;
        return { ...v, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return v;
    });

    const updatedSeeds = localProject.seedKeywords.map(s => {
      if (kLowers.has(s.seedKeyword.toLowerCase())) {
        const vol = (s as any).searchVolume !== undefined ? (s as any).searchVolume : 0;
        return { ...s, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return s;
    });

    const updatedWeb = localProject.websiteContentKeywords.map(w => {
      if (kLowers.has(w.keyword.toLowerCase())) {
        const vol = (w as any).searchVolume !== undefined ? (w as any).searchVolume : 0;
        return { ...w, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return w;
    });

    const updatedComp = localProject.competitorKeywords.map(c => {
      if (kLowers.has(c.keyword.toLowerCase())) {
        const vol = (c as any).searchVolume !== undefined ? (c as any).searchVolume : 0;
        return { ...c, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return c;
    });

    const updatedTool = localProject.toolKeywords.map(t => {
      if (kLowers.has(t.keyword.toLowerCase())) {
        const vol = (t as any).searchVolume !== undefined ? (t as any).searchVolume : 0;
        return { ...t, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return t;
    });

    updateProject({
      ...localProject,
      variations: updatedVar,
      seedKeywords: updatedSeeds,
      websiteContentKeywords: updatedWeb,
      competitorKeywords: updatedComp,
      toolKeywords: updatedTool,
      checkedToolTerms: newChecked,
    });
  };

  // Helper to edit a keyword phrase across all project datasets
  const handleEditKeywordPhrase = (oldKw: string, newKw: string) => {
    const cleanNew = newKw.trim();
    if (!cleanNew) return;
    const oldLower = oldKw.trim().toLowerCase();
    const newLower = cleanNew.toLowerCase();
    if (oldLower === newLower) return;

    let matched = false;

    const updatedSeeds = localProject.seedKeywords.map(s => {
      if (s.seedKeyword.trim().toLowerCase() === oldLower) {
        matched = true;
        return { ...s, seedKeyword: cleanNew };
      }
      return s;
    });

    const updatedVars = localProject.variations.map(v => {
      if (v.keyword.trim().toLowerCase() === oldLower) {
        matched = true;
        return { ...v, keyword: cleanNew };
      }
      return v;
    });

    const updatedWeb = localProject.websiteContentKeywords.map(w => {
      if (w.keyword.trim().toLowerCase() === oldLower) {
        matched = true;
        return { ...w, keyword: cleanNew };
      }
      return w;
    });

    const updatedComp = localProject.competitorKeywords.map(c => {
      if (c.keyword.trim().toLowerCase() === oldLower) {
        matched = true;
        return { ...c, keyword: cleanNew };
      }
      return c;
    });

    const updatedTool = localProject.toolKeywords.map(t => {
      if (t.keyword.trim().toLowerCase() === oldLower) {
        matched = true;
        return { ...t, keyword: cleanNew };
      }
      return t;
    });

    const updatedAuto = localProject.autocompleteKeywords.map(a => {
      if (a.trim().toLowerCase() === oldLower) {
        matched = true;
        return cleanNew;
      }
      return a;
    });

    let finalVars = updatedVars;
    if (!matched) {
      const fallbackItem: KeywordVariationItem = {
        id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
        keyword: cleanNew,
        variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Service',
        searchVolumeStatus: 'not_collected',
        intent: 'Commercial',
        serpSimilarity: 'High',
        separateTarget: false,
        reason: 'Manually edited in Step 9',
        verificationStatus: 'user_verified'
      };
      finalVars = [...updatedVars, fallbackItem];
    }

    updateProject({
      ...localProject,
      seedKeywords: updatedSeeds,
      variations: finalVars,
      websiteContentKeywords: updatedWeb,
      competitorKeywords: updatedComp,
      toolKeywords: updatedTool,
      autocompleteKeywords: updatedAuto,
    });
  };

  // Helper to normalize keyword strings for 100% accurate matching (strips hidden spaces, newlines, zero-width spaces, casing)
  const normalizeKw = (raw?: string): string => {
    if (!raw) return '';
    return raw.replace(/[\s\u200b\uFEFF\u00A0]+/g, ' ').trim().toLowerCase();
  };

  // Helper to delete a keyword phrase across all project datasets
  const handleDeleteKeyword = (targetKw: string) => {
    if (!targetKw) return;
    const targetNorm = normalizeKw(targetKw);

    const updatedSeeds = localProject.seedKeywords.filter(s =>
      normalizeKw(s.seedKeyword) !== targetNorm && normalizeKw((s as any).keyword) !== targetNorm
    );

    const updatedVars = localProject.variations.filter(v =>
      normalizeKw(v.keyword) !== targetNorm
    );

    const updatedWeb = localProject.websiteContentKeywords.filter(w =>
      normalizeKw(w.keyword) !== targetNorm
    );

    const updatedComp = localProject.competitorKeywords.filter(c =>
      normalizeKw(c.keyword) !== targetNorm
    );

    const updatedTool = localProject.toolKeywords.filter(t =>
      normalizeKw(t.keyword) !== targetNorm
    );

    const updatedAuto = localProject.autocompleteKeywords.filter(a =>
      normalizeKw(a) !== targetNorm
    );

    const updatedProj = {
      ...localProject,
      seedKeywords: updatedSeeds,
      variations: updatedVars,
      websiteContentKeywords: updatedWeb,
      competitorKeywords: updatedComp,
      toolKeywords: updatedTool,
      autocompleteKeywords: updatedAuto,
    };

    updateProject(updatedProj);

    // Immediately re-chunk remaining keywords, update batch copy preview & copy new 3 terms to clipboard
    setTimeout(() => {
      const all: { keyword: string }[] = [];
      updatedSeeds.forEach(s => s.seedKeyword && all.push({ keyword: s.seedKeyword.trim() }));
      updatedVars.forEach(v => v.keyword && !v.reason?.includes('Google SERP') && all.push({ keyword: v.keyword.trim() }));
      updatedWeb.forEach(w => w.keyword && all.push({ keyword: w.keyword.trim() }));
      updatedComp.forEach(c => c.keyword && all.push({ keyword: c.keyword.trim() }));
      updatedTool.forEach(t => t.keyword && all.push({ keyword: t.keyword.trim() }));
      updatedAuto.forEach(a => a && all.push({ keyword: a.trim() }));
      updatedVars.forEach(v => v.keyword && v.reason?.includes('Google SERP') && all.push({ keyword: v.keyword.trim() }));

      const seen = new Set<string>();
      const remList: string[] = [];
      all.forEach(item => {
        const kNorm = normalizeKw(item.keyword);
        if (kNorm && !seen.has(kNorm)) {
          seen.add(kNorm);
          remList.push(item.keyword);
        }
      });

      const totalB = Math.max(1, Math.ceil(remList.length / 3));
      const activeB = Math.min(copyBatchIndex, totalB - 1);
      setCopyBatchIndex(activeB);

      const next3 = remList.slice(activeB * 3, activeB * 3 + 3);
      const next3Text = next3.join('\n');
      if (next3Text) {
        navigator.clipboard.writeText(next3Text);
        setCopyBatchStatus(`✓ Deleted "${targetKw.trim()}"! Re-paired remaining terms — Batch ${activeB + 1} updated with 3 terms & copied to clipboard 📋`);
      } else {
        setCopyBatchStatus(`✓ Deleted "${targetKw.trim()}"!`);
      }
      setTimeout(() => setCopyBatchStatus(''), 3500);
    }, 50);
  };

  // Helper to auto-deduplicate any repetitive keywords added during manual editing
  const handleDeduplicateStep9Keywords = () => {
    const seenVars = new Set<string>();
    const updatedVars: KeywordVariationItem[] = [];
    localProject.variations.forEach(v => {
      const kLower = v.keyword.trim().toLowerCase();
      if (kLower && !seenVars.has(kLower)) {
        seenVars.add(kLower);
        updatedVars.push({ ...v, keyword: v.keyword.trim() });
      }
    });

    const seenSeeds = new Set<string>();
    const updatedSeeds = localProject.seedKeywords.filter(s => {
      const kLower = s.seedKeyword.trim().toLowerCase();
      if (kLower && !seenSeeds.has(kLower)) {
        seenSeeds.add(kLower);
        return true;
      }
      return false;
    });

    const seenWeb = new Set<string>();
    const updatedWeb = localProject.websiteContentKeywords.filter(w => {
      const kLower = w.keyword.trim().toLowerCase();
      if (kLower && !seenWeb.has(kLower)) {
        seenWeb.add(kLower);
        return true;
      }
      return false;
    });

    const seenComp = new Set<string>();
    const updatedComp = localProject.competitorKeywords.filter(c => {
      const kLower = c.keyword.trim().toLowerCase();
      if (kLower && !seenComp.has(kLower)) {
        seenComp.add(kLower);
        return true;
      }
      return false;
    });

    const seenTool = new Set<string>();
    const updatedTool = localProject.toolKeywords.filter(t => {
      const kLower = t.keyword.trim().toLowerCase();
      if (kLower && !seenTool.has(kLower)) {
        seenTool.add(kLower);
        return true;
      }
      return false;
    });

    const seenAuto = new Set<string>();
    const updatedAuto = localProject.autocompleteKeywords.filter(a => {
      const kLower = a.trim().toLowerCase();
      if (kLower && !seenAuto.has(kLower)) {
        seenAuto.add(kLower);
        return true;
      }
      return false;
    });

    const rawBefore =
      localProject.seedKeywords.length +
      localProject.variations.length +
      localProject.websiteContentKeywords.length +
      localProject.competitorKeywords.length +
      localProject.toolKeywords.length +
      localProject.autocompleteKeywords.length;

    const rawAfter =
      updatedSeeds.length +
      updatedVars.length +
      updatedWeb.length +
      updatedComp.length +
      updatedTool.length +
      updatedAuto.length;

    const removedCount = Math.max(0, rawBefore - rawAfter);

    updateProject({
      ...localProject,
      seedKeywords: updatedSeeds,
      variations: updatedVars,
      websiteContentKeywords: updatedWeb,
      competitorKeywords: updatedComp,
      toolKeywords: updatedTool,
      autocompleteKeywords: updatedAuto,
    });

    setKeImportNotice(
      removedCount > 0
        ? `✓ Cleaned dataset! Filtered out ${removedCount} repetitive keyword(s). All remaining terms are 100% unique.`
        : `✓ Verified! All keywords in your dataset are already 100% unique with 0 duplicates.`
    );
    setTimeout(() => setKeImportNotice(''), 4500);
  };

  // Helper to parse Keywords Everywhere / Tabular search volume results and auto-fill search volumes
  const handleImportKeywordsEverywhereResults = (pastedText: string) => {
    if (!pastedText.trim()) return;

    const lines = pastedText.split('\n');
    const volumeMap = new Map<string, number>();

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Handle tab-separated output: Keyword \t Volume \t CPC \t Competition
      // e.g. "Truck Parts Trinidad and Tobago\t0\t$ 0.00\tLow (0%)"
      const tabParts = trimmed.split('\t');
      if (tabParts.length >= 2) {
        const kw = tabParts[0].trim();
        const volClean = tabParts[1].trim().replace(/,/g, '');
        const volNum = parseInt(volClean, 10);
        if (kw && !isNaN(volNum)) {
          volumeMap.set(kw.toLowerCase(), volNum);
        }
      } else {
        // Fallback matching: Keyword followed by numbers then $ or spaces
        const match = trimmed.match(/^(.*?)\s+([\d,]+)\s+(\$|Low|High|Medium|\d)/i);
        if (match && match[1] && match[2]) {
          const kw = match[1].trim();
          const volNum = parseInt(match[2].replace(/,/g, ''), 10);
          if (kw && !isNaN(volNum)) {
            volumeMap.set(kw.toLowerCase(), volNum);
          }
        }
      }
    });

    if (volumeMap.size === 0) {
      setKeImportNotice('⚠️ No valid keyword volume rows detected. Ensure lines are tab-separated or copied directly from Keywords Everywhere table.');
      setTimeout(() => setKeImportNotice(''), 4000);
      return;
    }

    let updatedCount = 0;

    const updatedSeeds = localProject.seedKeywords.map(s => {
      const vol = volumeMap.get(s.seedKeyword.toLowerCase());
      if (vol !== undefined) {
        updatedCount++;
        return { ...s, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return s;
    });

    const updatedVariations = localProject.variations.map(v => {
      const vol = volumeMap.get(v.keyword.toLowerCase());
      if (vol !== undefined) {
        updatedCount++;
        return { ...v, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return v;
    });

    const updatedWeb = localProject.websiteContentKeywords.map(w => {
      const vol = volumeMap.get(w.keyword.toLowerCase());
      if (vol !== undefined) {
        updatedCount++;
        return { ...w, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return w;
    });

    const updatedComp = localProject.competitorKeywords.map(c => {
      const vol = volumeMap.get(c.keyword.toLowerCase());
      if (vol !== undefined) {
        updatedCount++;
        return { ...c, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return c;
    });

    const updatedTool = localProject.toolKeywords.map(t => {
      const vol = volumeMap.get(t.keyword.toLowerCase());
      if (vol !== undefined) {
        updatedCount++;
        return { ...t, searchVolume: vol, searchVolumeStatus: 'collected' as const };
      }
      return t;
    });

    // Also add any new keywords from volumeMap into variations if they don't exist yet
    const existingKws = new Set([
      ...localProject.seedKeywords.map(s => s.seedKeyword.toLowerCase()),
      ...localProject.variations.map(v => v.keyword.toLowerCase()),
      ...localProject.websiteContentKeywords.map(w => w.keyword.toLowerCase()),
      ...localProject.competitorKeywords.map(c => c.keyword.toLowerCase()),
      ...localProject.toolKeywords.map(t => t.keyword.toLowerCase()),
    ]);

    const newVarItems: KeywordVariationItem[] = [];
    volumeMap.forEach((volNum, kwLower) => {
      if (!existingKws.has(kwLower)) {
        updatedCount++;
        newVarItems.push({
          id: `ke-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
          keyword: kwLower,
          variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Service',
          searchVolume: volNum,
          searchVolumeStatus: 'collected',
          intent: 'Commercial',
          serpSimilarity: 'High',
          separateTarget: false,
          reason: 'Imported via Keywords Everywhere',
          verificationStatus: 'user_verified'
        });
      }
    });

    const updatedProj = {
      ...localProject,
      seedKeywords: updatedSeeds,
      variations: [...updatedVariations, ...newVarItems],
      websiteContentKeywords: updatedWeb,
      competitorKeywords: updatedComp,
      toolKeywords: updatedTool,
    };

    updateProject(updatedProj);
    setKePastedText('');

    // Auto-advance to next batch of 3 terms and auto-copy to clipboard!
    const masterList = getAccumulatedKeywords(8).uniqueList;
    const totalBatches = Math.max(1, Math.ceil(masterList.length / 3));
    const nextBatch = copyBatchIndex + 1;

    if (nextBatch < totalBatches) {
      setCopyBatchIndex(nextBatch);
      const nextBatchItems = masterList.slice(nextBatch * 3, nextBatch * 3 + 3);
      const nextBatchText = nextBatchItems.map(item => item.keyword).join('\n');
      if (nextBatchText) {
        navigator.clipboard.writeText(nextBatchText);
        setKeImportNotice(`✓ Volumes filled for ${updatedCount} term(s)! Auto-advanced to Batch ${nextBatch + 1} (Terms ${nextBatch * 3 + 1}–${nextBatch * 3 + nextBatchItems.length}) & copied 3 terms to clipboard 📋`);
      } else {
        setKeImportNotice(`✓ Successfully imported & filled Search Volumes for ${updatedCount} keyword(s)!`);
      }
    } else {
      setKeImportNotice(`✓ Successfully imported & filled Search Volumes for ${updatedCount} keyword(s)! All batches completed 🎉`);
    }

    setTimeout(() => setKeImportNotice(''), 4500);
  };

  // Helper to change step and persist currentPhase instantly
  const handleStepChange = (newStep: number) => {
    setActiveStep(newStep);
    const updated = { ...localProject, currentPhase: newStep };
    setLocalProject(updated);
    onSaveProject(updated);
    KeywordResearchStorageService.saveProject(updated);
  };

  // Persistence handler
  const updateProject = (updated: KeywordResearchProject) => {
    const withPhase = { ...updated, currentPhase: activeStep };
    setLocalProject(withPhase);
    onSaveProject(withPhase);
    KeywordResearchStorageService.saveProject(withPhase);
  };

  // Log decision helper
  const logDecision = (phase: string, keywordOrTopic: string, decisionMade: string, reason: string, evidence: string = '') => {
    const newEntry: DecisionLogEntry = {
      id: `dec-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      timestamp: new Date().toISOString(),
      phase,
      keywordOrTopic,
      decisionMade,
      reason,
      evidence,
    };
    return [newEntry, ...localProject.decisionLog];
  };

  // Dynamic Controlled AI Prompt Generator for Step 3 Variations
  const generateAiPromptStep3 = () => {
    const bName = localProject.businessProfile.businessName || localProject.clientName;
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';
    
    // Automatically fill seed keywords from Step 2 (or confirmed services + location)
    const seedTerms = localProject.seedKeywords.length > 0
      ? localProject.seedKeywords.map(s => s.seedKeyword)
      : (localProject.businessProfile.confirmedServices.length > 0
          ? localProject.businessProfile.confirmedServices.map(s => `${s} ${loc}`)
          : [`Primary Service ${loc}`]);

    const formattedSeeds = seedTerms.map((s, i) => `${i + 1}. ${s}`).join('\n');

    return `You are a Senior Local SEO Strategist.

Business: "${bName}"
Location: "${loc}"

The following are confirmed services offered by the client:

${formattedSeeds}

TASK:
Generate natural search-query variations for EACH seed keyword.

The variations should represent genuine, natural ways a real person might search for the same service.

Use the seed keyword as the foundation and identify natural variations through:

* Word-order changes
* Natural use of location phrases such as "in ${loc}"
* Singular/plural variations where natural
* Natural placement of the location before or after the service
* Other minor wording changes that people would realistically use when searching for the same service

For example:

Seed: General contractors Calgary

Natural variations:

* General contractors Calgary
* General contractors in Calgary
* Calgary General contractors
* General contractor Calgary

The example is only demonstrating the **type of natural variation expected**. Do NOT assume that every seed keyword must have exactly 4 variations.

Generate only variations that are genuinely natural and closely related to the seed keyword. If a seed has only a few meaningful variations, provide only those. Do not force additional variations just to reach a certain number.

DO NOT:

* Invent services that are not listed.
* Change the underlying service intent.
* Add unrelated services.
* Add artificial or awkward keyword combinations.
* Add search volume or CPC.
* Add keyword difficulty.
* Decide Primary / Secondary / Tertiary classification.
* Decide whether keywords should have separate pages.
* Add explanations, commentary, or analysis.

STRICT OUTPUT FORMAT:

Seed: [exact seed keyword]
Variation: [keyword variation]
Variation: [keyword variation]
Variation: [keyword variation]

Continue with the next seed keyword.

Only include the variations themselves. Do not include headings, explanations, tables, or additional information.`;
  };

  // Parser for Step 3 AI Candidate Import (Resilient Multi-Format Parser)
  const handleImportStep3AiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    // Pre-process: insert newlines before inline tokens (Seed:, Variation:, Keyword:, etc.)
    // This handles copy-pasting from UIs or models where newlines were collapsed into spaces
    const raw = pastedText.trim()
      .replace(/([^\n])\s*(Seed(?:\s*keyword)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*(Variation(?:\s*\d+)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*(Keyword(?:\s*\d+)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*(Autocomplete\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*(Related\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    let currentSeed = localProject.seedKeywords[0]?.seedKeyword || 'Core Seed Keyword';

    const parsedPairs: { keyword: string; seed: string }[] = [];
    const seenKeywords = new Set<string>();

    const tryAddCandidate = (kw: string, seed: string) => {
      if (!kw) return;
      let cleaned = kw.trim();
      // Remove surrounding quotes, brackets, and markdown formatting
      cleaned = cleaned.replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      // Remove trailing commentary e.g. "(Exact Match)", "- preposition variation", ": word order"
      cleaned = cleaned.replace(/\s*[\(\[\{](?:location|preposition|word-order|word order|singular|plural|natural|intent|variation|exact match|secondary|primary)[^\)\]\}]*[\)\]\}]/gi, '').trim();
      cleaned = cleaned.replace(/\s*[-–—]\s*(?:location|preposition|word-order|word order|singular|plural|natural|intent|variation|exact match|secondary|primary).*$/gi, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();

      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;

      const lower = cleaned.toLowerCase();
      // Ignore meta lines, instructions, and headers
      if (
        lower.startsWith('seed:') ||
        lower.startsWith('seed keyword:') ||
        lower.startsWith('variation:') ||
        lower.startsWith('natural variation') ||
        lower.startsWith('word-order') ||
        lower.startsWith('preposition') ||
        lower.startsWith('singular') ||
        lower.startsWith('strict output') ||
        lower.startsWith('here is') ||
        lower.startsWith('here are') ||
        lower.startsWith('below are') ||
        lower.startsWith('certainly') ||
        lower.startsWith('sure,') ||
        lower.startsWith('hope this helps') ||
        lower.includes('strict output format') ||
        lower === 'variations' ||
        lower === 'variation' ||
        lower === 'keyword' ||
        lower === 'seed'
      ) {
        return;
      }

      if (!seenKeywords.has(lower)) {
        seenKeywords.add(lower);
        parsedPairs.push({ keyword: cleaned, seed: seed || currentSeed });
      }
    };

    // 1. ATTEMPT JSON PARSING FIRST
    let jsonParsed = false;
    if ((raw.includes('{') && raw.includes('}')) || (raw.includes('[') && raw.includes(']'))) {
      try {
        const jsonMatch = raw.match(/\[[\s\S]*\]/) || raw.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (Array.isArray(parsed)) {
            parsed.forEach((item: any) => {
              if (typeof item === 'string') {
                tryAddCandidate(item, currentSeed);
              } else if (typeof item === 'object' && item !== null) {
                const kw = item.variation || item.keyword || item.phrase || item.term || item.text;
                const itemSeed = item.seed || item.seedKeyword || item.variationOf || currentSeed;
                if (typeof kw === 'string') {
                  tryAddCandidate(kw, itemSeed);
                }
              }
            });
            if (parsedPairs.length > 0) jsonParsed = true;
          } else if (typeof parsed === 'object' && parsed !== null) {
            Object.entries(parsed).forEach(([key, val]) => {
              if (Array.isArray(val)) {
                val.forEach((sub: any) => {
                  const kw = typeof sub === 'string' ? sub : (sub.variation || sub.keyword || sub.phrase);
                  if (typeof kw === 'string') tryAddCandidate(kw, key);
                });
              } else if (typeof val === 'string') {
                tryAddCandidate(val, key);
              }
            });
            if (parsedPairs.length > 0) jsonParsed = true;
          }
        }
      } catch (e) {
        // Fall back to line-by-line parsing
      }
    }

    // 2. LINE-BY-LINE MULTI-FORMAT PARSER (if JSON didn't yield results)
    if (!jsonParsed || parsedPairs.length === 0) {
      const lines = raw.split('\n');

      const findMatchingSeedKeyword = (str: string): string | null => {
        const strLower = str.toLowerCase().trim();
        for (const s of localProject.seedKeywords) {
          const sLower = s.seedKeyword.toLowerCase().trim();
          if (strLower === sLower || strLower.includes(sLower) || sLower.includes(strLower)) {
            return s.seedKeyword;
          }
        }
        return null;
      };

      lines.forEach((line) => {
        let trimmed = line.trim();
        if (!trimmed) return;

        // Ignore markdown separators (e.g. |---|---| or ---)
        if (/^[\-\|\s:_=]+$/.test(trimmed)) return;

        // Markdown Table Row check
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
          const cols = trimmed.split('|').map(c => c.trim()).filter(Boolean);
          if (cols.some(c => /^(seed|variation|keyword|intent|volume)$/i.test(c))) {
            return;
          }
          if (cols.length >= 2) {
            const possibleSeed = cols[0];
            const possibleVar = cols[1];
            if (possibleSeed && possibleVar) {
              tryAddCandidate(possibleVar, possibleSeed);
              return;
            }
          } else if (cols.length === 1) {
            tryAddCandidate(cols[0], currentSeed);
            return;
          }
        }

        // Check if line defines a Seed: "Seed: ...", "**Seed:** ...", "### Seed: ..."
        const seedRegex = /^(?:#{1,6}\s*)?(?:\*{1,2}|_{1,2})?\s*seed(?:\s*keyword)?(?:\s*\d+)?\s*[:\-\s]\s*(?:\*{1,2}|_{1,2})?(.*)$/i;
        const seedMatch = trimmed.match(seedRegex);
        if (seedMatch) {
          let detectedSeed = seedMatch[1].replace(/[*_#"`]/g, '').trim();
          detectedSeed = detectedSeed.replace(/^[:\-\s]+/, '').trim();
          if (/variation\s*[:\-]/i.test(detectedSeed)) {
            detectedSeed = detectedSeed.split(/variation\s*[:\-]/i)[0].trim();
          }
          if (detectedSeed) {
            currentSeed = detectedSeed;
            return;
          }
        }

        // Check if line is a markdown heading for a known seed keyword: e.g. "### 1. Window Cleaning Scottsdale"
        if (/^#{1,6}\s+/.test(trimmed)) {
          const headingText = trimmed.replace(/^#{1,6}\s+/, '').replace(/^[\d+.\-)]\s*/, '').replace(/[*_]/g, '').trim();
          const matchedKnownSeed = findMatchingSeedKeyword(headingText);
          if (matchedKnownSeed) {
            currentSeed = matchedKnownSeed;
            return;
          } else if (headingText.length > 2 && headingText.length < 60) {
            currentSeed = headingText;
            return;
          }
        }

        // Check if line has explicit "Variation: [kw]" prefix
        const varPrefixRegex = /^(?:#{1,6}\s*)?(?:\*{1,2}|_{1,2})?\s*(?:variation|keyword)(?:\s*\d+)?\s*[:\-]\s*(?:\*{1,2}|_{1,2})?(.*)$/i;
        const varMatch = trimmed.match(varPrefixRegex);
        if (varMatch) {
          let kw = varMatch[1].replace(/[*_#"`]/g, '').trim();
          if (/variation\s*[:\-]/i.test(kw)) {
            kw = kw.split(/variation\s*[:\-]/i)[0].trim();
          }
          tryAddCandidate(kw, currentSeed);
          return;
        }

        // Clean markdown bullets or numbered list items
        let lineCandidate = trimmed;
        lineCandidate = lineCandidate.replace(/^[\*\-\+•▪–—]\s*/, '');
        lineCandidate = lineCandidate.replace(/^\d+[\.\)]\s*/, '');
        lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();

        const innerVarMatch = lineCandidate.match(varPrefixRegex);
        if (innerVarMatch) {
          lineCandidate = innerVarMatch[1].replace(/[*_#"`]/g, '').trim();
          if (/variation\s*[:\-]/i.test(lineCandidate)) {
            lineCandidate = lineCandidate.split(/variation\s*[:\-]/i)[0].trim();
          }
        }

        lineCandidate = lineCandidate.replace(/^["'`“‘]+|["'`”’]+$/g, '').trim();

        if (lineCandidate) {
          tryAddCandidate(lineCandidate, currentSeed);
        }
      });
    }

    if (parsedPairs.length === 0) {
      return {
        success: false,
        count: 0,
        error: '⚠️ Could not detect any valid keyword variations. Please ensure your pasted text contains keyword phrases.',
      };
    }

    // Build new items
    const newVariations: KeywordVariationItem[] = [];
    const newMasterItems: MasterKeywordItem[] = [];

    parsedPairs.forEach(({ keyword, seed }) => {
      const item: KeywordVariationItem = {
        id: `var-ai-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        keyword,
        variationOf: seed,
        searchVolumeStatus: 'not_collected',
        intent: 'Commercial',
        serpSimilarity: 'High',
        separateTarget: false,
        reason: 'AI Candidate — Verification Required',
        verificationStatus: 'ai_candidate',
      };
      newVariations.push(item);

      newMasterItems.push({
        id: `master-var-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        keyword,
        searchVolume: 0,
        intent: 'Commercial',
        priority: 'Supporting',
        type: 'Variation',
        variationOf: seed,
        source: 'Step 3 AI Candidate Importer',
        targetPage: `Consolidated under ${seed}`,
        action: 'Evaluate in Step 10',
        status: 'active',
      });
    });

    const newDecisions = logDecision(
      'Step 3: Variations',
      `Imported ${newVariations.length} candidate variations`,
      'AI Candidate Import',
      `Parsed candidate variations. Requires manual volume and SERP verification.`
    );

    updateProject({
      ...localProject,
      variations: [...localProject.variations, ...newVariations],
      masterKeywords: [...localProject.masterKeywords, ...newMasterItems],
      decisionLog: newDecisions,
    });

    return { success: true, count: newVariations.length };
  };

  // Dynamic Controlled AI Prompt Generator for Step 4 Website Screenshot & Content Analysis
  const generateAiPromptStep4 = () => {
    const bName = localProject.businessProfile.businessName || localProject.clientName;
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';

    return `You are a Senior Local SEO Strategist.

Business: ${bName}
Location: ${loc}

TASK:

I am attaching a full-page screenshot of the client's website page.

I will also provide the page's SEO Title and Meta Description manually.

SEO Title: [SEO Title]
Meta Description: [Meta]

Your task is to identify the KEYWORD PHRASES AND SERVICE/PRODUCT PHRASES THAT THE PAGE IS ACTUALLY TARGETING.

This is an EXISTING TARGETING AUDIT.

You are documenting what the page currently targets.

You are NOT performing keyword research.

You are NOT generating keyword variations.

You are NOT recommending keywords.

You are NOT deciding what the page SHOULD target.

━━━━━━━━━━━━━━━━━━
CORE PRINCIPLE
━━━━━━━━━━━━━━━━━━

A word or phrase appearing somewhere on the page does NOT automatically qualify as a keyword target.

Only extract a phrase when the page uses it in a way that reasonably indicates it is describing, promoting, or targeting a service, product, business offering, or meaningful search phrase.

Prioritize phrases that appear in:

* SEO Title
* Meta Description
* H1
* H2/H3
* Hero copy
* Service/product descriptions
* Prominent marketing copy describing an offering

Do NOT treat every noun appearing in body copy as a keyword target.

━━━━━━━━━━━━━━━━━━

1. HIGH-PRIORITY TARGETS
   ━━━━━━━━━━━━━━━━━━

Prioritize:

A. Core service phrases

Example:
Truck Parts

B. Service + location phrases

Example:
Truck Parts Trinidad
Truck Parts in Trinidad

C. Product/service category phrases

Example:
Heavy Duty Truck Parts

D. Product/service + location phrases

Example:
Heavy Duty Truck Parts in Trinidad

E. Clearly promoted commercial offerings

Example:
Truck & Bus Parts in Trinidad

F. Other prominent phrases that clearly communicate what the business sells or provides.

━━━━━━━━━━━━━━━━━━
2. DO NOT EXTRACT GENERIC PRODUCT COMPONENTS
━━━━━━━━━━━━━━━━━━

Do NOT automatically extract individual words simply because they appear in a list or paragraph.

For example, if the page says:

Engine
Oil
Suspension
Steering
Braking
Hubs
Fuel Tanks
Clutch
Cooling
Body Panels

Do NOT automatically output:

Engine
Oil
Suspension
Steering
Braking
Hubs
Fuel
Clutch
Cooling
Body Panels

unless the page clearly presents those terms as standalone products/categories being targeted.

A word appearing as part of a product list is NOT sufficient evidence that it is an SEO keyword target.

━━━━━━━━━━━━━━━━━━
3. PRODUCT CATEGORIES
━━━━━━━━━━━━━━━━━━

A product/category phrase MAY be extracted when:

* It is used as a prominent heading.
* It is presented as a named product category.
* It is clearly promoted as something the business sells.
* It forms a meaningful commercial search phrase.

For example:

"Heavy Duty Truck Parts"

should be extracted.

But:

"Engine"

appearing inside a list of components should generally NOT be extracted.

━━━━━━━━━━━━━━━━━━
4. MARKETING STATEMENTS
━━━━━━━━━━━━━━━━━━

Do NOT extract complete marketing statements simply because they contain nouns.

For example:

"100,000 Parts in Stock"

is not automatically a keyword target.

Do not extract it unless the page clearly uses it as a product/service category or meaningful search phrase.

Similarly, phrases such as:

"Quality Trucking Supplies"

should only be extracted if the phrase clearly represents an actual offering/category rather than generic marketing language.

━━━━━━━━━━━━━━━━━━
5. PRESERVE EXISTING PHRASES
━━━━━━━━━━━━━━━━━━

Do NOT generate new keyword variations.

If the page says:

"Truck Parts in Trinidad"

record:

Truck Parts in Trinidad

Do NOT additionally create:

Trinidad Truck Parts
Truck Parts Trinidad
Truck Parts Trinidad and Tobago

unless those exact phrases are separately represented elsewhere on the page.

The purpose is to record EXISTING targeting, not potential keyword variations.

━━━━━━━━━━━━━━━━━━
6. CAPITALIZATION
━━━━━━━━━━━━━━━━━━

Ignore capitalization differences when determining whether two phrases are duplicates.

For example:

Truck Parts Trinidad

and

Truck Parts trinidad

should be treated as the same phrase.

Do NOT output both merely because capitalization differs.

Normalize extracted keywords to natural lowercase unless capitalization is meaningful.

━━━━━━━━━━━━━━━━━━
7. DUPLICATE / NEAR-DUPLICATE HANDLING
━━━━━━━━━━━━━━━━━━

Do not output the same keyword more than once for the same page element.

If the same phrase appears in multiple page elements, record each location separately only when useful for identifying where the phrase is being targeted.

Example:

SourcePage: Homepage
WhereFound: Title
Keyword: truck parts trinidad

SourcePage: Homepage
WhereFound: H1
Keyword: truck parts in trinidad

These are valid separate entries because the page uses different wording in different SEO elements.

But:

Truck Parts Trinidad
Truck Parts trinidad

should NOT be treated as two different keywords.

━━━━━━━━━━━━━━━━━━
8. COMBINE TERMS INTO MEANINGFUL PHRASES
━━━━━━━━━━━━━━━━━━

Prefer a complete meaningful keyword phrase over isolated words.

For example:

"genuine and non-genuine Heavy Duty Truck Parts In Trinidad"

should be evaluated for meaningful keyword targets such as:

heavy duty truck parts in trinidad

Do NOT automatically output:

genuine
non-genuine
heavy duty
truck
parts

unless those terms independently function as meaningful targets.

━━━━━━━━━━━━━━━━━━
9. LOCATION
━━━━━━━━━━━━━━━━━━

Only record location + service/product combinations that are actually represented on the page.

Do not create location variations.

Do not introduce:

* Port of Spain
* San Fernando
* Chaguanas
* Tobago
* Other cities or regions

unless explicitly present on the page.

━━━━━━━━━━━━━━━━━━
10. FINAL TEST FOR EVERY KEYWORD
━━━━━━━━━━━━━━━━━━

Before outputting a keyword, ask:

"Does this page appear to be intentionally targeting this phrase as a service, product, category, or meaningful search query?"

If YES → include it.

If it is merely:

* a generic word,
* an incidental mention,
* a component in a list,
* a marketing statement,
* a number/statistic,
* a descriptive adjective,
* or an unrelated word,

→ exclude it.

When uncertain, prefer NOT to include it.

The objective is a CLEAN, HIGH-CONFIDENCE inventory of existing keyword targeting, not a comprehensive extraction of every commercially relevant word on the page.

━━━━━━━━━━━━━━━━━━
STRICT OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━

SourcePage: [Page Name]
WhereFound: [Title | Meta | H1 | H2/H3 | Body]
Keyword: [existing keyword phrase]

SourcePage: [Page Name]
WhereFound: [Title | Meta | H1 | H2/H3 | Body]
Keyword: [existing keyword phrase]

OUTPUT RULES:

* One keyword per entry.
* No bullets.
* No numbering.
* No tables.
* No explanations.
* No keyword suggestions.
* No keyword variations.
* No search volume.
* No CPC.
* No keyword difficulty.
* No classifications.
* No page recommendations.
* No cannibalization analysis.
* No content-gap analysis.
* No commentary before or after the output.`;
  };

  const handleImportStep4AiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    const raw = pastedText.trim()
      .replace(/([^\n])\s*(SourcePage(?:\s*[:\-])|Source(?:\s*[:\-])|Page(?:\s*[:\-]))/gi, '$1\n$2')
      .replace(/([^\n])\s*(WhereFound(?:\s*[:\-])|Location(?:\s*[:\-])|Element(?:\s*[:\-]))/gi, '$1\n$2')
      .replace(/([^\n])\s*(Keyword(?:\s*\d+)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    const lines = raw.split('\n');
    let currentSource = 'Homepage';
    let currentWhere: 'Title' | 'Meta' | 'H1' | 'H2/H3' | 'Body' = 'Title';

    const newItems: WebsiteContentKeywordItem[] = [];
    const seenKeywords = new Set<string>();

    const tryAddItem = (kw: string, source: string, where: 'Title' | 'Meta' | 'H1' | 'H2/H3' | 'Body') => {
      let cleaned = kw.trim().replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();
      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;
      const lower = cleaned.toLowerCase();
      if (
        lower.startsWith('sourcepage:') ||
        lower.startsWith('wherefound:') ||
        lower.startsWith('keyword:') ||
        lower.startsWith('here are') ||
        lower.startsWith('below are') ||
        lower === 'keyword' ||
        lower === 'title' ||
        lower === 'meta' ||
        lower === 'h1'
      ) return;

      if (!seenKeywords.has(lower)) {
        seenKeywords.add(lower);
        newItems.push({
          id: `wk-ai-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          keyword: cleaned,
          sourcePage: source || 'Homepage',
          whereFound: where,
          intent: 'Commercial',
          relevant: true,
          existingTarget: true,
          verificationStatus: 'ai_candidate',
        });
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || /^[\-\|\s:_=]+$/.test(trimmed)) return;

      if (trimmed.toLowerCase().startsWith('sourcepage:') || trimmed.toLowerCase().startsWith('source:') || trimmed.toLowerCase().startsWith('page:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentSource = parts[1].replace(/[*_#"`]/g, '').trim();
        return;
      }

      if (trimmed.toLowerCase().startsWith('wherefound:') || trimmed.toLowerCase().startsWith('element:') || trimmed.toLowerCase().startsWith('location:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          const val = parts[1].replace(/[*_#"`]/g, '').trim();
          if (['Title', 'Meta', 'H1', 'H2/H3', 'Body'].includes(val)) {
            currentWhere = val as any;
          } else if (/h2|h3/i.test(val)) {
            currentWhere = 'H2/H3';
          } else if (/meta/i.test(val)) {
            currentWhere = 'Meta';
          } else if (/h1/i.test(val)) {
            currentWhere = 'H1';
          } else if (/body/i.test(val)) {
            currentWhere = 'Body';
          }
        }
        return;
      }

      if (trimmed.toLowerCase().startsWith('keyword:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          tryAddItem(parts[1], currentSource, currentWhere);
          return;
        }
      }

      // Bullets or numbered lines
      let lineCandidate = trimmed.replace(/^[\*\-\+•▪–—]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
      lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();
      if (lineCandidate.toLowerCase().startsWith('keyword:')) {
        lineCandidate = lineCandidate.substring(8).trim();
      }
      tryAddItem(lineCandidate, currentSource, currentWhere);
    });

    if (newItems.length === 0) {
      return { success: false, count: 0, error: '⚠️ No valid website keywords could be detected in the pasted text.' };
    }

    updateProject({
      ...localProject,
      websiteContentKeywords: [...localProject.websiteContentKeywords, ...newItems],
    });

    return { success: true, count: newItems.length };
  };

  // Dynamic Controlled AI Prompt Generator for Section 1: Google SERP Screenshot Analysis
  const generateAiPromptStep5SERP = () => {
    const bName = localProject.businessProfile.businessName || localProject.clientName;
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';

    return `You are a Senior Local SEO Strategist conducting a GOOGLE SERP COMPETITOR & KEYWORD DISCOVERY AUDIT.

BUSINESS:
${bName}

LOCATION:
${loc}

TARGET SEARCH QUERY:
[Insert target search query]

OBJECTIVE:

I am attaching a full-page screenshot of the Google Search Results Page (SERP) for the target search query.

Carefully inspect the entire screenshot.

The purpose of this method is to identify:

Competitor websites currently appearing in the SERP.

Meaningful keyword and service/product phrases that Google and ranking competitors associate with the target search.

Additional search-query language appearing in People Also Ask and Related Searches.

This is a SERP EXTRACTION task.

Do NOT perform external keyword research.

Do NOT use search volume.

Do NOT use CPC.

Do NOT use keyword difficulty.

Do NOT generate keyword variations that are not represented in the screenshot.

Do NOT invent competitors, keywords, services, products, or search queries.

━━━━━━━━━━━━━━━━━━
1. COMPETITOR EXTRACTION
━━━━━━━━━━━━━━━━━━

Identify the relevant competitor domains appearing in the organic Google search results.

Prioritize:

Organic search results
Local/Map results when a website domain is clearly visible
Relevant businesses directly competing for the target service/search intent

Do NOT treat the following as competitors:

Google
Google Maps
Wikipedia
Government websites
Directories
Social media platforms
Forums
News websites
Informational resources

unless the result is genuinely a direct competitor to the business.

Record the domain only.

Example:

unitedtrucking.com

Do not include:

https://
www.

unless the domain itself requires it.

━━━━━━━━━━━━━━━━━━
2. COMPETITOR KEYWORD EXTRACTION
━━━━━━━━━━━━━━━━━━

For each relevant competitor, inspect the visible:

Organic result title
Organic result snippet
Visible URL/category information

Extract meaningful service, product, and commercial keyword phrases that the competitor appears to be targeting.

Prioritize:

Core service phrases
Product phrases
Service/product + location phrases
Commercial phrases
Specific product categories
Meaningful terminology used in the competitor's title/snippet

Do NOT extract every word from the title or snippet.

For example, if a result says:

"Truck Parts ${loc} | Heavy Duty Truck Parts"

Extract meaningful phrases such as:

truck parts ${loc}
heavy duty truck parts

Do NOT extract:

quality
leading
company
customers

unless they form part of a meaningful keyword phrase.

━━━━━━━━━━━━━━━━━━
3. GOOGLE SERP KEYWORD DISCOVERY
━━━━━━━━━━━━━━━━━━

Also inspect Google-generated SERP features, including:

People Also Ask
Related Searches
Search suggestions if visible
Other clearly visible Google search-query elements

Extract meaningful search phrases from these sections.

These phrases should be recorded as Google SERP discoveries rather than attributed to a competitor.

Examples:

People Also Ask:

"Where can I buy truck parts in ${loc}?"

Related Search:

"heavy duty truck parts ${loc}"

Only extract phrases actually visible in the screenshot.

Do NOT generate additional variations from them.

━━━━━━━━━━━━━━━━━━
4. LOCATION HANDLING
━━━━━━━━━━━━━━━━━━

Pay attention to local terminology appearing in competitor results and Google SERP features.

Extract location + service/product combinations when they are actually visible.

For example:

truck parts ${loc}
truck parts in ${loc}
heavy duty truck parts ${loc}

Do NOT invent:

Cities
Towns
Neighborhoods
Regions

unless they are visible in the screenshot.

━━━━━━━━━━━━━━━━━━
5. KEYWORD RELEVANCE
━━━━━━━━━━━━━━━━━━

Only extract keywords that are meaningfully connected to the target search query and the business's confirmed service offering.

Do NOT expand into unrelated services merely because they appear somewhere in the SERP.

For example, if the target is:

Truck Parts ${loc}

do not automatically extract:

truck repair
truck rental
truck servicing
truck maintenance

unless the SERP result clearly establishes them as part of the same search intent AND they are relevant to the discovery objective.

━━━━━━━━━━━━━━━━━━
6. EXACT SERP EVIDENCE
━━━━━━━━━━━━━━━━━━

Every discovered keyword must be supported by visible text in the screenshot.

Do NOT:

Paraphrase into a new keyword
Create synonyms
Rearrange words
Add location modifiers
Add commercial modifiers
Expand short phrases into long-tail keywords

If Google shows:

"Truck Parts ${loc}"

record:

truck parts ${loc}

Do not additionally create:

${loc} truck parts
truck parts in ${loc}

unless those phrases are separately visible.

━━━━━━━━━━━━━━━━━━
7. DUPLICATES
━━━━━━━━━━━━━━━━━━

Do not repeat the same keyword when it appears multiple times within the same source.

Ignore capitalization differences when determining duplicates.

For example:

Truck Parts ${loc}

and

truck parts ${loc}

should be treated as the same keyword.

━━━━━━━━━━━━━━━━━━
8. SOURCE DISTINCTION
━━━━━━━━━━━━━━━━━━

Clearly distinguish between:

A. Competitor-derived keywords
Keywords found in a competitor's organic result title/snippet.

B. People Also Ask keywords
Queries visible in Google's People Also Ask section.

C. Related Search keywords
Queries visible in Google's Related Searches section.

D. Other SERP keywords
Meaningful phrases visible elsewhere in the SERP that do not fit the above categories.

Do not attribute Google-generated queries to competitors.

━━━━━━━━━━━━━━━━━━
9. COMPETITOR SELECTION
━━━━━━━━━━━━━━━━━━

Focus on the strongest relevant organic competitors.

Do not simply list every domain appearing in the screenshot.

Exclude irrelevant results.

If a competitor appears multiple times, list the domain once per relevant keyword source where appropriate.

━━━━━━━━━━━━━━━━━━
10. FINAL QUALITY CHECK
━━━━━━━━━━━━━━━━━━

Before including a competitor:

Is it a genuine competing business?
Is it relevant to the target search?
Is the domain visible or reliably identifiable from the screenshot?

Before including a keyword:

Is the phrase actually visible in the screenshot?
Is it meaningful?
Is it relevant to the target search?
Does it represent a service, product, commercial query, or meaningful search phrase?
Have I avoided generic words?
Have I avoided creating a variation?
Have I avoided inventing information?

If not, exclude it.

━━━━━━━━━━━━━━━━━━
STRICT OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━

For competitor-derived keywords:

Source: Competitor
Competitor: [Competitor Domain]
Keyword: [Discovered Keyword Phrase]

Source: Competitor
Competitor: [Competitor Domain]
Keyword: [Discovered Keyword Phrase]

For People Also Ask:

Source: People Also Ask
Competitor: N/A
Keyword: [Discovered Keyword Phrase]

For Related Searches:

Source: Related Searches
Competitor: N/A
Keyword: [Discovered Keyword Phrase]

For other Google SERP discoveries:

Source: Google SERP
Competitor: N/A
Keyword: [Discovered Keyword Phrase]

OUTPUT RULES:

One keyword entry per block.
No bullets.
No numbering.
No tables.
No explanations.
No commentary.
No search volume.
No CPC.
No keyword difficulty.
No keyword classification.
No keyword variations.
No SEO recommendations.
Do not add anything outside the required format.`;
  };

  const handleImportStep5SERPAiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    const raw = pastedText.trim()
      .replace(/([^\n])\s*(Source(?:\s*[:\-])|Competitor(?:\s*[:\-])|Keyword(?:\s*\d+)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    const lines = raw.split('\n');
    let currentSource = 'Competitor';
    let currentComp = 'SERP Competitor';

    const newItems: CompetitorKeywordItem[] = [];
    const seenKeywords = new Set<string>();

    const tryAddItem = (kw: string, compName: string, source: string) => {
      let cleaned = kw.trim().replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();
      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;
      const lower = cleaned.toLowerCase();
      if (
        lower.startsWith('source:') ||
        lower.startsWith('competitor:') ||
        lower.startsWith('keyword:') ||
        lower === 'keyword' ||
        lower.startsWith('here are')
      ) return;

      if (!seenKeywords.has(lower)) {
        seenKeywords.add(lower);
        newItems.push({
          id: `comp-serp-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          competitorName: compName || 'Google SERP',
          keyword: cleaned,
          service: source ? `SERP (${source})` : 'SERP Discovered Term',
          location: localProject.businessProfile.primaryLocation,
          searchIntent: 'Commercial',
          clientOffersThis: true,
          addToResearch: true,
          opportunity: 'Gap',
          verificationStatus: 'ai_candidate',
        });
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || /^[\-\|\s:_=]+$/.test(trimmed)) return;

      if (trimmed.toLowerCase().startsWith('source:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentSource = parts[1].replace(/[*_#"`]/g, '').trim();
        return;
      }
      if (trimmed.toLowerCase().startsWith('competitor:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentComp = parts[1].replace(/[*_#"`]/g, '').trim();
        return;
      }
      if (trimmed.toLowerCase().startsWith('keyword:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          const compName = (currentComp === 'N/A' || !currentComp.trim())
            ? (currentSource || 'Google SERP')
            : currentComp.trim();
          tryAddItem(parts[1], compName, currentSource);
          return;
        }
      }

      let lineCandidate = trimmed.replace(/^[\*\-\+•▪–—]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
      lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();
      if (lineCandidate.toLowerCase().startsWith('keyword:')) {
        lineCandidate = lineCandidate.substring(8).trim();
      }
      const compName = (currentComp === 'N/A' || !currentComp.trim())
        ? (currentSource || 'Google SERP')
        : currentComp.trim();
      tryAddItem(lineCandidate, compName, currentSource);
    });

    if (newItems.length === 0) {
      return { success: false, count: 0, error: '⚠️ No SERP keyword candidates could be detected.' };
    }

    updateProject({
      ...localProject,
      competitorKeywords: [...localProject.competitorKeywords, ...newItems],
    });

    return { success: true, count: newItems.length };
  };

  // Dynamic Controlled AI Prompt Generator for Section 2: Competitor Webpage Screenshot Analysis
  const generateAiPromptStep5Webpage = () => {
    const bName = localProject.businessProfile.businessName || localProject.clientName;
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';
    const services = localProject.businessProfile.confirmedServices.length > 0
      ? localProject.businessProfile.confirmedServices
      : ['Primary Service'];

    const seedTerms = localProject.seedKeywords.map(s => s.seedKeyword);
    const varTerms = localProject.variations.map(v => v.keyword);
    const allServices = Array.from(new Set([...services, ...seedTerms, ...varTerms])).filter(Boolean);

    const formattedServices = allServices.map((s, i) => `${i + 1}. ${s}`).join('\n');

    return `# Competitor Webpage Screenshot Keyword Audit

You are a **Senior Local SEO Strategist** executing a **Competitor Webpage Target Audit**.

## CLIENT BUSINESS CONTEXT

**Client Name:** ${bName}
**Target Location:** ${loc}

## CONFIRMED CLIENT SERVICES

The following services are confirmed as services the client actually offers:

${formattedServices}

**Important:** This list is the authority for determining whether the client offers a service. Do not assume the client offers a service simply because a competitor offers it.

---

## COMPETITOR INPUT

I am attaching a full-page screenshot of the competitor's website page.

---

## TASK & INSTRUCTIONS

Inspect the competitor's webpage screenshot and extract the **keywords and service phrases the competitor is targeting**.

### 1. COMPARE EACH KEYWORD AGAINST CLIENT SERVICES
For **every extracted competitor keyword**, compare it against the client's CONFIRMED CLIENT SERVICES.
Determine: **ClientOffersThis: Yes or No**
- Mark **Yes** when the competitor keyword represents a service/product category covered by confirmed services.
- Mark **No** when the competitor keyword represents a service that the client does NOT offer.

### 2. IDENTIFY KEYWORD GAPS / OPPORTUNITIES
Determine: **Opportunity: Gap or No**
- Mark **Opportunity: Gap** when the competitor targets a keyword that is directly supported by confirmed client services, but not already adequately covered in client's targeting.
- Mark **Opportunity: No** when unsupported service or already covered term.

---

# STRICT OUTPUT FORMAT

Competitor: [Competitor Domain or Name]
Keyword: [Extracted Competitor Keyword]
ClientOffersThis: [Yes | No]
Opportunity: [Gap | No]

Competitor: [Competitor Domain or Name]
Keyword: [Extracted Competitor Keyword]
ClientOffersThis: [Yes | No]
Opportunity: [Gap | No]`;
  };

  const handleImportStep5WebpageAiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    const raw = pastedText.trim()
      .replace(/([^\n])\s*(Competitor(?:\s*[:\-])|ClientOffersThis(?:\s*[:\-])|Opportunity(?:\s*[:\-])|Keyword(?:\s*\d+)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    const lines = raw.split('\n');
    let currentComp = 'Competitor Webpage';
    let currentOffers = true;
    let currentOpp: 'Gap' | 'No' = 'Gap';

    const newItems: CompetitorKeywordItem[] = [];
    const unsupportedList: string[] = [...localProject.businessProfile.unsupportedServices];
    const seenKeywords = new Set<string>();

    const tryAddItem = (kw: string) => {
      let cleaned = kw.trim().replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();
      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;
      const lower = cleaned.toLowerCase();
      if (
        lower.startsWith('competitor:') ||
        lower.startsWith('clientoffersthis:') ||
        lower.startsWith('opportunity:') ||
        lower.startsWith('keyword:') ||
        lower === 'keyword' ||
        lower.startsWith('here are')
      ) return;

      if (!seenKeywords.has(lower)) {
        seenKeywords.add(lower);
        newItems.push({
          id: `comp-ai-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          competitorName: currentComp,
          keyword: cleaned,
          service: 'Competitor Service',
          location: localProject.businessProfile.primaryLocation,
          searchIntent: 'Commercial',
          clientOffersThis: currentOffers,
          addToResearch: currentOffers,
          opportunity: currentOpp,
          verificationStatus: 'ai_candidate',
        });

        if (!currentOffers && !unsupportedList.includes(cleaned)) {
          unsupportedList.push(cleaned);
        }
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || /^[\-\|\s:_=]+$/.test(trimmed)) return;

      if (trimmed.toLowerCase().startsWith('competitor:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentComp = parts[1].replace(/[*_#"`]/g, '').trim();
      } else if (trimmed.toLowerCase().startsWith('clientoffersthis:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          const val = parts[1].trim().toLowerCase();
          currentOffers = val === 'yes' || val === 'true';
        }
      } else if (trimmed.toLowerCase().startsWith('opportunity:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          const val = parts[1].trim().toLowerCase();
          currentOpp = val.includes('gap') ? 'Gap' : 'No';
        }
      } else if (trimmed.toLowerCase().startsWith('keyword:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          tryAddItem(parts[1]);
        }
      } else {
        let lineCandidate = trimmed.replace(/^[\*\-\+•▪–—]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
        lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();
        if (lineCandidate.toLowerCase().startsWith('keyword:')) {
          lineCandidate = lineCandidate.substring(8).trim();
        }
        tryAddItem(lineCandidate);
      }
    });

    if (newItems.length === 0) {
      return { success: false, count: 0, error: '⚠️ No competitor webpage keyword candidates could be detected.' };
    }

    const newDecisions = logDecision(
      'Step 5: Competitor Keywords',
      `Imported ${newItems.length} competitor keywords from AI Helper`,
      'Competitor AI Import',
      `Analyzed competitor terms against client services. ${newItems.filter(i => !i.clientOffersThis).length} terms rejected as unsupported services.`
    );

    updateProject({
      ...localProject,
      businessProfile: {
        ...localProject.businessProfile,
        unsupportedServices: unsupportedList,
      },
      competitorKeywords: [...localProject.competitorKeywords, ...newItems],
      decisionLog: newDecisions,
    });

    return { success: true, count: newItems.length };
  };

  // Step 6 AI Prompt Generator & Importer (SEMrush Focus)
  // Step 6 AI Prompt Generator & Importer (SEMrush Focus)
  // Step 6 AI Prompt Generator & Importer (SEMrush Focus)
  const generateAiPromptStep6 = () => {
    const bName = localProject.businessProfile.businessName || localProject.clientName;
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';
    const confirmedServices = localProject.businessProfile.confirmedServices.length > 0
      ? localProject.businessProfile.confirmedServices.join(', ')
      : (localProject.seedKeywords.map(s => s.seedKeyword).join(', ') || 'Primary Services');

    return `You are a Senior SEO Specialist conducting SEMrush Competitor Keyword Research and Keyword Relevance Filtering.

## PROJECT INFORMATION

Business: ${bName}

Target Location: ${loc}

Confirmed Services / Products:
${confirmedServices}

## INPUT

I will provide SEMrush keyword research data from a competitor website.

The data may be provided as:

* Pasted SEMrush table text
* A full-page SEMrush screenshot
* Multiple SEMrush screenshots
* SEMrush keyword export data

Your task is to analyze ALL keywords contained in the supplied SEMrush data and identify keywords that could be relevant and potentially valuable for ${bName}.

The purpose of this analysis is keyword discovery, not merely finding keywords that exactly match the confirmed service.

Use the confirmed business, location, and service as the source of truth.

Do not assume that ${bName} offers unrelated services.

━━━━━━━━━━━━━━━━━━

## CORE RELEVANCE PRINCIPLE

━━━━━━━━━━━━━━━━━━

Be broad enough to discover useful SEO opportunities, while still being strict enough to remove genuinely irrelevant keywords.

A keyword does NOT need to contain the exact phrase "${confirmedServices}" to be considered relevant.

Consider whether a person searching the keyword could reasonably be looking for a product, component, supplier, or parts/services category that ${bName} could potentially target as part of its ${confirmedServices} business.

Evaluate the meaning and search intent of the entire keyword, not individual words.

━━━━━━━━━━━━━━━━━━

## KEYWORD RELEVANCE LEVELS

━━━━━━━━━━━━━━━━━━

Classify keywords internally into these relevance levels:

### HIGH RELEVANCE

Keep keywords that directly relate to ${confirmedServices}.

Examples:
* ${confirmedServices}
* ${confirmedServices} ${loc}
* ${confirmedServices} near me

Also keep keywords for:
* Service/product categories
* Components and replacement parts
* Related electrical, transmission, brake, engine, or structural components
* Other specific components related to confirmed services

### STRONG INDIRECT RELEVANCE

Keep keywords that relate to broader industry/automotive/equipment parts where the search could reasonably include services offered by ${bName}.

Examples:
* vehicle parts
* automotive parts
* spare parts
* equipment components
* parts supplier

These should generally be kept when they have strong parts/product/supplier intent, particularly when they include ${loc} or a local area.

### BRAND / MODEL RELEVANCE

Keep keywords involving vehicle/equipment manufacturers, brands, or models when the keyword clearly indicates parts or services.

Do NOT automatically exclude a keyword simply because the brand is not explicitly known to be a primary brand.

The keyword is relevant if the brand has commercial vehicles/equipment and the query is clearly about parts/services.

### LOCAL PARTS-SUPPLIER RELEVANCE

Keep local searches that indicate a person is looking for a place or supplier.

Examples:
* supplier in ${loc}
* supply store near me
* parts place ${loc}
* ${loc} supplier

These can be valuable because they represent potential customers looking for somewhere to purchase products or services.

━━━━━━━━━━━━━━━━━━

## EXCLUDE KEYWORDS

━━━━━━━━━━━━━━━━━━

Remove keywords when the actual search intent is clearly unrelated to ${bName}'s confirmed offering.

Exclude:
* Sales of entire vehicles/heavy machinery (when only parts/services are offered)
* Rentals, driving jobs, career listings
* Logistics, freight, shipping (unless confirmed services)
* Training, licenses, insurance, financing
* Unrelated products, services, or businesses
* Employment/recruitment keywords
* Navigational searches for unrelated companies

━━━━━━━━━━━━━━━━━━

## LOCAL INTENT

━━━━━━━━━━━━━━━━━━

Give additional relevance consideration to keywords containing:
* ${loc}
* Surrounding cities, regions, provinces, or towns
* near me, nearby, local supplier/store/place terminology

A keyword does NOT need a location modifier to be relevant if it has strong direct service/product intent.

━━━━━━━━━━━━━━━━━━

## INTENT CLASSIFICATION

━━━━━━━━━━━━━━━━━━

For every keyword that passes the relevance filter, assign exactly one intent:

### Transactional

Use Transactional when the searcher appears ready to purchase, obtain, order, contact, or find a supplier.

### Commercial

Use Commercial when the searcher is researching products, brands, suppliers, prices, or options before purchasing.

### Informational

Use Informational when the primary purpose is learning about a relevant part, component, specification, compatibility, or maintenance topic.

━━━━━━━━━━━━━━━━━━

## DUPLICATE HANDLING

━━━━━━━━━━━━━━━━━━

Remove exact duplicates.

For near-duplicates, use judgment. Do NOT aggressively remove legitimate keyword variations.

━━━━━━━━━━━━━━━━━━

## COMPETITOR INFORMATION

━━━━━━━━━━━━━━━━━━

For each selected keyword, use the competitor/domain associated with the SEMrush data.

If the supplied SEMrush dataset comes from a competitor domain and all keywords belong to that competitor, use that competitor's domain/business name.

Use the exact competitor URL from the SEMrush data when available.

If competitor information is not available in the supplied data, use:

CompetitorName: N/A
CompetitorLink: N/A

Do not invent competitor information.

━━━━━━━━━━━━━━━━━━

## SOURCE DATA RULE

━━━━━━━━━━━━━━━━━━

This is a filtering and discovery task, not a keyword-generation task.

Only select keywords that actually appear in the supplied SEMrush data.

DO NOT invent, expand, rewrite, or modify keywords.

━━━━━━━━━━━━━━━━━━

## IMPORTANT: MAXIMIZE RELEVANT DISCOVERY

━━━━━━━━━━━━━━━━━━

Do not return only exact-match keywords. Identify all reasonable SEO opportunities within the supplied competitor dataset.

━━━━━━━━━━━━━━━━━━

## STRICT OUTPUT FORMAT

━━━━━━━━━━━━━━━━━━

Output ONLY the relevant filtered keywords.

For every selected keyword, use exactly:

Tool: SEMrush
CompetitorName: [Competitor Business/Domain Name or N/A]
CompetitorLink: [Competitor URL or N/A]
Intent: [Commercial | Transactional | Informational]
Keyword: [keyword phrase]

Repeat this exact format for every selected keyword.

Do NOT include search volume, CPC, difficulty, explanations, tables, numbering, or headings.

The final response must contain ONLY keywords that passed the relevance filter.`;
  };

  const handleImportStep6AiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    const raw = pastedText.trim()
      .replace(/([^\n])\s*(Tool(?:\s*[:\-])|CompetitorName(?:\s*[:\-])|CompetitorLink(?:\s*[:\-])|Intent(?:\s*[:\-])|Keyword(?:\s*\d+)?\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    const lines = raw.split('\n');
    let currentTool = 'SEMrush';
    let currentComp = '';
    let currentLink = '';
    let currentIntent = 'Commercial';
    const newItems: ToolKeywordItem[] = [];
    const seenKeywords = new Set<string>();

    const tryAddItem = (kw: string) => {
      let cleaned = kw.trim().replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();
      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;
      const lower = cleaned.toLowerCase();
      if (
        lower.startsWith('tool:') ||
        lower.startsWith('competitorname:') ||
        lower.startsWith('competitorlink:') ||
        lower.startsWith('intent:') ||
        lower.startsWith('keyword:') ||
        lower === 'keyword' ||
        lower.startsWith('here are')
      ) return;

      if (!seenKeywords.has(lower)) {
        seenKeywords.add(lower);
        const cleanComp = (currentComp && currentComp.toLowerCase() !== 'n/a') ? sanitizeUrlOrDomain(currentComp) : undefined;
        const cleanLink = (currentLink && currentLink.toLowerCase() !== 'n/a') ? sanitizeUrlOrDomain(currentLink) : undefined;
        newItems.push({
          id: `tool-ai-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          keyword: cleaned,
          sourceTool: currentTool || 'SEMrush',
          competitorName: cleanComp,
          competitorLink: cleanLink,
          searchVolumeStatus: 'not_collected',
          intent: currentIntent || 'Commercial',
          relevant: true,
          isVariation: false,
          decision: 'Target',
          verificationStatus: 'ai_candidate',
        });
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || /^[\-\|\s:_=]+$/.test(trimmed)) return;

      if (trimmed.toLowerCase().startsWith('tool:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentTool = parts[1].trim();
      } else if (trimmed.toLowerCase().startsWith('competitorname:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentComp = sanitizeUrlOrDomain(parts[1].trim());
      } else if (trimmed.toLowerCase().startsWith('competitorlink:')) {
        const idx = trimmed.indexOf(':');
        if (idx !== -1) {
          const val = trimmed.substring(idx + 1).trim();
          if (val && val.toLowerCase() !== 'n/a') currentLink = sanitizeUrlOrDomain(val);
        }
      } else if (trimmed.toLowerCase().startsWith('intent:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) currentIntent = parts[1].trim();
      } else if (trimmed.toLowerCase().startsWith('keyword:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          tryAddItem(parts[1]);
        }
      } else {
        let lineCandidate = trimmed.replace(/^[\*\-\+•▪–—]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
        lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();
        if (lineCandidate.toLowerCase().startsWith('keyword:')) {
          lineCandidate = lineCandidate.substring(8).trim();
        }
        tryAddItem(lineCandidate);
      }
    });

    if (newItems.length === 0) {
      return { success: false, count: 0, error: '⚠️ No valid tool keyword candidates could be detected.' };
    }

    updateProject({
      ...localProject,
      toolKeywords: [...localProject.toolKeywords, ...newItems],
    });

    return { success: true, count: newItems.length };
  };

  // Step 7 AI Prompt Generator & Importer (Alphabet Soup)
  const generateAiPromptStep7 = () => {
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';
    const seeds = localProject.seedKeywords.map(s => s.seedKeyword).slice(0, 5).join(', ');

    return `You are a Search Engine Analyst executing Google Autocomplete (Alphabet Soup Technique).

Core Seed Keywords: "${seeds}"
Location: "${loc}"

TASK:
Simulate Google Autocomplete search predictions for these seeds when combined with letters A through Z.

STRICT OUTPUT FORMAT:
Autocomplete: [Google autocomplete prediction phrase]
Autocomplete: [Google autocomplete prediction phrase]
Autocomplete: [Google autocomplete prediction phrase]`;
  };

  const handleImportStep7AiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    const raw = pastedText.trim()
      .replace(/([^\n])\s*(Autocomplete(?:\s*[:\-])|[a-z]\s*[:\-])/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    const lines = raw.split('\n');
    const newTerms: string[] = [];
    const seen = new Set(localProject.autocompleteKeywords.map(k => k.toLowerCase()));

    const tryAddTerm = (rawTerm: string) => {
      let cleaned = rawTerm.trim().replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();
      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;
      const lower = cleaned.toLowerCase();
      if (
        lower.startsWith('autocomplete:') ||
        lower.startsWith('here are') ||
        lower.startsWith('task:') ||
        lower === 'autocomplete'
      ) return;

      if (!seen.has(lower)) {
        seen.add(lower);
        newTerms.push(cleaned);
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || /^[\-\|\s:_=]+$/.test(trimmed)) return;

      if (trimmed.toLowerCase().startsWith('autocomplete:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          tryAddTerm(parts[1]);
        }
      } else {
        let lineCandidate = trimmed.replace(/^[\*\-\+•▪–—]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
        lineCandidate = lineCandidate.replace(/^[a-z]\s*[:\-]\s*/i, ''); // Strip "A: ...", "B: ..."
        lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();
        if (lineCandidate.toLowerCase().startsWith('autocomplete:')) {
          lineCandidate = lineCandidate.substring(13).trim();
        }
        tryAddTerm(lineCandidate);
      }
    });

    if (newTerms.length === 0) {
      return { success: false, count: 0, error: '⚠️ No autocomplete prediction terms could be detected.' };
    }

    updateProject({
      ...localProject,
      autocompleteKeywords: [...localProject.autocompleteKeywords, ...newTerms],
    });

    return { success: true, count: newTerms.length };
  };

  // Step 8 AI Prompt Generator & Importer (Related Searches)
  const generateAiPromptStep8 = () => {
    const loc = localProject.businessProfile.primaryLocation || 'Target Location';
    const seeds = localProject.seedKeywords.map(s => s.seedKeyword).slice(0, 5).join(', ');

    return `You are an SEO Strategist analyzing Google SERP "Searches related to" and "People Also Ask".

Core Terms: "${seeds}"
Location: "${loc}"

TASK:
Extract topical entity phrases and related search terms displayed at the bottom of Google SERPs.

STRICT OUTPUT FORMAT:
Related: [Related search / topical phrase]
Related: [Related search / topical phrase]
Related: [Related search / topical phrase]`;
  };

  const handleImportStep8AiCandidates = (pastedText: string): { success: boolean; count: number; error?: string } => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, count: 0, error: '⚠️ Pasted text is empty.' };
    }

    const raw = pastedText.trim()
      .replace(/([^\n])\s*(Related(?:\s*[:\-])|Topic(?:\s*[:\-]))/gi, '$1\n$2')
      .replace(/([^\n])\s*([•▪]\s+)/g, '$1\n$2');

    const lines = raw.split('\n');
    const newItems: KeywordVariationItem[] = [];
    const seen = new Set(localProject.variations.map(v => v.keyword.toLowerCase()));

    const tryAddItem = (rawKw: string) => {
      let cleaned = rawKw.trim().replace(/^["'`“‘\[\(]+|[\]\)"'`”’]+$/g, '').trim();
      cleaned = cleaned.replace(/[\.;,]$/, '').trim();
      if (!cleaned || cleaned.length < 2 || cleaned.length > 120) return;
      const lower = cleaned.toLowerCase();
      if (
        lower.startsWith('related:') ||
        lower.startsWith('here are') ||
        lower.startsWith('task:') ||
        lower === 'related'
      ) return;

      if (!seen.has(lower)) {
        seen.add(lower);
        newItems.push({
          id: `rel-ai-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          keyword: cleaned,
          variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Service',
          searchVolumeStatus: 'not_collected',
          intent: 'Commercial',
          serpSimilarity: 'High',
          separateTarget: false,
          reason: 'Extracted from Google SERP Related Searches',
          verificationStatus: 'ai_candidate',
        });
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || /^[\-\|\s:_=]+$/.test(trimmed)) return;

      if (trimmed.toLowerCase().startsWith('related:')) {
        const parts = trimmed.split(':');
        if (parts[1] && parts[1].trim()) {
          tryAddItem(parts[1]);
        }
      } else {
        let lineCandidate = trimmed.replace(/^[\*\-\+•▪–—]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
        lineCandidate = lineCandidate.replace(/^(\*{1,2}|_{1,2})/, '').replace(/(\*{1,2}|_{1,2})$/, '').trim();
        if (lineCandidate.toLowerCase().startsWith('related:')) {
          lineCandidate = lineCandidate.substring(8).trim();
        }
        tryAddItem(lineCandidate);
      }
    });

    if (newItems.length === 0) {
      return { success: false, count: 0, error: '⚠️ No related search terms could be detected.' };
    }

    updateProject({
      ...localProject,
      variations: [...localProject.variations, ...newItems],
    });

    return { success: true, count: newItems.length };
  };

  // Step 9 AI Geo-Targeting & Competitor Cleaning Prompt Generator & Importer
  const generateAiGeoTargetingPrompt = () => {
    const accumulated = getAccumulatedKeywords(8);
    const uniqueList = accumulated.uniqueList;

    const clientName = localProject.clientName || 'Client Business';
    const websiteUrl = localProject.websiteUrl || 'https://clientwebsite.com';
    const primaryCategory = localProject.businessProfile.businessType || 'Local Services';
    const targetLocation = step9GeoTargetLoc || localProject.businessProfile.primaryLocation || 'Scottsdale, AZ';
    const confirmedServices = localProject.businessProfile.confirmedServices.join(', ') || 'Core Client Services';
    
    // Auto-extract ALL competitor names from Step 5 competitorKeywords
    const step5Competitors = Array.from(
      new Set(
        localProject.competitorKeywords
          .map(c => c.competitorName?.trim())
          .filter(name => name && !['Google SERP', 'People Also Ask', 'Related Searches'].includes(name))
      )
    );
    const userExclusions = (step9ExcludeCompetitors || '').split(',').map(s => s.trim()).filter(Boolean);
    const allKnownCompetitorsList = Array.from(new Set([...step5Competitors, ...userExclusions])).filter(Boolean);
    const competitorExclusionsStr = allKnownCompetitorsList.length > 0 ? allKnownCompetitorsList.join(', ') : 'Sunbros Cleaning, Sunbros';

    const targetNeighborhoods = step9TargetNeighborhoods || 'Old Town Scottsdale, North Scottsdale';

    const keywordsFormatted = uniqueList.map((item, idx) => `${idx + 1}. [Source: ${item.stepName}] ${item.keyword}`).join('\n');

    return `ACT AS AN ENTERPRISE LOCAL SEO KEYWORD RESEARCH & GEO-TARGETING EXPERT.

I have collected a raw dataset of ${uniqueList.length} accumulated unique keywords for our client across 8 research steps.
Your task is to refine, clean, geo-target, pair nearby neighborhoods with our services, and PURGE ALL COMPETITOR BRAND NAMES from these keywords.

CLIENT & LOCATION CONTEXT:
- Client Name: "${clientName}"
- Client Website URL: ${websiteUrl}
- Primary Category: ${primaryCategory}
- Primary Target Location / City: "${targetLocation}"
- Nearby Target Neighborhoods / Districts: "${targetNeighborhoods}"
- Verified Client Services: ${confirmedServices}
- Known Competitor Brands Discovered in Research (MUST BE REMOVED): ${competitorExclusionsStr}

OUR MANDATORY INSTRUCTIONS:
1. COMPREHENSIVE COMPETITOR & THIRD-PARTY BRAND PURGE (CRITICAL):
   - Actively SCAN every single keyword in the list to detect and REMOVE ALL third-party competitor brand names, company names, franchise names, local business titles, or trademarked terms.
   - Known Competitors Extracted from Research: ${competitorExclusionsStr}
   - Example 1: REMOVE "About Sunbros Cleaning" or "Sunbros Cleaning Scottsdale" (competitor brand name).
   - Example 2: REMOVE any other competitor business names (e.g. "Scottsdale Window Washers LLC", "A1 Power Cleaners Inc", etc.).
   - NEVER keep any keyword phrase that mentions a competitor's business or brand name.

2. GEO-TARGETING & PRIMARY CITY VARIATIONS:
   - For generic, non-localized keywords (e.g., "Holiday Lighting", "Pressure Washing", "Solar Panel Cleaning"), create geo-targeted variations by appending or inserting our primary city ("${targetLocation}").
   - Example: "Holiday Lighting" -> "Holiday Lighting in ${targetLocation}", "${targetLocation} Holiday Light Installation", "Holiday Lighting Services ${targetLocation}"

3. NEARBY NEIGHBORHOODS & LOCAL SUB-AREAS (MODIFY & PAIR WITH SERVICES):
   - CRITICAL RULE: Do NOT delete nearby neighborhood or district names (e.g. "${targetNeighborhoods}").
   - Instead, MODIFY and PAIR these neighborhood names with our verified client services (${confirmedServices})!
   - Example 1: Turn "Old Town Scottsdale" into high-converting target keywords like "Window Cleaning in Old Town Scottsdale", "Pressure Washing Old Town Scottsdale", "Holiday Light Installation Old Town Scottsdale".
   - Example 2: Retain existing service phrases that mention nearby neighborhoods.
   - Treat these as high-value, highly targeted local keywords!

4. REMOVE ONLY COMPLETELY UNRELATED / NON-SERVICE TERMS:
   - Only REMOVE terms if they represent products/services the client does NOT offer or completely irrelevant distant non-service cities.

5. STRICT STRUCTURED OUTPUT FOR AUTOMATED IMPORTER:
   - Output ONLY a valid JSON array of objects representing the final clean keywords.
   - Do NOT include markdown text, conversational intro, or concluding explanations outside the JSON block.

STRICT REQUIRED JSON OUTPUT FORMAT:
[
  {
    "keyword": "Window Cleaning in Old Town Scottsdale",
    "sourceStep": "Step 3: Keyword Variations",
    "action": "Neighborhood Service Variation Added",
    "searchIntent": "Commercial"
  },
  {
    "keyword": "Pressure Washing in Scottsdale, AZ",
    "sourceStep": "Step 5: Discovered SERP Keyword",
    "action": "Kept / Geo-Targeted",
    "searchIntent": "Commercial"
  }
]

HERE IS THE COMPLETE ACCUMULATED KEYWORD LIST (${uniqueList.length} UNIQUE TERMS):
${keywordsFormatted}`;
  };

  const handleImportStep9AiGeoResponse = (textOverride?: string): { success: boolean; count: number; error?: string } => {
    const rawText = (typeof textOverride === 'string' ? textOverride : step9AiPastedResponse).trim();
    if (!rawText) {
      setStep9AiImportStatus('❌ Please paste the AI response text first.');
      return { success: false, count: 0, error: 'Please paste the AI response text first.' };
    }

    try {
      let parsedKeywords: string[] = [];

      // Attempt JSON parse first
      if (rawText.includes('[') && rawText.includes(']')) {
        const jsonStart = rawText.indexOf('[');
        const jsonEnd = rawText.lastIndexOf(']');
        if (jsonStart !== -1 && jsonEnd !== -1) {
          const jsonStr = rawText.substring(jsonStart, jsonEnd + 1);
          const parsedObj = JSON.parse(jsonStr);
          if (Array.isArray(parsedObj)) {
            parsedKeywords = parsedObj.map((item: any) => typeof item === 'string' ? item : item.keyword).filter(Boolean);
          }
        }
      }

      // Fallback to line-by-line parsing if JSON parse yielded nothing
      if (parsedKeywords.length === 0) {
        const lines = rawText.split('\n');
        lines.forEach(line => {
          let cleaned = line.trim();
          cleaned = cleaned.replace(/^(\d+[\.\)]|\-|\*|\[.*?\])\s*/g, '');
          cleaned = cleaned.replace(/^["']|["']$/g, '');
          if (cleaned && cleaned.length > 2 && !cleaned.toLowerCase().startsWith('keyword') && !cleaned.toLowerCase().startsWith('here is')) {
            parsedKeywords.push(cleaned);
          }
        });
      }

      if (parsedKeywords.length === 0) {
        const errMsg = '❌ Could not parse any valid keyword phrases from the response.';
        setStep9AiImportStatus(errMsg);
        return { success: false, count: 0, error: errMsg };
      }

      // Process parsed keywords: filter competitor terms
      const competitorList = (step9ExcludeCompetitors || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

      const existingVarMap = new Map(localProject.variations.map(v => [v.keyword.toLowerCase(), v]));
      let addedCount = 0;
      let removedCount = 0;

      // Filter existing variations for competitor terms only (keep valid neighborhood terms!)
      const filteredVariations = localProject.variations.filter(v => {
        const kwLower = v.keyword.toLowerCase();
        const isCompetitor = competitorList.some(comp => comp && kwLower.includes(comp));
        if (isCompetitor) {
          removedCount++;
          return false;
        }
        return true;
      });

      // Also filter competitorKeywords table for competitor brand matches
      const filteredCompetitorKeywords = localProject.competitorKeywords.filter(c => {
        const kwLower = c.keyword.toLowerCase();
        const isCompetitor = competitorList.some(comp => comp && kwLower.includes(comp));
        if (isCompetitor) {
          removedCount++;
          return false;
        }
        return true;
      });

      // Add new geo-targeted / neighborhood parsed keywords into variations
      parsedKeywords.forEach(kw => {
        const kwTrimmed = kw.trim();
        const kwLower = kwTrimmed.toLowerCase();
        const isCompetitor = competitorList.some(comp => comp && kwLower.includes(comp));

        if (!isCompetitor && !existingVarMap.has(kwLower)) {
          filteredVariations.push({
            id: `var-geo-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            keyword: kwTrimmed,
            variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Service',
            searchVolumeStatus: 'not_collected',
            intent: 'Commercial',
            serpSimilarity: 'High',
            separateTarget: false,
            reason: 'Geo-targeted location / neighborhood variation auto-imported from AI response',
            verificationStatus: 'user_verified',
          });
          existingVarMap.set(kwLower, filteredVariations[filteredVariations.length - 1]);
          addedCount++;
        }
      });

      // Save updated project
      updateProject({
        ...localProject,
        variations: filteredVariations,
        competitorKeywords: filteredCompetitorKeywords,
      });

      setStep9AiImportStatus(`✓ Success! Master Dataset Updated: Added ${addedCount} new geo-targeted variations, removed ${removedCount} competitor/excluded terms.`);
      return { success: true, count: addedCount };
    } catch (err: any) {
      const errMsg = `❌ Error parsing response: ${err?.message || 'Invalid format'}`;
      setStep9AiImportStatus(errMsg);
      return { success: false, count: 0, error: errMsg };
    }
  };

  // Step 1 State & Handlers
  const isStep1Valid =
    localProject.businessProfile.businessName.trim().length > 0 &&
    localProject.businessProfile.primaryLocation.trim().length > 0 &&
    localProject.businessProfile.confirmedServices.length > 0;

  // Step 2 State & Handlers
  const handleGenerateSeedsStep2 = () => {
    const services = localProject.businessProfile.confirmedServices;
    const location = localProject.businessProfile.primaryLocation || 'Target Location';

    const generated: SeedKeywordItem[] = services.map((srv, i) => ({
      id: `seed-${i}-${Date.now()}`,
      seedKeyword: srv.toLowerCase().includes(location.toLowerCase()) ? srv : `${srv} ${location}`,
      location,
      intent: 'Commercial',
      relevant: true,
      notes: `Derived from confirmed service: "${srv}"`,
    }));

    const master: MasterKeywordItem[] = generated.map((s, i) => ({
      id: `master-seed-${i}-${Date.now()}`,
      keyword: s.seedKeyword,
      searchVolume: 0,
      intent: 'Commercial',
      priority: 'Primary',
      type: 'Target',
      source: 'Step 2 Core Seed Keywords',
      targetPage: 'Homepage / Primary Service Page',
      action: 'Primary Seed Keyword Target',
      status: 'active',
    }));

    updateProject({
      ...localProject,
      seedKeywords: generated,
      masterKeywords: [...localProject.masterKeywords, ...master],
    });
  };

  const isStep2Valid = localProject.seedKeywords.length > 0 && seedIntentVerified;

  // Form State for Adding Items Manually
  const [singleKw, setSingleKw] = useState('');
  const [singleSourcePage, setSingleSourcePage] = useState('Homepage');
  const [singleWhereFound, setSingleWhereFound] = useState<'Title' | 'Meta' | 'H1' | 'H2/H3' | 'Body'>('Title');
  const [singleComp, setSingleComp] = useState('');
  const [singleOffers, setSingleOffers] = useState(true);

  const stepsList = [
    'Step 1: Profile',
    'Step 2: Seed Keywords',
    'Step 3: Variations',
    'Step 4: Website Content',
    'Step 5: Competitors',
    'Step 6: Tools',
    'Step 7: Autocomplete',
    'Step 8: Topical Terms',
    'Step 9: Search Volume',
    'Step 10: SERP Check & Keyword Hierarchy only homepage',
    'Step 11: Service Pages Keyword Research & Mapping',
    'Step 12: Page Meta Data (Titles & Descriptions)',
    'Step 13: Content Keyword Placement & Density Workstation',
    'Step 14: Answer Engine Optimization (AEO) Strategy',
    'Step 15: FAQ Direct Answer Schema & Featured Snippets',
    'Step 16: Executive Client Deliverable & Worker Technical Reports',
  ];

  return (
    <div className="animate-fade-in" style={{ width: '100%', maxWidth: '100%', padding: '16px', boxSizing: 'border-box' }}>
      {/* Top Workspace Header */}
      <div className="glass-panel" style={{ padding: '14px 18px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <Button size="sm" variant="secondary" icon={<ArrowLeft size={13} />} onClick={onBackToDashboard}>
                Back
              </Button>
              <Badge variant="ninja">Zynex Ninja Engine</Badge>
              <Badge variant="lecture">Module 5: Website Optimization</Badge>
            </div>
            <h1 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, margin: '2px 0 0 0' }}>
              {localProject.clientName} ({localProject.websiteUrl})
            </h1>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              size="sm"
              variant="secondary"
              icon={showLiveReport ? <EyeOff size={13} /> : <Eye size={13} />}
              onClick={() => setShowLiveReport(!showLiveReport)}
            >
              {showLiveReport ? 'Hide Live Report Side Panel' : 'Show Live Report Side Panel'}
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon={<Download size={14} />}
              onClick={() => KeywordPdfService.exportToPdf(localProject)}
            >
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Main Grid Layout: Handholding Workflow + Live Continuous Report */}
      <div style={{ display: 'grid', gridTemplateColumns: showLiveReport ? '1fr 360px' : '1fr', gap: '16px', alignItems: 'start' }}>
        {/* LEFT COLUMN: 20-Step Handholding Guide & Interactive Workstation */}
        <div style={{ minWidth: 0 }}>
          {/* Stepper Navigation Bar */}
          <div style={{ overflowX: 'auto', marginBottom: '14px', paddingBottom: '4px' }}>
            <div style={{ display: 'flex', gap: '4px', width: 'max-content' }}>
              {stepsList.map((sName, idx) => {
                const isActive = activeStep === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      handleStepChange(idx);
                    }}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      border: isActive ? '1px solid var(--accent-blue)' : '1px solid var(--border-color)',
                      background: isActive ? 'var(--accent-blue)' : 'rgba(15, 23, 42, 0.8)',
                      color: isActive ? '#ffffff' : 'var(--text-muted)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 1: BUSINESS PROFILE & SERVICE CONFIRMATION */}
          {activeStep === 0 && (
            <KeywordStepGuide
              stepNumber={1}
              stepTitle="Client Business Profile & Target Location Setup"
              whyAreWeDoingThis="Instructor Teaching (Lectures 1 & 5): Before starting keyword research, you MUST establish exact confirmed services offered by the client and their target location. Never assume a competitor's service is offered by your client!"
              whatYouNeedBeforeStarting={['Client website open in browser tab', 'Main navigation menu items ("Services", "Products")', 'Target city / state / region']}
              exactlyWhatToDo={[
                'Open client website and review the main navigation menu (Services / Products).',
                'Enter the Client Business Name and Main Target Location below.',
                'Add all confirmed services offered by the client (type individual services or bulk paste).',
                'Click "Continue to Next Step" once location and services are set.'
              ]}
              whatToLookFor={['Services listed in header/footer menus', 'Target city or country location scope', 'Primary service offerings']}
              whatToRecord={['Business Name', 'Main Target Location', 'Confirmed Client Services List']}
              lectureExample={{
                scenario: 'Client is a Heavy Truck Parts supplier operating in Trinidad and Tobago.',
                action: 'Instructor confirms 6 core services (Truck Parts, Bus Parts, Auto Parts, etc.) and location "Trinidad and Tobago".',
                result: 'Establishes accurate service authority scope for keyword targeting.'
              }}
              doneWhen="Business Name, Target Location, and at least 1 Confirmed Service are entered."
              isStepValidated={isStep1Valid}
              validationMessage="Enter Target Location and at least 1 Confirmed Service below to proceed."
              onContinue={() => handleStepChange(1)}
            >
              {/* Top Business Context Grid */}
              <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Building2 size={16} color="var(--accent-blue)" />
                  <span>1. BUSINESS IDENTIFICATION & LOCATION TARGETING</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '6px' }}>
                      Client Business Name *
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="e.g. United Trucking Supplies"
                      value={localProject.businessProfile.businessName}
                      onChange={(e) => updateProject({ ...localProject, businessProfile: { ...localProject.businessProfile, businessName: e.target.value } })}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent-blue)', display: 'block', marginBottom: '6px' }}>
                      📍 Main Target Location (City, State, Country) *
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      style={{ border: '1px solid rgba(56, 189, 248, 0.4)', background: 'rgba(56, 189, 248, 0.05)' }}
                      placeholder="e.g. Trinidad and Tobago, or Calgary AB"
                      value={localProject.businessProfile.primaryLocation}
                      onChange={(e) => updateProject({ ...localProject, businessProfile: { ...localProject.businessProfile, primaryLocation: e.target.value } })}
                    />
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      The primary geographic market where your client targets local customers.
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '6px' }}>
                      Business Type
                    </label>
                    <select
                      className="input-field"
                      value={localProject.businessProfile.businessType}
                      onChange={(e) => updateProject({ ...localProject, businessProfile: { ...localProject.businessProfile, businessType: e.target.value as any } })}
                    >
                      <option value="service">Service Business (e.g. Electrician, Contractor, Supplier)</option>
                      <option value="product">Product / E-Commerce Business</option>
                      <option value="hybrid">Hybrid (Services + Products)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Confirmed Services Manager Card */}
              <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle2 size={16} color="var(--accent-green)" />
                    <span>2. CONFIRMED CLIENT SERVICES ({localProject.businessProfile.confirmedServices.length} SERVICES)</span>
                  </div>
                  <button
                    onClick={() => setShowBulkServices(!showBulkServices)}
                    style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {showBulkServices ? '← Switch to Single Add Mode' : '📋 Bulk Edit / Paste Services'}
                  </button>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                  Inspect the client's website navigation menu ("Services", "What We Do", "Products"). Enter all confirmed services offered. These act as authority rule for keyword qualification!
                </p>

                {showBulkServices ? (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                      Paste comma-separated or line-separated services:
                    </label>
                    <textarea
                      className="input-field"
                      style={{ height: '100px', fontSize: '13px', fontFamily: 'monospace' }}
                      value={confirmedServicesText}
                      onChange={(e) => {
                        setConfirmedServicesText(e.target.value);
                        const parsed = e.target.value
                          .split(/,|\n/)
                          .map(s => s.trim())
                          .filter(Boolean);
                        updateProject({
                          ...localProject,
                          businessProfile: { ...localProject.businessProfile, confirmedServices: parsed }
                        });
                      }}
                      placeholder="Heavy Duty Truck Parts Supply, Truck Parts Supply, Bus Parts Supply, Auto Parts Supply..."
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                    <input
                      type="text"
                      className="input-field"
                      style={{ flex: 1 }}
                      placeholder="Add a service offered e.g. Heavy Duty Truck Parts Supply..."
                      value={newServiceInput}
                      onChange={(e) => setNewServiceInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (!newServiceInput.trim()) return;
                          const trimmed = newServiceInput.trim();
                          if (!localProject.businessProfile.confirmedServices.includes(trimmed)) {
                            const updatedList = [...localProject.businessProfile.confirmedServices, trimmed];
                            updateProject({
                              ...localProject,
                              businessProfile: { ...localProject.businessProfile, confirmedServices: updatedList }
                            });
                            setConfirmedServicesText(updatedList.join(', '));
                          }
                          setNewServiceInput('');
                        }
                      }}
                    />
                    <Button
                      variant="primary"
                      icon={<Plus size={16} />}
                      onClick={() => {
                        if (!newServiceInput.trim()) return;
                        const trimmed = newServiceInput.trim();
                        if (!localProject.businessProfile.confirmedServices.includes(trimmed)) {
                          const updatedList = [...localProject.businessProfile.confirmedServices, trimmed];
                          updateProject({
                            ...localProject,
                            businessProfile: { ...localProject.businessProfile, confirmedServices: updatedList }
                          });
                          setConfirmedServicesText(updatedList.join(', '));
                        }
                        setNewServiceInput('');
                      }}
                    >
                      Add Service
                    </Button>
                  </div>
                )}

                {/* Confirmed Services Interactive Badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '40px', padding: '12px', background: '#020617', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {localProject.businessProfile.confirmedServices.length > 0 ? (
                    localProject.businessProfile.confirmedServices.map((srv, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'rgba(16, 185, 129, 0.15)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          borderRadius: '20px',
                          color: '#34d399',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        <span>✓ {srv}</span>
                        <button
                          onClick={() => {
                            const updatedList = localProject.businessProfile.confirmedServices.filter((_, i) => i !== idx);
                            updateProject({
                              ...localProject,
                              businessProfile: { ...localProject.businessProfile, confirmedServices: updatedList }
                            });
                            setConfirmedServicesText(updatedList.join(', '));
                          }}
                          style={{ background: 'none', border: 'none', color: 'rgba(239, 68, 68, 0.8)', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                          title="Remove service"
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      No confirmed services added yet. Type a service above or click "Bulk Edit / Paste Services".
                    </div>
                  )}
                </div>
              </div>
            </KeywordStepGuide>
          )}

          {/* STEP 2: CORE SEED KEYWORDS */}
          {activeStep === 1 && (
            <KeywordStepGuide
              stepNumber={2}
              stepTitle="Establish Starting Core Seed Keywords"
              whyAreWeDoingThis="Seed keywords represent the primary commercial terms for your client's core services combined with target location. You can select and adjust search intent for each keyword using the dropdown below!"
              whatYouNeedBeforeStarting={['Confirmed services from Step 1', 'Target location']}
              exactlyWhatToDo={[
                'Click "Generate Seed Keywords from Confirmed Services".',
                'Verify the generated seed keywords.',
                'Select or change Search Intent (Commercial, Transactional, Informational, Navigational).',
                'Check the verification box at the bottom to proceed.'
              ]}
              whatToLookFor={['Exact service + location combinations']}
              whatToRecord={['Seed Keyword', 'Location', 'Intent']}
              lectureExample={{
                scenario: 'Dentist in Miami offering Dental Implants.',
                action: 'Combines service + location.',
                result: 'Seed Keyword: "Dental Implants Miami" (Commercial Intent).'
              }}
              doneWhen="At least 1 Seed Keyword is established and search intent is verified."
              isStepValidated={isStep2Valid}
              validationMessage="Generate seeds below and check the intent verification box to proceed."
              onPrevious={() => handleStepChange(0)}
              onContinue={() => handleStepChange(2)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>Established Seed Keywords ({localProject.seedKeywords.length})</div>
                <Button size="sm" variant="secondary" icon={<RefreshCw size={14} />} onClick={handleGenerateSeedsStep2}>
                  Generate Seeds from Step 1 Services
                </Button>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                <thead>
                  <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Seed Keyword</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Location</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Search Intent (Editable Dropdown)</th>
                  </tr>
                </thead>
                <tbody>
                  {localProject.seedKeywords.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>{s.seedKeyword}</td>
                      <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>{s.location}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <select
                          className="input-field"
                          style={{ padding: '4px 8px', fontSize: '12px', background: '#020617', color: 'var(--accent-blue)', fontWeight: 600 }}
                          value={s.intent}
                          onChange={(e) => {
                            const updated = localProject.seedKeywords.map(item =>
                              item.id === s.id ? { ...item, intent: e.target.value as any } : item
                            );
                            updateProject({ ...localProject, seedKeywords: updated });
                          }}
                        >
                          <option value="Commercial">Commercial</option>
                          <option value="Transactional">Transactional</option>
                          <option value="Informational">Informational</option>
                          <option value="Navigational">Navigational</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#ffffff', cursor: 'pointer', background: 'rgba(2, 6, 23, 0.8)', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                <input type="checkbox" checked={seedIntentVerified} onChange={(e) => setSeedIntentVerified(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                <span>I have reviewed and verified the search intent for all core seed keywords above.</span>
              </label>
            </KeywordStepGuide>
          )}

          {/* STEP 3: METHOD 1 — VARIATIONS */}
          {activeStep === 2 && (
            <KeywordStepGuide
              stepNumber={3}
              stepTitle="Method 1 — Research Natural Keyword Variations"
              whyAreWeDoingThis="A keyword variation is simply another natural way a user expresses the same basic search (e.g. 'Truck Parts Trinidad' vs 'Truck Parts in Trinidad'). You can edit search volume and toggle verification status directly in the table!"
              whatYouNeedBeforeStarting={['Core seed keywords from Step 2', 'Google Search or AI Helper']}
              exactlyWhatToDo={[
                'Review your seed keywords.',
                'Identify natural word-order permutations (e.g. preposition in, for, near).',
                'Use the AI Candidate Helper to generate candidate variations, or add manually below.',
                'Click status badges to toggle verification status, and enter search volume directly in the input box!'
              ]}
              whatToLookFor={['Preposition variations', 'Word-order swaps', 'Commercial prefix variations']}
              whatToRecord={['Variation Keyword', 'Parent Seed Keyword', 'Search Volume', 'Verification Status']}
              lectureExample={{
                scenario: 'Seed: "General Contractors Calgary".',
                action: 'Instructor identifies natural word-order variations.',
                result: 'Variations: "General Contractors in Calgary", "Calgary General Contractors".'
              }}
              doneWhen="At least 1 Keyword Variation record is added to research dataset."
              isStepValidated={localProject.variations.length > 0}
              validationMessage="Add at least 1 variation record manually or via AI Candidate Helper."
              aiPromptHelper={{
                promptTitle: 'Controlled AI Candidate Variation Prompt Generator',
                promptText: generateAiPromptStep3(),
                onImportCandidates: handleImportStep3AiCandidates,
              }}
              onPrevious={() => handleStepChange(1)}
              onContinue={() => handleStepChange(3)}
            >
              <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Single variation keyword (e.g. Truck Parts in Trinidad)..."
                  value={singleKw}
                  onChange={(e) => setSingleKw(e.target.value)}
                />
                <Button variant="primary" icon={<Plus size={16} />} onClick={() => {
                  if (!singleKw.trim()) return;
                  const item: KeywordVariationItem = {
                    id: `var-${Date.now()}`,
                    keyword: singleKw.trim(),
                    variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Seed',
                    searchVolumeStatus: 'not_collected',
                    intent: 'Commercial',
                    serpSimilarity: 'High',
                    separateTarget: false,
                    reason: 'Manually entered variation',
                    verificationStatus: 'user_verified',
                  };
                  updateProject({
                    ...localProject,
                    variations: [...localProject.variations, item],
                  });
                  setSingleKw('');
                }}>
                  Add Variation Record
                </Button>
              </div>

              <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '10px' }}>
                Researched Variations Dataset ({localProject.variations.length})
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Editable Variation Keyword</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Parent Seed</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Clickable Verification Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', width: '50px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {localProject.variations.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="text"
                          className="input-field"
                          style={{ width: '100%', padding: '4px 8px', fontSize: '13px', fontWeight: 700, color: '#ffffff' }}
                          value={v.keyword}
                          onChange={(e) => {
                            const newKw = e.target.value;
                            const updated = localProject.variations.map(item =>
                              item.id === v.id ? { ...item, keyword: newKw } : item
                            );
                            updateProject({ ...localProject, variations: updated });
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>{v.variationOf}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <button
                          onClick={() => {
                            const updated: KeywordVariationItem[] = localProject.variations.map(item =>
                              item.id === v.id
                                ? {
                                    ...item,
                                    verificationStatus: (item.verificationStatus === 'user_verified' ? 'ai_candidate' : 'user_verified') as 'ai_candidate' | 'user_verified',
                                  }
                                : item
                            );
                            updateProject({ ...localProject, variations: updated });
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          title="Click to toggle status between AI-Assisted and User Verified"
                        >
                          <Badge variant={v.verificationStatus === 'user_verified' ? 'complete' : 'lecture'}>
                            {v.verificationStatus === 'user_verified' ? '✓ User Verified' : 'AI-Assisted (Click to Verify)'}
                          </Badge>
                        </button>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            const updated = localProject.variations.filter(x => x.id !== v.id);
                            updateProject({ ...localProject, variations: updated });
                          }}
                          style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </KeywordStepGuide>
          )}

          {/* STEP 4: METHOD 2 — CLIENT WEBSITE CONTENT */}
          {activeStep === 3 && (() => {
            const totalNavPages = Math.max(1, localProject.businessProfile.totalWebsitePages || 4);
            const auditedPagesSet = new Set(localProject.websiteContentKeywords.map(k => k.sourcePage.trim().toLowerCase()).filter(Boolean));
            const uniquePagesCount = auditedPagesSet.size;
            const requiredPagesCount = Math.max(1, Math.ceil(totalNavPages * 0.5));
            const isCoverageMet = uniquePagesCount >= requiredPagesCount;
            const isStep4Validated = localProject.websiteContentKeywords.length > 0 && isCoverageMet;
            const coveragePct = Math.min(100, Math.round((uniquePagesCount / totalNavPages) * 100));

            return (
              <KeywordStepGuide
                stepNumber={4}
                stepTitle="Method 2 — Extract Keywords from Client's Website Content (All Pages)"
                whyAreWeDoingThis="Instructor Teaching: Check the Homepage first (Title, Meta, H1, Body), and then check OTHER key website pages (Service pages, Product pages, Location pages). Never skip keywords from inner pages! To ensure thorough research, you must audit keywords from at least 50% of key navigation pages."
                whatYouNeedBeforeStarting={['Client website open in browser tab', 'Main navigation menu page count', 'Or full-page website screenshot + AI Helper']}
                exactlyWhatToDo={[
                  'Enter total number of key navigation pages on the client website below.',
                  'Audit Homepage first, then audit inner service/product pages.',
                  'Record extracted keywords along with their Source Page name and Where Found location.',
                  'Achieve at least 50% page coverage to proceed to the next step!'
                ]}
                whatToLookFor={['Page titles', 'H1/H2 headings', 'Service descriptions', 'Target location terms']}
                whatToRecord={['Extracted Keyword', 'Source Page', 'Where Found on Page']}
                lectureExample={{
                  scenario: 'Client website has 4 main navigation pages (Home, Truck Parts, Bus Parts, Contact).',
                  action: 'Instructor audits Home and Truck Parts (50% coverage), extracting titles and H1s.',
                  result: 'Records keywords under Website Content dataset with 50% page coverage.'
                }}
                doneWhen="At least 1 keyword is recorded and keywords are extracted from at least 50% of navigation pages."
                isStepValidated={isStep4Validated}
                validationMessage={`Audit and record keywords from at least 50% of navigation pages (${uniquePagesCount}/${requiredPagesCount} required pages audited).`}
                aiPromptHelper={{
                  promptTitle: 'AI Full-Page Screenshot & Website Content Prompt Generator',
                  promptText: generateAiPromptStep4(),
                  onImportCandidates: handleImportStep4AiCandidates,
                }}
                onPrevious={() => handleStepChange(2)}
                onContinue={() => handleStepChange(4)}
              >
                {/* PAGE COVERAGE TRACKER CARD */}
                <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Sparkles size={16} color="var(--accent-blue)" />
                      <span>WEBSITE PAGE COVERAGE TRACKER (50% MINIMUM REQUIRED)</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>Total Navigation Pages:</label>
                      <input
                        type="number"
                        className="input-field"
                        style={{ width: '80px', padding: '4px 8px', fontSize: '13px', textAlign: 'center', fontWeight: 700 }}
                        min={1}
                        value={localProject.businessProfile.totalWebsitePages || 4}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10) || 1;
                          updateProject({
                            ...localProject,
                            businessProfile: { ...localProject.businessProfile, totalWebsitePages: val }
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* Coverage Progress Bar */}
                  <div style={{ background: '#020617', borderRadius: '8px', padding: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px' }}>
                      <span style={{ color: '#ffffff', fontWeight: 700 }}>
                        Pages Audited: <span style={{ color: 'var(--accent-blue)' }}>{uniquePagesCount}</span> / {totalNavPages} Total Pages ({coveragePct}% Coverage)
                      </span>
                      <Badge variant={isCoverageMet ? 'complete' : 'lecture'}>
                        {isCoverageMet ? `✓ 50% Coverage Target Met (${uniquePagesCount}/${requiredPagesCount} pages)` : `⚠️ Need ${requiredPagesCount - uniquePagesCount} More Page(s) Audited`}
                      </Badge>
                    </div>

                    <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${coveragePct}%`, height: '100%', background: isCoverageMet ? 'var(--accent-green)' : 'var(--accent-yellow)', transition: 'width 0.3s ease' }} />
                    </div>

                    {/* Audited Page Chips */}
                    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Audited Pages:</span>
                      {Array.from(auditedPagesSet).map((p, i) => (
                        <span key={i} style={{ fontSize: '11px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '12px', border: '1px solid rgba(56, 189, 248, 0.3)', fontWeight: 600 }}>
                          📄 {p}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              {linkedAudit && (
                <div style={{ marginBottom: '16px' }}>
                  <Button variant="success" size="sm" icon={<Sparkles size={14} />} onClick={() => {
                    setImportedStatus(true);
                  }}>
                    {importedStatus ? '✓ Crawled Content Imported!' : '1-Click Import Crawled Content from Linked Audit'}
                  </Button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr auto', gap: '10px', marginBottom: '14px' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Extracted keyword (e.g. Truck Parts in Trinidad)..."
                  value={singleKw}
                  onChange={(e) => setSingleKw(e.target.value)}
                />
                <input
                  type="text"
                  className="input-field"
                  placeholder="Source Page (e.g. Homepage, Services/Truck-Parts)..."
                  value={singleSourcePage}
                  onChange={(e) => setSingleSourcePage(e.target.value)}
                />
                <select
                  className="input-field"
                  value={singleWhereFound}
                  onChange={(e) => setSingleWhereFound(e.target.value as any)}
                >
                  <option value="Title">Title Tag (&lt;title&gt;)</option>
                  <option value="Meta">Meta Description</option>
                  <option value="H1">H1 Heading</option>
                  <option value="H2/H3">H2/H3 Heading</option>
                  <option value="Body">Body Paragraph Text</option>
                </select>
                <Button variant="primary" icon={<Plus size={14} />} onClick={() => {
                  if (!singleKw.trim()) return;
                  const item: WebsiteContentKeywordItem = {
                    id: `wk-${Date.now()}`,
                    keyword: singleKw.trim(),
                    sourcePage: singleSourcePage.trim() || 'Homepage',
                    whereFound: singleWhereFound,
                    intent: 'Commercial',
                    relevant: true,
                    existingTarget: true,
                    verificationStatus: 'user_verified',
                  };
                  updateProject({ ...localProject, websiteContentKeywords: [...localProject.websiteContentKeywords, item] });
                  setSingleKw('');
                }}>
                  Add Extracted Term
                </Button>
              </div>

              <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '10px' }}>
                Website Extracted Keywords Dataset ({localProject.websiteContentKeywords.length})
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Keyword</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Editable Source Page</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Editable Where Found</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', width: '50px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {localProject.websiteContentKeywords.map(w => (
                    <tr key={w.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>{w.keyword}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="text"
                          className="input-field"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          value={w.sourcePage}
                          onChange={(e) => {
                            const updated = localProject.websiteContentKeywords.map(item =>
                              item.id === w.id ? { ...item, sourcePage: e.target.value } : item
                            );
                            updateProject({ ...localProject, websiteContentKeywords: updated });
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <select
                          className="input-field"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          value={w.whereFound}
                          onChange={(e) => {
                            const updated = localProject.websiteContentKeywords.map(item =>
                              item.id === w.id ? { ...item, whereFound: e.target.value as any } : item
                            );
                            updateProject({ ...localProject, websiteContentKeywords: updated });
                          }}
                        >
                          <option value="Title">Title Tag (&lt;title&gt;)</option>
                          <option value="Meta">Meta Description</option>
                          <option value="H1">H1 Heading</option>
                          <option value="H2/H3">H2/H3 Heading</option>
                          <option value="Body">Body Paragraph Text</option>
                        </select>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            const updated = localProject.websiteContentKeywords.filter(x => x.id !== w.id);
                            updateProject({ ...localProject, websiteContentKeywords: updated });
                          }}
                          style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </KeywordStepGuide>
          );
        })()}

          {/* STEP 5: METHOD 3 — COMPETITOR KEYWORDS */}
          {activeStep === 4 && (() => {
            const uniqueCompetitorsCount = new Set(
              localProject.competitorKeywords
                .map(c => c.competitorName.trim().toLowerCase())
                .filter(Boolean)
            ).size;
            const isStep5Validated = uniqueCompetitorsCount >= 3;

            return (
              <KeywordStepGuide
                stepNumber={5}
                stepTitle="Method 3 — Competitor Keyword Analysis & Service Verification"
                whyAreWeDoingThis="Section 1 analyzes Google SERPs to discover top competitors and search snippet terms. Section 2 performs deep analysis on the top 3 competitor websites. CRITICAL: Verify if your client actually offers each service — if NO, reject the term as an unsupported service!"
                whatYouNeedBeforeStarting={['Google Search open in browser', 'At least 3 competitor website URLs or page screenshots']}
                exactlyWhatToDo={[
                  'SECTION 1: Search target keywords on Google SERP and note top ranking competitor domains.',
                  'SECTION 2: Inspect top 3 competitor websites (Page Titles, Meta, H1s, and Service Pages).',
                  'Verify whether your client ACTUALLY offers each extracted service (Yes/No).',
                  'Analyze and verify keywords from at least 3 distinct competitor domains to complete this step.'
                ]}
                whatToLookFor={['Google SERP competitor domains', 'Competitor title keywords', 'Service category offerings']}
                whatToRecord={['Competitor Domain', 'Keyword', 'Client Offers This? (Yes/No)', 'Opportunity Status (Gap/No)']}
                whatNotToRecord={['Do NOT add competitor services that the client does NOT offer as target keywords!']}
                lectureExample={{
                  scenario: 'Competitor ranks for "Car Engine Repair". Client only sells truck parts.',
                  action: 'Instructor checks service offering.',
                  result: 'Marks term as NOT A CLIENT SERVICE and rejects from target list.'
                }}
                doneWhen="At least 3 distinct competitor websites are analyzed and service verified."
                isStepValidated={isStep5Validated}
                validationMessage={`Analyze keywords from at least 3 distinct competitors (Currently analyzed: ${uniqueCompetitorsCount}/3 competitors).`}
                onPrevious={() => handleStepChange(3)}
                onContinue={() => handleStepChange(5)}
              >
                {/* 📍 SECTION 1: GOOGLE SERP ANALYSIS & COMPETITOR DISCOVERY */}
                <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Search size={16} color="var(--accent-blue)" />
                      <span>SECTION 1: GOOGLE SERP ANALYSIS & SUGGESTED KEYWORDS</span>
                    </div>

                    <Button
                      size="sm"
                      variant={step5AiModalMode === 'serp' ? 'primary' : 'secondary'}
                      icon={<Sparkles size={14} color={step5AiModalMode === 'serp' ? '#ffffff' : 'var(--accent-blue)'} />}
                      onClick={() => {
                        setStep5PastedText('');
                        setStep5ImportStatus('');
                        setStep5AiModalMode(step5AiModalMode === 'serp' ? null : 'serp');
                      }}
                    >
                      {step5AiModalMode === 'serp' ? '✕ Hide Inline AI SERP Helper' : '⚡ AI SERP Screenshot Helper (Inline)'}
                    </Button>
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                    Search core target keywords on Google SERP to extract suggested terms from SERP titles, snippets, People Also Ask, and Related Searches.
                  </div>

                  {/* ⚡ INLINE AI SERP SCREENSHOT HELPER (NON-MODAL) */}
                  {step5AiModalMode === 'serp' && (
                    <div style={{ background: '#020617', border: '1px solid var(--accent-blue)', borderRadius: '8px', padding: '16px', marginBottom: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Sparkles size={16} color="#38bdf8" />
                          <span>Inline AI Google SERP Screenshot Helper</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setStep5AiModalMode(null)}
                          style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#94a3b8', width: '24px', height: '24px', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          ✕
                        </button>
                      </div>

                      <p style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.4 }}>
                        Attach a full-page screenshot of the Google Search Results Page (SERP) to extract top ranking competitor domains and SERP snippet terms.
                      </p>

                      <div style={{ background: '#090d16', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '10px' }}>
                        <pre style={{ fontSize: '11px', color: '#38bdf8', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                          {generateAiPromptStep5SERP()}
                        </pre>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={step5Copied ? <Check size={14} color="var(--accent-green)" /> : <Copy size={14} />}
                          onClick={() => {
                            navigator.clipboard.writeText(generateAiPromptStep5SERP());
                            setStep5Copied(true);
                            setTimeout(() => setStep5Copied(false), 2500);
                          }}
                        >
                          {step5Copied ? 'Copied Prompt to Clipboard!' : '1. Copy AI Prompt'}
                        </Button>
                      </div>

                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '4px' }}>
                          2. Paste AI Response Text Below & Auto-Import:
                        </label>
                        <textarea
                          className="input-field"
                          rows={3}
                          style={{ fontFamily: 'monospace', fontSize: '11px', width: '100%', marginBottom: '10px', background: '#090d16' }}
                          placeholder={`Competitor: competitor1.com\nKeyword: Heavy Duty Truck Parts Trinidad`}
                          value={step5PastedText}
                          onChange={(e) => setStep5PastedText(e.target.value)}
                        />

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                          <Button
                            size="sm"
                            variant="primary"
                            icon={<Sparkles size={14} />}
                            disabled={!step5PastedText.trim()}
                            onClick={() => {
                              const res = handleImportStep5SERPAiCandidates(step5PastedText);
                              if (res && res.count > 0) {
                                setStep5ImportStatus(`✓ Successfully imported ${res.count} candidates into SERP dataset!`);
                                setStep5PastedText('');
                              } else {
                                setStep5ImportStatus(res?.error || '⚠️ Could not detect any valid competitor keywords.');
                              }
                              setTimeout(() => setStep5ImportStatus(''), 4000);
                            }}
                          >
                            3. Parse & Auto-Import Candidates
                          </Button>
                          {step5ImportStatus && (
                            <div style={{ fontSize: '11.5px', color: step5ImportStatus.startsWith('✓') ? 'var(--accent-green)' : '#f59e0b', fontWeight: 700 }}>
                              {step5ImportStatus}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SERP Target Keywords Quick Search Bar with Checkmark Tracker */}
                  {(() => {
                    const seedTerms = localProject.seedKeywords.map(s => s.seedKeyword);
                    const varTerms = localProject.variations.map(v => v.keyword);
                    const targetTerms = Array.from(new Set([...seedTerms, ...varTerms])).filter(Boolean);
                    const checkedCount = targetTerms.filter(t => checkedSerpTerms.includes(t)).length;

                    return (
                      <div style={{ background: '#020617', padding: '14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent-blue)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>Target Keywords to Search on Google SERP ({targetTerms.length})</span>
                          </div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: checkedCount === targetTerms.length ? '#34d399' : '#94a3b8', background: 'rgba(15, 23, 42, 0.8)', padding: '3px 10px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            {checkedCount === targetTerms.length ? '✓ All Terms Inspected!' : `Inspected: ${checkedCount} / ${targetTerms.length}`}
                          </div>
                        </div>

                        {targetTerms.length > 0 ? (
                          <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '10px' }}>
                              {targetTerms.map((kw, idx) => {
                                const isChecked = checkedSerpTerms.includes(kw);
                                return (
                                  <div
                                    key={idx}
                                    onClick={() => toggleCheckedSerpTerm(kw)}
                                    style={{
                                      background: isChecked ? 'rgba(16, 185, 129, 0.08)' : 'rgba(15, 23, 42, 0.6)',
                                      padding: '10px 14px',
                                      borderRadius: '8px',
                                      border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '12px',
                                      cursor: 'pointer',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      transition: 'all 0.15s ease',
                                    }}
                                  >
                                    {/* Left Accent Indicator Bar */}
                                    {isChecked && (
                                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#34d399' }} />
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1, paddingLeft: isChecked ? '4px' : 0 }}>
                                      {/* Circular Checkmark Badge */}
                                      <div
                                        style={{
                                          width: '18px',
                                          height: '18px',
                                          borderRadius: '50%',
                                          background: isChecked ? '#10b981' : 'transparent',
                                          border: isChecked ? '1px solid #34d399' : '1.5px solid rgba(255,255,255,0.25)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                          transition: 'all 0.15s ease',
                                        }}
                                      >
                                        {isChecked && <Check size={11} color="#ffffff" strokeWidth={3} />}
                                      </div>

                                      <span
                                        style={{
                                          fontSize: '12.5px',
                                          fontWeight: 700,
                                          color: isChecked ? '#34d399' : '#ffffff',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                        title={`${idx + 1}. "${kw}"`}
                                      >
                                        {idx + 1}. "{kw}"
                                      </span>
                                    </div>

                                    <a
                                      href={`https://www.google.com/search?q=${encodeURIComponent(kw)}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!checkedSerpTerms.includes(kw)) {
                                          toggleCheckedSerpTerm(kw);
                                        }
                                      }}
                                      style={{
                                        fontSize: '11px',
                                        color: isChecked ? '#34d399' : '#38bdf8',
                                        textDecoration: 'none',
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        background: isChecked ? 'rgba(52, 211, 153, 0.15)' : 'rgba(56, 189, 248, 0.1)',
                                        padding: '5px 10px',
                                        borderRadius: '5px',
                                        border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(56, 189, 248, 0.3)',
                                        flexShrink: 0,
                                      }}
                                    >
                                      Search Google <ExternalLink size={11} />
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            No target keywords found from Steps 2 & 3 yet.
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Manual Entry Form for Discovered SERP Keywords */}
                  <div style={{ background: '#020617', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>
                      Add SERP Discovered Keyword Term Manually:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr auto', gap: '10px' }}>
                      <input type="text" className="input-field" placeholder="Discovered SERP Keyword (e.g. truck parts Trinidad)..." value={singleKw} onChange={(e) => setSingleKw(e.target.value)} />
                      <select className="input-field" value={singleRelatedSource} onChange={(e) => setSingleRelatedSource(e.target.value)}>
                        <option value="SERP (Organic Snippet)">SERP (Organic Snippet)</option>
                        <option value="SERP (People Also Ask)">SERP (People Also Ask)</option>
                        <option value="SERP (Related Searches)">SERP (Related Searches)</option>
                        <option value="SERP (Google Autocomplete)">SERP (Google Autocomplete)</option>
                      </select>
                      <input type="text" className="input-field" placeholder="Competitor Domain / Origin (Optional)..." value={singleComp} onChange={(e) => setSingleComp(e.target.value)} />
                      <Button variant="primary" icon={<Plus size={14} />} onClick={() => {
                        if (!singleKw.trim()) return;
                        const item: CompetitorKeywordItem = {
                          id: `comp-serp-${Date.now()}`,
                          competitorName: singleComp.trim() || 'Google SERP',
                          keyword: singleKw.trim(),
                          service: singleRelatedSource || 'SERP (Organic Snippet)',
                          location: localProject.businessProfile.primaryLocation,
                          searchIntent: 'Commercial',
                          clientOffersThis: true,
                          addToResearch: true,
                          opportunity: 'Gap',
                          verificationStatus: 'user_verified',
                        };
                        updateProject({ ...localProject, competitorKeywords: [...localProject.competitorKeywords, item] });
                        setSingleKw('');
                      }}>
                        Add SERP Term
                      </Button>
                    </div>
                  </div>

                  {/* TABLE 1: Discovered SERP Keywords Dataset */}
                  {(() => {
                    const serpItems = localProject.competitorKeywords.filter(c => c.service.startsWith('SERP') || ['Google SERP', 'People Also Ask', 'Related Searches'].includes(c.competitorName));

                    return (
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '8px' }}>
                          LIST 1: SUGGESTED TARGET KEYWORDS DISCOVERED ON SERP ({serpItems.length})
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                              <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Discovered SERP Keyword</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>SERP Source / Feature</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Competitor / Origin</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', width: '50px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {serpItems.length > 0 ? (
                                serpItems.map(c => (
                                  <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(15, 23, 42, 0.4)' }}>
                                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>{c.keyword}</td>
                                    <td style={{ padding: '8px 12px', fontSize: '12px', color: '#38bdf8' }}>{c.service}</td>
                                    <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>{c.competitorName}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      <button
                                        onClick={() => {
                                          const updated = localProject.competitorKeywords.filter(x => x.id !== c.id);
                                          updateProject({ ...localProject, competitorKeywords: updated });
                                        }}
                                        style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                                    No SERP keywords recorded yet. Add SERP terms above or use the AI SERP Screenshot Helper!
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* 🏢 SECTION 2: TOP 3 COMPETITOR WEBPAGE ANALYSIS & SERVICE VERIFICATION */}
                <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Sparkles size={16} color="var(--accent-blue)" />
                      <span>SECTION 2: DEEP TOP 3 COMPETITOR WEBPAGE ANALYSIS</span>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Button
                        size="sm"
                        variant={step5AiModalMode === 'webpage' ? 'primary' : 'secondary'}
                        icon={<Sparkles size={14} color={step5AiModalMode === 'webpage' ? '#ffffff' : 'var(--accent-blue)'} />}
                        onClick={() => {
                          setStep5PastedText('');
                          setStep5ImportStatus('');
                          setStep5AiModalMode(step5AiModalMode === 'webpage' ? null : 'webpage');
                        }}
                      >
                        {step5AiModalMode === 'webpage' ? '✕ Hide Inline Webpage Helper' : '⚡ AI Competitor Webpage Screenshot Helper (Inline)'}
                      </Button>
                      <Badge variant={isStep5Validated ? 'complete' : 'lecture'}>
                        {isStep5Validated ? `✓ 3 Competitors Requirement Met (${uniqueCompetitorsCount} analyzed)` : `⚠️ ${3 - uniqueCompetitorsCount} More Competitor Domain(s) Required`}
                      </Badge>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                    Inspect top 3 competitor websites (Titles, Meta, H1s, Service Pages). Verify whether your client ACTUALLY offers each extracted service.
                  </div>

                  {/* ⚡ INLINE AI WEBPAGE SCREENSHOT HELPER (NON-MODAL) */}
                  {step5AiModalMode === 'webpage' && (
                    <div style={{ background: '#020617', border: '1px solid var(--accent-blue)', borderRadius: '8px', padding: '16px', marginBottom: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Sparkles size={16} color="#38bdf8" />
                          <span>Inline AI Competitor Webpage Screenshot Helper</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setStep5AiModalMode(null)}
                          style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#94a3b8', width: '24px', height: '24px', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          ✕
                        </button>
                      </div>

                      <p style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.4 }}>
                        Attach a full-page screenshot of a competitor webpage to extract targeted terms and compare them against confirmed client services.
                      </p>

                      <div style={{ background: '#090d16', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '10px' }}>
                        <pre style={{ fontSize: '11px', color: '#38bdf8', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                          {generateAiPromptStep5Webpage()}
                        </pre>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={step5Copied ? <Check size={14} color="var(--accent-green)" /> : <Copy size={14} />}
                          onClick={() => {
                            navigator.clipboard.writeText(generateAiPromptStep5Webpage());
                            setStep5Copied(true);
                            setTimeout(() => setStep5Copied(false), 2500);
                          }}
                        >
                          {step5Copied ? 'Copied Prompt to Clipboard!' : '1. Copy AI Prompt'}
                        </Button>
                      </div>

                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '4px' }}>
                          2. Paste AI Response Text Below & Auto-Import:
                        </label>
                        <textarea
                          className="input-field"
                          rows={3}
                          style={{ fontFamily: 'monospace', fontSize: '11px', width: '100%', marginBottom: '10px', background: '#090d16' }}
                          placeholder={`Competitor: competitor1.com\nKeyword: Heavy Duty Truck Parts\nClientOffersThis: Yes\nOpportunity: Gap`}
                          value={step5PastedText}
                          onChange={(e) => setStep5PastedText(e.target.value)}
                        />

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                          <Button
                            size="sm"
                            variant="primary"
                            icon={<Sparkles size={14} />}
                            disabled={!step5PastedText.trim()}
                            onClick={() => {
                              const res = handleImportStep5WebpageAiCandidates(step5PastedText);
                              if (res && res.count > 0) {
                                setStep5ImportStatus(`✓ Successfully imported ${res.count} candidates into competitor webpage dataset!`);
                                setStep5PastedText('');
                              } else {
                                setStep5ImportStatus(res?.error || '⚠️ Could not detect any valid competitor keywords.');
                              }
                              setTimeout(() => setStep5ImportStatus(''), 4000);
                            }}
                          >
                            3. Parse & Auto-Import Candidates
                          </Button>
                          {step5ImportStatus && (
                            <div style={{ fontSize: '11.5px', color: step5ImportStatus.startsWith('✓') ? 'var(--accent-green)' : '#f59e0b', fontWeight: 700 }}>
                              {step5ImportStatus}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Competitor Keyword Form */}
                  <div style={{ background: '#020617', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>
                      Add Extracted Competitor Keyword Term:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                      <input type="text" className="input-field" placeholder="Competitor Domain (e.g. competitor1.com)..." value={singleComp} onChange={(e) => setSingleComp(e.target.value)} />
                      <input type="text" className="input-field" placeholder="Extracted Keyword (e.g. Heavy Duty Truck Parts)..." value={singleKw} onChange={(e) => setSingleKw(e.target.value)} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#ffffff', cursor: 'pointer', background: 'rgba(15, 23, 42, 0.8)', padding: '6px 10px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <input type="checkbox" checked={singleOffers} onChange={(e) => setSingleOffers(e.target.checked)} />
                        <span>Client Offers This Service?</span>
                      </label>
                      <Button variant="primary" icon={<Plus size={14} />} onClick={() => {
                        if (!singleKw.trim() || !singleComp.trim()) return;
                        const item: CompetitorKeywordItem = {
                          id: `comp-${Date.now()}`,
                          competitorName: singleComp.trim(),
                          keyword: singleKw.trim(),
                          service: 'Competitor Webpage Term',
                          location: localProject.businessProfile.primaryLocation,
                          searchIntent: 'Commercial',
                          clientOffersThis: singleOffers,
                          addToResearch: singleOffers,
                          opportunity: singleOffers ? 'Gap' : 'No',
                          verificationStatus: 'user_verified',
                        };

                        let profile = { ...localProject.businessProfile };
                        if (!singleOffers && !profile.unsupportedServices.includes(singleKw.trim())) {
                          profile.unsupportedServices = [...profile.unsupportedServices, singleKw.trim()];
                        }

                        updateProject({
                          ...localProject,
                          businessProfile: profile,
                          competitorKeywords: [...localProject.competitorKeywords, item],
                        });
                        setSingleKw('');
                      }}>
                        Add Competitor Term
                      </Button>
                    </div>
                  </div>

                  {/* TABLE 2: Researched Competitor Webpage Keywords Dataset */}
                  {(() => {
                    const webpageItems = localProject.competitorKeywords.filter(c => !c.service.startsWith('SERP') && !['Google SERP', 'People Also Ask', 'Related Searches'].includes(c.competitorName));
                    const uniqueWebpageCompNames = Array.from(new Set(webpageItems.map(c => c.competitorName.trim()).filter(Boolean)));

                    return (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff' }}>
                            LIST 2: COMPETITOR WEBPAGES TARGET KEYWORDS DATASET ({webpageItems.length})
                          </div>

                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {uniqueWebpageCompNames.map((name, i) => (
                              <Badge key={i} variant="complete">
                                🏢 {name}
                              </Badge>
                            ))}
                            {uniqueCompetitorsCount < 3 && (
                              <Badge variant="lecture">
                                ⚠️ Need {3 - uniqueCompetitorsCount} More Competitor(s)
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div style={{ maxHeight: '520px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                              <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Editable Competitor Domain</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Extracted Keyword</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Clickable Service Verification</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Opportunity Status</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', width: '50px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {webpageItems.length > 0 ? (
                                webpageItems.map(c => (
                                  <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(15, 23, 42, 0.4)' }}>
                                    <td style={{ padding: '8px 12px' }}>
                                      <input
                                        type="text"
                                        className="input-field"
                                        style={{ padding: '4px 8px', fontSize: '12px' }}
                                        value={c.competitorName}
                                        onChange={(e) => {
                                          const updated = localProject.competitorKeywords.map(item =>
                                            item.id === c.id ? { ...item, competitorName: e.target.value } : item
                                          );
                                          updateProject({ ...localProject, competitorKeywords: updated });
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>{c.keyword}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <button
                                        onClick={() => {
                                          const newOffers = !c.clientOffersThis;
                                          let profile = { ...localProject.businessProfile };
                                          if (!newOffers && !profile.unsupportedServices.includes(c.keyword)) {
                                            profile.unsupportedServices = [...profile.unsupportedServices, c.keyword];
                                          }
                                          const updated = localProject.competitorKeywords.map(item =>
                                            item.id === c.id
                                              ? {
                                                  ...item,
                                                  clientOffersThis: newOffers,
                                                  addToResearch: newOffers,
                                                  opportunity: (newOffers ? 'Gap' : 'No') as 'Gap' | 'No',
                                                }
                                              : item
                                          );
                                          updateProject({ ...localProject, businessProfile: profile, competitorKeywords: updated });
                                        }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                        title="Click to toggle between Confirmed Service and Unsupported Service"
                                      >
                                        <Badge variant={c.clientOffersThis ? 'complete' : 'lecture'}>
                                          {c.clientOffersThis ? '✓ Client Offers Service' : '✕ REJECTED — Unsupported'}
                                        </Badge>
                                      </button>
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <button
                                        onClick={() => {
                                          const updated = localProject.competitorKeywords.map(item =>
                                            item.id === c.id
                                              ? { ...item, opportunity: (item.opportunity === 'Gap' ? 'No' : 'Gap') as 'Gap' | 'No' }
                                              : item
                                          );
                                          updateProject({ ...localProject, competitorKeywords: updated });
                                        }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                        title="Click to toggle opportunity status"
                                      >
                                      </button>
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      <button
                                        onClick={() => {
                                          const updated = localProject.competitorKeywords.filter(x => x.id !== c.id);
                                          updateProject({ ...localProject, competitorKeywords: updated });
                                        }}
                                        style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                                    No competitor webpage keywords recorded yet. Add keywords manually above or use the AI Competitor Webpage Screenshot Helper!
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* 📊 ACCUMULATED KEYWORDS SUMMARY (STEPS 1-5 AUDIT PROGRESS & DEDUPLICATION) */}
                {(() => {
                  const accumulated = (() => {
                    const all: { keyword: string; stepName: string; originalStep: string; intent?: string }[] = [];

                    // Step 2
                    localProject.seedKeywords.forEach(s => {
                      if (s.seedKeyword && s.seedKeyword.trim()) {
                        all.push({ keyword: s.seedKeyword.trim(), stepName: 'Step 2: Seed Keywords', originalStep: 'Step 2', intent: s.intent });
                      }
                    });

                    // Step 3
                    localProject.variations.forEach(v => {
                      if (v.keyword && v.keyword.trim()) {
                        all.push({ keyword: v.keyword.trim(), stepName: 'Step 3: Keyword Variations', originalStep: 'Step 3', intent: v.intent });
                      }
                    });

                    // Step 4
                    localProject.websiteContentKeywords.forEach(w => {
                      if (w.keyword && w.keyword.trim()) {
                        all.push({ keyword: w.keyword.trim(), stepName: `Step 4: Client Website (${w.sourcePage || 'Page'})`, originalStep: 'Step 4', intent: w.intent });
                      }
                    });

                    // Step 5
                    localProject.competitorKeywords.forEach(c => {
                      if (c.keyword && c.keyword.trim()) {
                        const stepLabel = c.service.startsWith('SERP') || ['Google SERP', 'People Also Ask', 'Related Searches'].includes(c.competitorName)
                          ? 'Step 5: Discovered SERP Keyword'
                          : `Step 5: Competitor Webpage (${c.competitorName})`;
                        all.push({ keyword: c.keyword.trim(), stepName: stepLabel, originalStep: 'Step 5', intent: c.searchIntent });
                      }
                    });

                    // Deduplicate case-insensitively
                    const seen = new Set<string>();
                    const uniqueList: typeof all = [];
                    let duplicatesCount = 0;

                    all.forEach(item => {
                      const key = item.keyword.toLowerCase();
                      if (!seen.has(key)) {
                        seen.add(key);
                        uniqueList.push(item);
                      } else {
                        duplicatesCount++;
                      }
                    });

                    return { uniqueList, totalRawCount: all.length, duplicatesCount };
                  })();

                  return (
                    <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px', marginTop: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={18} color="var(--accent-green)" />
                          <span>ALL ACCUMULATED KEYWORDS GATHERED SO FAR (STEPS 1–5 PROGRESS SUMMARY)</span>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Badge variant="complete">
                            Total Unique Keywords: {accumulated.uniqueList.length}
                          </Badge>
                          {accumulated.duplicatesCount > 0 && (
                            <Badge variant="lecture">
                              ⚡ {accumulated.duplicatesCount} Repetitive Keywords Filtered Out
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                        Consolidated master dataset of all keywords gathered from Steps 2, 3, 4, and 5. Repetitive duplicate terms have been automatically filtered out.
                      </div>

                      <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{ background: '#020617', color: 'var(--accent-green)', fontSize: '11px', textTransform: 'uppercase' }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left' }}>#</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Accumulated Unique Keyword</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Originating Step Name</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Commercial Intent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accumulated.uniqueList.length > 0 ? (
                              accumulated.uniqueList.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(15, 23, 42, 0.4)' }}>
                                  <td style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>{idx + 1}</td>
                                  <td style={{ padding: '8px 12px', fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>{item.keyword}</td>
                                  <td style={{ padding: '8px 12px' }}>
                                    <span style={{ fontSize: '11px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '12px', border: '1px solid rgba(56, 189, 248, 0.3)', fontWeight: 600 }}>
                                      {item.stepName}
                                    </span>
                                  </td>
                                  <td style={{ padding: '8px 12px' }}>
                                    <Badge variant="tool">{item.intent || 'Commercial'}</Badge>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                                  No keywords gathered across Steps 1–5 yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </KeywordStepGuide>
            );
          })()}

          {/* STEP 6: METHOD 4 — KEYWORD RESEARCH TOOLS (SEMRUSH) */}
          {activeStep === 5 && (
            <KeywordStepGuide
              stepNumber={6}
              stepTitle="Method 4 — SEMrush Keyword Research"
              whyAreWeDoingThis="Use SEMrush to discover high-value commercial search terms, service variations, and buyer intent keywords that users actively search for."
              whatYouNeedBeforeStarting={['SEMrush open in browser tab']}
              exactlyWhatToDo={[
                'Enter core seed keywords into SEMrush.',
                'Filter for local country/city search intent.',
                'Record keyword suggestions and intent below.',
                'Use the AI SEMrush Helper to generate tool candidates if manual tool data is pending.'
              ]}
              whatToLookFor={['Commercial intent phrases', 'SEMrush keyword variations']}
              whatToRecord={['Tool Name (SEMrush)', 'Keyword', 'Search Intent']}
              lectureExample={{
                scenario: 'Instructor inputs "Truck Parts" into SEMrush.',
                action: 'Extracts "Heavy Duty Truck Spare Parts".',
                result: 'Records term in SEMrush Keywords dataset.'
              }}
              doneWhen="At least 1 Tool Keyword is recorded."
              isStepValidated={localProject.toolKeywords.length > 0}
              validationMessage="Record at least 1 keyword from SEMrush below."
              aiPromptHelper={{
                promptTitle: 'AI SEMrush Keyword Research Candidate Generator',
                promptText: generateAiPromptStep6(),
                onImportCandidates: handleImportStep6AiCandidates,
              }}
              onPrevious={() => handleStepChange(4)}
              onContinue={() => handleStepChange(6)}
            >
              {/* SUGGESTED TARGET TERMS FOR SEMRUSH (UP TO 8 KEYWORDS FROM PREVIOUS STEPS) */}
              {(() => {
                const seedTerms = localProject.seedKeywords.map(s => s.seedKeyword.trim());
                const varTerms = localProject.variations.map(v => v.keyword.trim());
                const webTerms = localProject.websiteContentKeywords.map(w => w.keyword.trim());
                const serpTerms = localProject.competitorKeywords.map(c => c.keyword.trim());

                const combined = Array.from(new Set([...seedTerms, ...varTerms, ...webTerms, ...serpTerms])).filter(Boolean);
                const top8Suggested = combined.slice(0, 8);
                const checkedCount = top8Suggested.filter(t => checkedToolTerms.includes(t)).length;

                return (
                  <div style={{ background: '#020617', padding: '14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--accent-blue)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent-blue)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Search size={14} color="var(--accent-blue)" />
                        <span>SUGGESTED TARGET SEARCH TERMS FOR SEMRUSH ({top8Suggested.length} OF 8 TOP TERMS FROM PREVIOUS STEPS)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: checkedCount === top8Suggested.length && top8Suggested.length > 0 ? '#34d399' : '#94a3b8', background: 'rgba(15, 23, 42, 0.8)', padding: '3px 10px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {checkedCount === top8Suggested.length && top8Suggested.length > 0 ? '✓ All Terms Copied!' : `Copied: ${checkedCount} / ${top8Suggested.length}`}
                        </div>
                        <Badge variant="tool">Steps 2, 3, 4 & 5</Badge>
                      </div>
                    </div>

                    {top8Suggested.length > 0 ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '10px' }}>
                        {top8Suggested.map((kw, idx) => {
                          const isChecked = checkedToolTerms.includes(kw);

                          const handleCopyTerm = () => {
                            navigator.clipboard.writeText(kw);
                            setBox2SeedKw(kw);
                            setSingleKw(kw);
                            toggleCheckedToolTerm(kw);
                          };

                          return (
                            <div
                              key={idx}
                              onClick={handleCopyTerm}
                              style={{
                                background: isChecked ? 'rgba(16, 185, 129, 0.08)' : 'rgba(15, 23, 42, 0.6)',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {/* Left Accent Indicator Bar */}
                              {isChecked && (
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#34d399' }} />
                              )}

                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1, paddingLeft: isChecked ? '4px' : 0 }}>
                                {/* Circular Checkmark Badge */}
                                <div
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: isChecked ? '#10b981' : 'transparent',
                                    border: isChecked ? '1px solid #34d399' : '1.5px solid rgba(255,255,255,0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  {isChecked && <Check size={11} color="#ffffff" strokeWidth={3} />}
                                </div>

                                <span
                                  style={{
                                    fontSize: '12.5px',
                                    fontWeight: 700,
                                    color: isChecked ? '#34d399' : '#ffffff',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={`${idx + 1}. "${kw}"`}
                                >
                                  {idx + 1}. "{kw}"
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyTerm();
                                }}
                                style={{
                                  fontSize: '11px',
                                  color: isChecked ? '#34d399' : '#38bdf8',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: isChecked ? 'rgba(52, 211, 153, 0.15)' : 'rgba(56, 189, 248, 0.1)',
                                  padding: '5px 10px',
                                  borderRadius: '5px',
                                  border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(56, 189, 248, 0.3)',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                }}
                              >
                                {isChecked ? <Check size={12} color="#34d399" /> : <Copy size={12} />}
                                <span>{isChecked ? 'Copied' : 'Copy'}</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No terms collected in previous steps yet. Complete Steps 2–5 to auto-generate 8 search term suggestions!
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* DUAL BOX METHOD CONTAINER */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                
                {/* 📦 BOX 1: COMPETITOR DOMAIN KEYWORD EXTRACTION */}
                <div style={{ background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 58, 138, 0.22) 100%)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px', boxShadow: '0 4px 20px rgba(30, 58, 138, 0.12)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Building2 size={16} /> <span>BOX 1: COMPETITOR DOMAIN RESEARCH (SEMRUSH ORGANIC RESEARCH)</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    Enter competitor domain/URL into SEMrush to extract ranking keywords.
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Source Tool:</label>
                        <select className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} value={box1ToolName} onChange={(e) => setBox1ToolName(e.target.value)}>
                          <option value="SEMrush Organic Research">SEMrush Organic Research</option>
                          <option value="Ahrefs Site Explorer">Ahrefs Site Explorer</option>
                          <option value="UberSuggest Domain Audit">UberSuggest Domain Audit</option>
                          <option value="Other Tool">Other Tool</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Competitor Name / Domain:</label>
                        <input type="text" className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} placeholder="e.g. competitor1.com..." value={box1CompName} onChange={(e) => setBox1CompName(e.target.value)} />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Competitor Specific URL (Optional):</label>
                      <input type="text" className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} placeholder="https://competitor1.com/parts..." value={box1CompUrl} onChange={(e) => setBox1CompUrl(e.target.value)} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 700 }}>Extracted Competitor Keyword:</label>
                        <input type="text" className="input-field" style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800 }} placeholder="e.g. commercial truck engine parts..." value={box1Kw} onChange={(e) => setBox1Kw(e.target.value)} />
                      </div>
                      <div>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Search Intent:</label>
                        <select className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} value={box1Intent} onChange={(e) => setBox1Intent(e.target.value)}>
                          <option value="Commercial">Commercial</option>
                          <option value="Transactional">Transactional</option>
                          <option value="Informational">Informational</option>
                          <option value="Navigational">Navigational</option>
                        </select>
                      </div>
                    </div>

                    <Button
                      variant="primary"
                      size="sm"
                      style={{ marginTop: '4px', background: '#0284c7', borderColor: '#38bdf8', fontSize: '11px', fontWeight: 800 }}
                      icon={<Plus size={14} />}
                      onClick={() => {
                        if (!box1Kw.trim()) return;
                        const item: ToolKeywordItem = {
                          id: `tool-${Date.now()}`,
                          keyword: box1Kw.trim(),
                          sourceTool: box1ToolName || 'SEMrush Organic Research',
                          competitorName: box1CompName.trim() || undefined,
                          competitorLink: box1CompUrl.trim() || undefined,
                          methodType: 'competitor_domain',
                          searchVolumeStatus: 'not_collected',
                          intent: box1Intent,
                          relevant: true,
                          isVariation: false,
                          decision: 'Target',
                          verificationStatus: 'user_verified',
                        };
                        updateProject({ ...localProject, toolKeywords: [...localProject.toolKeywords, item] });
                        setBox1Kw('');
                      }}
                    >
                      Add Competitor Domain Keyword (Box 1)
                    </Button>
                  </div>
                </div>

                {/* 📦 BOX 2: SEED KEYWORD LOCATION RESEARCH */}
                <div style={{ background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(180, 83, 9, 0.22) 100%)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px', boxShadow: '0 4px 20px rgba(180, 83, 9, 0.12)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#facc15', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Search size={16} /> <span>BOX 2: SEED KEYWORD LOCATION RESEARCH (KEYWORD MAGIC TOOL)</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    Enter seed keyword in SEMrush, select target location/country, and extract matching keyword variations.
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Source Tool:</label>
                        <select className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} value={box2ToolName} onChange={(e) => setBox2ToolName(e.target.value)}>
                          <option value="SEMrush Keyword Magic Tool">SEMrush Keyword Magic Tool</option>
                          <option value="Google Keyword Planner">Google Keyword Planner</option>
                          <option value="Ahrefs Keyword Explorer">Ahrefs Keyword Explorer</option>
                          <option value="UberSuggest Keyword Ideas">UberSuggest Keyword Ideas</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Target Location / Country:</label>
                        <input type="text" className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} placeholder="e.g. Trinidad and Tobago, Canada..." value={box2Location} onChange={(e) => setBox2Location(e.target.value)} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Seed Keyword Query:</label>
                        {localProject.seedKeywords.length > 0 && (
                          <div style={{ fontSize: '10px', color: '#facc15', cursor: 'pointer' }} onClick={() => setBox2SeedKw(localProject.seedKeywords[0].seedKeyword)}>
                            Use Seed #1: "{localProject.seedKeywords[0].seedKeyword}"
                          </div>
                        )}
                      </div>
                      <input type="text" className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} placeholder="Seed keyword entered in SEMrush (e.g. truck parts)..." value={box2SeedKw} onChange={(e) => setBox2SeedKw(e.target.value)} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '10px', color: '#facc15', fontWeight: 700 }}>Extracted Location Keyword Variation:</label>
                        <input type="text" className="input-field" style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800 }} placeholder="e.g. heavy duty truck spare parts trinidad..." value={box2Kw} onChange={(e) => setBox2Kw(e.target.value)} />
                      </div>
                      <div>
                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>Search Intent:</label>
                        <select className="input-field" style={{ padding: '6px 8px', fontSize: '11px' }} value={box2Intent} onChange={(e) => setBox2Intent(e.target.value)}>
                          <option value="Commercial">Commercial</option>
                          <option value="Transactional">Transactional</option>
                          <option value="Informational">Informational</option>
                          <option value="Navigational">Navigational</option>
                        </select>
                      </div>
                    </div>

                    <Button
                      variant="primary"
                      size="sm"
                      style={{ marginTop: '4px', background: '#eab308', color: '#000', borderColor: '#facc15', fontSize: '11px', fontWeight: 800 }}
                      icon={<Plus size={14} />}
                      onClick={() => {
                        if (!box2Kw.trim()) return;
                        const item: ToolKeywordItem = {
                          id: `tool-${Date.now()}`,
                          keyword: box2Kw.trim(),
                          sourceTool: box2ToolName || 'SEMrush Keyword Magic Tool',
                          seedKeywordQuery: box2SeedKw.trim() || undefined,
                          targetLocation: box2Location.trim() || undefined,
                          methodType: 'seed_location',
                          searchVolumeStatus: 'not_collected',
                          intent: box2Intent,
                          relevant: true,
                          isVariation: false,
                          decision: 'Target',
                          verificationStatus: 'user_verified',
                        };
                        updateProject({ ...localProject, toolKeywords: [...localProject.toolKeywords, item] });
                        setBox2Kw('');
                      }}
                    >
                      Add Seed Location Keyword (Box 2)
                    </Button>
                  </div>
                </div>

              </div>

              {/* DATASET TABLE */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>SEMRUSH / TOOL KEYWORDS DATASET ({localProject.toolKeywords.length} TERMS)</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Badge variant="tool">Box 1: Competitors</Badge>
                    <Badge variant="lecture">Box 2: Seed Location</Badge>
                  </div>
                </div>
                <div style={{ maxHeight: '360px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', width: '120px' }}>Method / Tool</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Source Details</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Extracted Keyword</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Search Intent</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', width: '50px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localProject.toolKeywords.length > 0 ? (
                        localProject.toolKeywords.map(t => {
                          const isCompetitor = t.methodType === 'competitor_domain' || t.competitorName || t.competitorLink;
                          const cleanCompName = sanitizeUrlOrDomain(t.competitorName);
                          const cleanCompLink = sanitizeUrlOrDomain(t.competitorLink);
                          const hrefLink = cleanCompLink ? (cleanCompLink.startsWith('http') ? cleanCompLink : `https://${cleanCompLink}`) : '';

                          return (
                            <tr key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(15, 23, 42, 0.4)' }}>
                              <td style={{ padding: '8px 12px', fontSize: '11px' }}>
                                <Badge variant={isCompetitor ? 'tool' : 'lecture'}>
                                  {isCompetitor ? '🏢 Competitor Domain' : '📍 Seed Location'}
                                </Badge>
                                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{t.sourceTool}</div>
                              </td>

                              <td style={{ padding: '8px 12px', fontSize: '11px', color: '#cbd5e1' }}>
                                {isCompetitor ? (
                                  <div>
                                    <div style={{ fontWeight: 700, color: '#ffffff' }}>{cleanCompName || 'Competitor Domain'}</div>
                                    {cleanCompLink && (
                                      <a href={hrefLink} target="_blank" rel="noreferrer" style={{ color: '#38bdf8', fontSize: '10px' }}>
                                        {cleanCompLink} <ExternalLink size={10} />
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <div style={{ fontWeight: 700, color: '#facc15' }}>Seed: "{t.seedKeywordQuery || localProject.seedKeywords[0]?.seedKeyword || 'Seed'}"</div>
                                    <div style={{ fontSize: '10px', color: '#38bdf8' }}>Location: {t.targetLocation || localProject.businessProfile.primaryLocation || 'Target Region'}</div>
                                  </div>
                                )}
                              </td>

                              <td style={{ padding: '8px 12px', fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>{t.keyword}</td>
                              
                              <td style={{ padding: '8px 12px', fontSize: '12px' }}>
                                <Badge variant="tool">{t.intent}</Badge>
                              </td>

                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <button
                                  onClick={() => {
                                    const updated = localProject.toolKeywords.filter(x => x.id !== t.id);
                                    updateProject({ ...localProject, toolKeywords: updated });
                                  }}
                                  style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                            No tool keywords recorded yet. Use Box 1 (Competitor Domain) or Box 2 (Seed Location Research) above!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* STEPS 1–6 ACCUMULATED KEYWORDS AUDIT SUMMARY CARD */}
              {(() => {
                const all: { keyword: string; stepName: string; originalStep: string; intent?: string }[] = [];

                // Step 2: Seed Keywords
                localProject.seedKeywords.forEach(s => {
                  if (s.seedKeyword && s.seedKeyword.trim()) {
                    all.push({ keyword: s.seedKeyword.trim(), stepName: 'Step 2: Seed Keywords', originalStep: 'Step 2', intent: 'Commercial' });
                  }
                });

                // Step 3: Variations
                localProject.variations.forEach(v => {
                  if (v.keyword && v.keyword.trim()) {
                    all.push({ keyword: v.keyword.trim(), stepName: 'Step 3: Keyword Variations', originalStep: 'Step 3', intent: v.intent });
                  }
                });

                // Step 4: Client Website Content
                localProject.websiteContentKeywords.forEach(w => {
                  if (w.keyword && w.keyword.trim()) {
                    all.push({ keyword: w.keyword.trim(), stepName: `Step 4: Client Website (${w.sourcePage || 'Page'})`, originalStep: 'Step 4', intent: w.intent });
                  }
                });

                // Step 5: SERP & Competitor Webpages
                localProject.competitorKeywords.forEach(c => {
                  if (c.keyword && c.keyword.trim()) {
                    const stepLabel = c.service.startsWith('SERP') || ['Google SERP', 'People Also Ask', 'Related Searches'].includes(c.competitorName)
                      ? 'Step 5: Discovered SERP Keyword'
                      : `Step 5: Competitor Webpage (${c.competitorName})`;
                    all.push({ keyword: c.keyword.trim(), stepName: stepLabel, originalStep: 'Step 5', intent: c.searchIntent });
                  }
                });

                // Step 6: SEMrush / Tool Keywords
                localProject.toolKeywords.forEach(t => {
                  if (t.keyword && t.keyword.trim()) {
                    const compLabel = t.competitorName ? ` (${t.competitorName})` : '';
                    all.push({ keyword: t.keyword.trim(), stepName: `Step 6: SEMrush${compLabel}`, originalStep: 'Step 6', intent: t.intent });
                  }
                });

                // Deduplicate case-insensitively
                const seen = new Set<string>();
                const uniqueList: typeof all = [];
                let duplicatesCount = 0;

                all.forEach(item => {
                  const key = item.keyword.toLowerCase();
                  if (!seen.has(key)) {
                    seen.add(key);
                    uniqueList.push(item);
                  } else {
                    duplicatesCount++;
                  }
                });

                return (
                  <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px', marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle2 size={18} color="var(--accent-green)" />
                        <span>ALL ACCUMULATED KEYWORDS GATHERED SO FAR (STEPS 1–6 PROGRESS SUMMARY)</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Badge variant="complete">Total Unique Keywords: {uniqueList.length}</Badge>
                        {duplicatesCount > 0 && (
                          <Badge variant="tool">⚡ {duplicatesCount} Repetitive Keywords Filtered Out</Badge>
                        )}
                      </div>
                    </div>

                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', margin: 0 }}>
                      Complete consolidated dataset of unique keywords gathered across Seed Keywords (Step 2), Keyword Variations (Step 3), On-Page Client Content (Step 4), Discovered SERP/Competitor Webpages (Step 5), and SEMrush Competitor Research (Step 6).
                    </p>

                    <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', marginTop: '10px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#020617', color: 'var(--accent-green)', fontSize: '11px', textTransform: 'uppercase' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>#</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Origin / Source Step</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Keyword Phrase</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Search Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uniqueList.length > 0 ? (
                            uniqueList.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'rgba(15,23,42,0.6)' : 'rgba(15,23,42,0.3)' }}>
                                <td style={{ padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>{idx + 1}</td>
                                <td style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-blue)' }}>{item.stepName}</td>
                                <td style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 700, color: '#ffffff' }}>{item.keyword}</td>
                                <td style={{ padding: '6px 12px', fontSize: '11px' }}>
                                  <Badge variant="tool">{item.intent || 'Commercial'}</Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                                No keywords accumulated yet across Steps 1–6. Complete the steps above to populate the master dataset!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 7: METHOD 5 — GOOGLE AUTOCOMPLETE */}
          {activeStep === 6 && (
            <KeywordStepGuide
              stepNumber={7}
              stepTitle="Method 5 — Google Autocomplete & Alphabet Soup Technique"
              whyAreWeDoingThis="Instructor Teaching (Lecture 5): Type seed keywords into Google Search box followed by letters (a, b, c...) to capture real user auto-predictions."
              whatYouNeedBeforeStarting={['Google Search open in browser incognito mode']}
              exactlyWhatToDo={[
                'Type your core seed keyword into Google Search bar.',
                'Add letter modifiers: "Keyword a", "Keyword b", "Keyword c"...',
                'Record the autocomplete predictions that represent relevant commercial search terms.',
                'Use the AI Autocomplete Helper to generate predictions automatically.',
                'Optionally mark step as intentionally skipped if your accumulated keywords milestone is reached.'
              ]}
              whatToLookFor={['Real-time Google search suggestions', 'Location autocompletes', 'Product model autocompletes']}
              whatToRecord={['Seed Keyword', 'Letter Modifier', 'Autocomplete Term']}
              lectureExample={{
                scenario: 'Instructor types "truck parts a" into Google search.',
                action: 'Google suggests "truck parts accessories", "truck parts auto zone".',
                result: 'Records relevant terms into Autocomplete Dataset.'
              }}
              doneWhen="At least 1 Autocomplete Term is recorded or step is intentionally skipped."
              isStepValidated={localProject.autocompleteKeywords.length > 0 || step7Skipped}
              validationMessage={step7Skipped ? '✓ Step intentionally skipped.' : 'Record at least 1 autocomplete term below or mark step as skipped.'}
              aiPromptHelper={{
                promptTitle: 'AI Google Autocomplete Prediction Generator',
                promptText: generateAiPromptStep7(),
                onImportCandidates: handleImportStep7AiCandidates,
              }}
              onPrevious={() => handleStepChange(5)}
              onContinue={() => handleStepChange(7)}
            >
              {/* STEP 7 ACCUMULATED MILESTONE & INTENTIONAL SKIP BANNER */}
              {(() => {
                const accumulated = getAccumulatedKeywords(6);
                const count = accumulated.uniqueList.length;

                return (
                  <div style={{ background: count >= 200 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(15, 23, 42, 0.8)', border: count >= 200 ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: count >= 200 ? 'var(--accent-green)' : '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Zap size={16} color={count >= 200 ? 'var(--accent-green)' : '#38bdf8'} />
                        <span>ACCUMULATED KEYWORDS: {count} UNIQUE KEYWORDS COLLECTED (STEPS 1–6)</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {count >= 200
                          ? `⚡ Excellent! You already have ${count} unique keywords collected across Steps 1–6 (200+ milestone reached). You can intentionally skip this step if desired.`
                          : `You have ${count} unique keywords collected so far. You can record Google Autocomplete predictions or mark this step as intentionally skipped.`
                        }
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: '#020617', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                      <input
                        type="checkbox"
                        checked={step7Skipped}
                        onChange={(e) => setStep7Skipped(e.target.checked)}
                        style={{ accentColor: 'var(--accent-green)', width: '16px', height: '16px' }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 700, color: step7Skipped ? 'var(--accent-green)' : '#ffffff' }}>
                        {step7Skipped ? '✓ Intentionally Skipped Step 7' : 'Mark Step 7 as Intentionally Skipped'}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {/* SUGGESTED TARGET TERMS FOR GOOGLE AUTOCOMPLETE (UP TO 8 KEYWORDS FROM PREVIOUS STEPS) */}
              {(() => {
                const seedTerms = localProject.seedKeywords.map(s => s.seedKeyword.trim());
                const varTerms = localProject.variations.map(v => v.keyword.trim());
                const webTerms = localProject.websiteContentKeywords.map(w => w.keyword.trim());
                const serpTerms = localProject.competitorKeywords.map(c => c.keyword.trim());

                const combined = Array.from(new Set([...seedTerms, ...varTerms, ...webTerms, ...serpTerms])).filter(Boolean);
                const top8Suggested = combined.slice(0, 8);
                const checkedCount = top8Suggested.filter(t => checkedToolTerms.includes(t)).length;

                return (
                  <div style={{ background: '#020617', padding: '14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--accent-blue)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent-blue)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Search size={14} color="var(--accent-blue)" />
                        <span>SUGGESTED TARGET SEARCH TERMS FOR AUTOCOMPLETE ({top8Suggested.length} OF 8 TOP TERMS FROM PREVIOUS STEPS)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: checkedCount === top8Suggested.length && top8Suggested.length > 0 ? '#34d399' : '#94a3b8', background: 'rgba(15, 23, 42, 0.8)', padding: '3px 10px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {checkedCount === top8Suggested.length && top8Suggested.length > 0 ? '✓ All Terms Copied!' : `Copied: ${checkedCount} / ${top8Suggested.length}`}
                        </div>
                        <Badge variant="tool">Steps 2, 3, 4 & 5</Badge>
                      </div>
                    </div>

                    {top8Suggested.length > 0 ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '10px' }}>
                        {top8Suggested.map((kw, idx) => {
                          const isChecked = checkedToolTerms.includes(kw);

                          const handleCopyTerm = () => {
                            navigator.clipboard.writeText(kw);
                            setSingleKw(kw);
                            toggleCheckedToolTerm(kw);
                          };

                          return (
                            <div
                              key={idx}
                              onClick={handleCopyTerm}
                              style={{
                                background: isChecked ? 'rgba(16, 185, 129, 0.08)' : 'rgba(15, 23, 42, 0.6)',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {/* Left Accent Indicator Bar */}
                              {isChecked && (
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#34d399' }} />
                              )}

                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1, paddingLeft: isChecked ? '4px' : 0 }}>
                                {/* Circular Checkmark Badge */}
                                <div
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: isChecked ? '#10b981' : 'transparent',
                                    border: isChecked ? '1px solid #34d399' : '1.5px solid rgba(255,255,255,0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  {isChecked && <Check size={11} color="#ffffff" strokeWidth={3} />}
                                </div>

                                <span
                                  style={{
                                    fontSize: '12.5px',
                                    fontWeight: 700,
                                    color: isChecked ? '#34d399' : '#ffffff',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={`${idx + 1}. "${kw}"`}
                                >
                                  {idx + 1}. "{kw}"
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyTerm();
                                }}
                                style={{
                                  fontSize: '11px',
                                  color: isChecked ? '#34d399' : '#38bdf8',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: isChecked ? 'rgba(52, 211, 153, 0.15)' : 'rgba(56, 189, 248, 0.1)',
                                  padding: '5px 10px',
                                  borderRadius: '5px',
                                  border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(56, 189, 248, 0.3)',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                }}
                              >
                                {isChecked ? <Check size={12} color="#34d399" /> : <Copy size={12} />}
                                <span>{isChecked ? 'Copied' : 'Copy'}</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No terms collected in previous steps yet. Complete Steps 2–5 to auto-generate 8 search term suggestions!
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '10px' }}>
                  ➕ ADD GOOGLE AUTOCOMPLETE TERM (ALPHABET SOUP)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '10px' }}>
                  <select className="input-field" value={singleAutoLetter} onChange={(e) => setSingleAutoLetter(e.target.value)}>
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(l => (
                      <option key={l} value={l}>Letter {l}</option>
                    ))}
                  </select>
                  <input type="text" className="input-field" placeholder="Google Autocomplete suggestion phrase..." value={singleKw} onChange={(e) => setSingleKw(e.target.value)} />
                  <input type="number" className="input-field" placeholder="Search Vol (optional)..." value={singleToolVol} onChange={(e) => setSingleToolVol(e.target.value)} />
                  <Button variant="primary" icon={<Plus size={14} />} onClick={() => {
                    if (!singleKw.trim()) return;
                    updateProject({ ...localProject, autocompleteKeywords: [...localProject.autocompleteKeywords, singleKw.trim()] });
                    setSingleKw('');
                  }}>
                    Add Autocomplete Term
                  </Button>
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '12px' }}>
                  GOOGLE AUTOCOMPLETE DATASET ({localProject.autocompleteKeywords.length})
                </div>
                <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                    {localProject.autocompleteKeywords.length > 0 ? (
                      localProject.autocompleteKeywords.map((kw, i) => (
                        <div key={i} style={{ background: '#020617', padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>🔍 "{kw}"</span>
                          <button
                            onClick={() => {
                              const updated = localProject.autocompleteKeywords.filter((_, idx) => idx !== i);
                              updateProject({ ...localProject, autocompleteKeywords: updated });
                            }}
                            style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px 0' }}>
                        No autocomplete terms recorded yet. Enter terms above or use the AI Autocomplete Prediction Generator!
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* STEPS 1–7 ACCUMULATED KEYWORDS AUDIT SUMMARY CARD */}
              {(() => {
                const accumulated = getAccumulatedKeywords(7);
                const { uniqueList, duplicatesCount } = accumulated;

                return (
                  <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px', marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle2 size={18} color="var(--accent-green)" />
                        <span>ALL ACCUMULATED KEYWORDS GATHERED SO FAR (STEPS 1–7 PROGRESS SUMMARY)</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Badge variant="complete">Total Unique Keywords: {uniqueList.length}</Badge>
                        {duplicatesCount > 0 && (
                          <Badge variant="tool">⚡ {duplicatesCount} Repetitive Keywords Filtered Out</Badge>
                        )}
                      </div>
                    </div>

                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', margin: 0 }}>
                      Complete consolidated dataset of unique keywords gathered across Steps 1–7 (Seed, Variations, On-Page Content, SERP, SEMrush, and Autocomplete).
                    </p>

                    <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', marginTop: '10px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#020617', color: 'var(--accent-green)', fontSize: '11px', textTransform: 'uppercase' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>#</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Origin / Source Step</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Keyword Phrase</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Search Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uniqueList.length > 0 ? (
                            uniqueList.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'rgba(15,23,42,0.6)' : 'rgba(15,23,42,0.3)' }}>
                                <td style={{ padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>{idx + 1}</td>
                                <td style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-blue)' }}>{item.stepName}</td>
                                <td style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 700, color: '#ffffff' }}>{item.keyword}</td>
                                <td style={{ padding: '6px 12px', fontSize: '11px' }}>
                                  <Badge variant="tool">{item.intent || 'Commercial'}</Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                                No keywords accumulated yet across Steps 1–7. Complete the steps above to populate the master dataset!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 8: METHOD 6 — GOOGLE RELATED SEARCHES & TOPICAL TERMS */}
          {activeStep === 7 && (
            <KeywordStepGuide
              stepNumber={8}
              stepTitle="Method 6 — Google Related Searches & People Also Ask Terms"
              whyAreWeDoingThis="Instructor Teaching (Lecture 5 & 7): Scroll to bottom of Google SERP for 'Searches related to' and extract 'People Also Ask' questions to cover topical entity breadth."
              whatYouNeedBeforeStarting={['Google SERP bottom section open']}
              exactlyWhatToDo={[
                'Perform Google search for core seed terms.',
                'Scroll to bottom of SERP and inspect "Searches related to...".',
                'Inspect "People Also Ask" dropdown questions.',
                'Record related terms and topical entity phrases below.',
                'Optionally mark step as intentionally skipped if your accumulated keywords milestone is reached.'
              ]}
              whatToLookFor={['Bottom of SERP related terms', 'People Also Ask questions', 'LSI topical phrases']}
              whatToRecord={['Related Keyword Phrase', 'SERP Location / Source']}
              lectureExample={{
                scenario: 'Instructor searches "Heavy duty truck parts".',
                action: 'Extracts related term "Commercial vehicle spare parts supplier Trinidad".',
                result: 'Records term under Related Searches.'
              }}
              doneWhen="At least 1 Related / Topical Term is recorded or step is intentionally skipped."
              isStepValidated={localProject.variations.some(v => v.reason?.includes('Google SERP')) || step8Skipped || true}
              validationMessage={step8Skipped ? '✓ Step intentionally skipped.' : 'Record at least 1 related term below or mark step as skipped.'}
              aiPromptHelper={{
                promptTitle: 'AI Related Searches & Topical Entity Generator',
                promptText: generateAiPromptStep8(),
                onImportCandidates: handleImportStep8AiCandidates,
              }}
              onPrevious={() => handleStepChange(6)}
              onContinue={() => handleStepChange(8)}
            >
              {/* STEP 8 ACCUMULATED MILESTONE & INTENTIONAL SKIP BANNER */}
              {(() => {
                const accumulated = getAccumulatedKeywords(7);
                const count = accumulated.uniqueList.length;

                return (
                  <div style={{ background: count >= 200 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(15, 23, 42, 0.8)', border: count >= 200 ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: count >= 200 ? 'var(--accent-green)' : '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Zap size={16} color={count >= 200 ? 'var(--accent-green)' : '#38bdf8'} />
                        <span>ACCUMULATED KEYWORDS: {count} UNIQUE KEYWORDS COLLECTED (STEPS 1–7)</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {count >= 200
                          ? `⚡ Excellent! You already have ${count} unique keywords collected across Steps 1–7 (200+ milestone reached). You can intentionally skip this step if desired.`
                          : `You have ${count} unique keywords collected so far. You can record Related Searches / PAA phrases or mark this step as intentionally skipped.`
                        }
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: '#020617', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                      <input
                        type="checkbox"
                        checked={step8Skipped}
                        onChange={(e) => setStep8Skipped(e.target.checked)}
                        style={{ accentColor: 'var(--accent-green)', width: '16px', height: '16px' }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 700, color: step8Skipped ? 'var(--accent-green)' : '#ffffff' }}>
                        {step8Skipped ? '✓ Intentionally Skipped Step 8' : 'Mark Step 8 as Intentionally Skipped'}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {/* SUGGESTED TARGET TERMS FOR TOPICAL ENTITIES (UP TO 8 KEYWORDS FROM PREVIOUS STEPS) */}
              {(() => {
                const seedTerms = localProject.seedKeywords.map(s => s.seedKeyword.trim());
                const varTerms = localProject.variations.map(v => v.keyword.trim());
                const webTerms = localProject.websiteContentKeywords.map(w => w.keyword.trim());
                const serpTerms = localProject.competitorKeywords.map(c => c.keyword.trim());

                const combined = Array.from(new Set([...seedTerms, ...varTerms, ...webTerms, ...serpTerms])).filter(Boolean);
                const top8Suggested = combined.slice(0, 8);
                const checkedCount = top8Suggested.filter(t => checkedToolTerms.includes(t)).length;

                return (
                  <div style={{ background: '#020617', padding: '14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--accent-blue)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent-blue)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Search size={14} color="var(--accent-blue)" />
                        <span>SUGGESTED TARGET SEARCH TERMS FOR TOPICAL ENTITIES ({top8Suggested.length} OF 8 TOP TERMS FROM PREVIOUS STEPS)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: checkedCount === top8Suggested.length && top8Suggested.length > 0 ? '#34d399' : '#94a3b8', background: 'rgba(15, 23, 42, 0.8)', padding: '3px 10px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {checkedCount === top8Suggested.length && top8Suggested.length > 0 ? '✓ All Terms Copied!' : `Copied: ${checkedCount} / ${top8Suggested.length}`}
                        </div>
                        <Badge variant="tool">Steps 2, 3, 4 & 5</Badge>
                      </div>
                    </div>

                    {top8Suggested.length > 0 ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '10px' }}>
                        {top8Suggested.map((kw, idx) => {
                          const isChecked = checkedToolTerms.includes(kw);

                          const handleCopyTerm = () => {
                            navigator.clipboard.writeText(kw);
                            setSingleKw(kw);
                            toggleCheckedToolTerm(kw);
                          };

                          return (
                            <div
                              key={idx}
                              onClick={handleCopyTerm}
                              style={{
                                background: isChecked ? 'rgba(16, 185, 129, 0.08)' : 'rgba(15, 23, 42, 0.6)',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {/* Left Accent Indicator Bar */}
                              {isChecked && (
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#34d399' }} />
                              )}

                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1, paddingLeft: isChecked ? '4px' : 0 }}>
                                {/* Circular Checkmark Badge */}
                                <div
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: isChecked ? '#10b981' : 'transparent',
                                    border: isChecked ? '1px solid #34d399' : '1.5px solid rgba(255,255,255,0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  {isChecked && <Check size={11} color="#ffffff" strokeWidth={3} />}
                                </div>

                                <span
                                  style={{
                                    fontSize: '12.5px',
                                    fontWeight: 700,
                                    color: isChecked ? '#34d399' : '#ffffff',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={`${idx + 1}. "${kw}"`}
                                >
                                  {idx + 1}. "{kw}"
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyTerm();
                                }}
                                style={{
                                  fontSize: '11px',
                                  color: isChecked ? '#34d399' : '#38bdf8',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: isChecked ? 'rgba(52, 211, 153, 0.15)' : 'rgba(56, 189, 248, 0.1)',
                                  padding: '5px 10px',
                                  borderRadius: '5px',
                                  border: isChecked ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(56, 189, 248, 0.3)',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                }}
                              >
                                {isChecked ? <Check size={12} color="#34d399" /> : <Copy size={12} />}
                                <span>{isChecked ? 'Copied' : 'Copy'}</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No terms collected in previous steps yet. Complete Steps 2–5 to auto-generate 8 search term suggestions!
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '10px' }}>
                  ➕ ADD RELATED SEARCH / TOPICAL ENTITY TERM
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2.5fr auto', gap: '10px' }}>
                  <select className="input-field" value={singleRelatedSource} onChange={(e) => setSingleRelatedSource(e.target.value)}>
                    <option value="Bottom of SERP">Bottom of SERP (Related Searches)</option>
                    <option value="People Also Ask">People Also Ask (PAA)</option>
                    <option value="Topical Entity">Topical Entity / LSI Phrase</option>
                  </select>
                  <input type="text" className="input-field" placeholder="Related phrase e.g. Commercial vehicle parts Trinidad..." value={singleKw} onChange={(e) => setSingleKw(e.target.value)} />
                  <Button variant="primary" icon={<Plus size={14} />} onClick={() => {
                    if (!singleKw.trim()) return;
                    const item: KeywordVariationItem = {
                      id: `rel-${Date.now()}`,
                      keyword: singleKw.trim(),
                      variationOf: localProject.seedKeywords[0]?.seedKeyword || 'Core Service',
                      searchVolumeStatus: 'not_collected',
                      intent: 'Commercial',
                      serpSimilarity: 'High',
                      separateTarget: false,
                      reason: `Extracted from Google SERP: ${singleRelatedSource}`,
                      verificationStatus: 'user_verified',
                    };
                    updateProject({ ...localProject, variations: [...localProject.variations, item] });
                    setSingleKw('');
                  }}>
                    Add Related Term
                  </Button>
                </div>
              </div>

              {/* STEPS 1–8 ACCUMULATED KEYWORDS AUDIT SUMMARY CARD */}
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList, duplicatesCount } = accumulated;

                return (
                  <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px', marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle2 size={18} color="var(--accent-green)" />
                        <span>ALL ACCUMULATED KEYWORDS GATHERED SO FAR (STEPS 1–8 PROGRESS SUMMARY)</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Badge variant="complete">Total Unique Keywords: {uniqueList.length}</Badge>
                        {duplicatesCount > 0 && (
                          <Badge variant="tool">⚡ {duplicatesCount} Repetitive Keywords Filtered Out</Badge>
                        )}
                      </div>
                    </div>

                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', margin: 0 }}>
                      Complete consolidated dataset of unique keywords gathered across Steps 1–8 (Seed, Variations, On-Page Content, SERP, SEMrush, Autocomplete, and Related/PAA).
                    </p>

                    <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', marginTop: '10px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#020617', color: 'var(--accent-green)', fontSize: '11px', textTransform: 'uppercase' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>#</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Origin / Source Step</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Keyword Phrase</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Search Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uniqueList.length > 0 ? (
                            uniqueList.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'rgba(15,23,42,0.6)' : 'rgba(15,23,42,0.3)' }}>
                                <td style={{ padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>{idx + 1}</td>
                                <td style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-blue)' }}>{item.stepName}</td>
                                <td style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 700, color: '#ffffff' }}>{item.keyword}</td>
                                <td style={{ padding: '6px 12px', fontSize: '11px' }}>
                                  <Badge variant="tool">{item.intent || 'Commercial'}</Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                                No keywords accumulated yet across Steps 1–8. Complete the steps above to populate the master dataset!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 9: MASTER ACCUMULATED KEYWORDS & KEYWORDS EVERYWHERE 3-KEYWORD BATCH WORKSTATION */}
          {activeStep === 8 && (
            <KeywordStepGuide
              stepNumber={9}
              stepTitle="Search Volume Collection & Keywords Everywhere Batch Importer"
              whyAreWeDoingThis="Instructor Rule (Lecture 5): Gather search volumes for ALL collected unique terms using Keywords Everywhere / SEO tools. Copy 3 terms at a time, paste results back to auto-fill search volumes!"
              whatYouNeedBeforeStarting={['Keywords Everywhere Chrome Extension or Keyword Tool open in browser']}
              exactlyWhatToDo={[
                'Copy 3 keywords at a time using the "Copy This Batch (3 Terms)" or "Copy 3 & Advance Next ➔" button.',
                'Paste the 3 keywords into Google Search bar or Keywords Everywhere bulk tool.',
                'Copy the resulting data table from Keywords Everywhere (e.g. "Truck Parts Trinidad and Tobago  0  $ 0.00  Low (0%)").',
                'Paste the results into the "Keywords Everywhere Results Importer" box below and click "Parse & Auto-Fill Search Volumes".'
              ]}
              whatToLookFor={['Monthly search volume numbers from Keywords Everywhere', 'Local volume breakdown', 'Zero-volume terms (keep all terms!)']}
              whatToRecord={['Exact Search Volume (or 0 for zero-volume terms)', 'Keywords Everywhere Volume Status']}
              lectureExample={{
                scenario: 'User copies 3 terms: "Truck Parts Trinidad and Tobago", "Truck Parts in Trinidad and Tobago", "Trinidad and Tobago Truck Parts".',
                action: 'Pastes into Keywords Everywhere, receives tab-separated output with volumes (0, 0, 0).',
                result: 'Pastes into importer box -> Auto-fills search volumes as 0 and marks status as Collected!'
              }}
              doneWhen="Search Volume column is populated and verified for all unique keywords."
              isStepValidated={true}
              aiPromptHelper={{
                promptTitle: 'AI Geo-Targeting, Variation Expansion & Competitor Cleaning Prompt',
                promptText: generateAiGeoTargetingPrompt(),
                onImportCandidates: (pastedText) => {
                  setStep9AiPastedResponse(pastedText);
                  return handleImportStep9AiGeoResponse(pastedText);
                },
              }}
              onPrevious={() => handleStepChange(7)}
              onContinue={() => handleStepChange(9)}
            >
              {(() => {
                const rawList = getRawAccumulatedKeywords();
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList, duplicatesCount } = accumulated;

                const totalBatches = Math.max(1, Math.ceil(uniqueList.length / 3));

                // Current batch calculation (Batch size 3)
                const validBatchIndex = Math.min(copyBatchIndex, totalBatches - 1);
                const batchStart = validBatchIndex * 3;
                const currentBatchItems = uniqueList.slice(batchStart, batchStart + 3);
                const currentBatchText = currentBatchItems.map(item => item.keyword).join('\n');

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* STEP 9 AI GEO-TARGETING & COMPETITOR CLEANING DRAWER / MODAL */}
                    {showStep9GeoModal && (
                      <div style={{ background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(2, 6, 23, 0.98) 100%)', border: '1.5px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: '8px', boxShadow: '0 8px 32px rgba(56, 189, 248, 0.15)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                          <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Wrench size={18} color="var(--accent-blue)" />
                            <span>✨ AI GEO-TARGETING, VARIATION EXPANSION & COMPETITOR CLEANING PROMPT GENERATOR</span>
                          </div>
                          <Button size="sm" variant="secondary" onClick={() => setShowStep9GeoModal(false)}>
                            ✕ Close Helper
                          </Button>
                        </div>

                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', margin: 0 }}>
                          Generate a customized AI prompt containing all <strong>{uniqueList.length} accumulated unique keywords</strong> and source info to geo-target generic keywords (e.g. <em>"Holiday Lighting"</em> → <em>"Holiday Lighting in Scottsdale, AZ"</em>), clean competitor brands (e.g. <em>"About Sunbros Cleaning"</em>), and filter excluded terms (e.g. <em>"Old Town Scottsdale"</em>).
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginBottom: '16px', background: 'rgba(15, 23, 42, 0.6)', padding: '14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '4px' }}>
                              📍 Target Location(s):
                            </label>
                            <input
                              type="text"
                              className="input-field"
                              value={step9GeoTargetLoc}
                              onChange={(e) => setStep9GeoTargetLoc(e.target.value)}
                              placeholder="e.g. Scottsdale, AZ; Phoenix, AZ"
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', display: 'block', marginBottom: '4px' }}>
                              🚫 Competitor Brands to Remove:
                            </label>
                            <input
                              type="text"
                              className="input-field"
                              value={step9ExcludeCompetitors}
                              onChange={(e) => setStep9ExcludeCompetitors(e.target.value)}
                              placeholder="e.g. Sunbros Cleaning, Sunbros"
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 700, color: '#34d399', display: 'block', marginBottom: '4px' }}>
                              🏡 Target Neighborhoods (Combined with Services):
                            </label>
                            <input
                              type="text"
                              className="input-field"
                              value={step9TargetNeighborhoods}
                              onChange={(e) => setStep9TargetNeighborhoods(e.target.value)}
                              placeholder="e.g. Old Town Scottsdale, North Scottsdale"
                            />
                          </div>
                        </div>

                        {/* Generated Prompt Output Box */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent-blue)' }}>
                              📋 Generated AI Prompt (Ready to Copy to ChatGPT / Gemini / Claude):
                            </span>
                            <Button
                              size="sm"
                              variant={step9PromptCopied ? 'success' : 'primary'}
                              icon={step9PromptCopied ? <Check size={13} /> : <Copy size={13} />}
                              onClick={() => {
                                const promptText = generateAiGeoTargetingPrompt();
                                navigator.clipboard.writeText(promptText);
                                setStep9PromptCopied(true);
                                setTimeout(() => setStep9PromptCopied(false), 3000);
                              }}
                            >
                              {step9PromptCopied ? '✓ Prompt Copied to Clipboard!' : 'Copy AI Prompt to Clipboard'}
                            </Button>
                          </div>

                          <textarea
                            readOnly
                            rows={6}
                            className="input-field"
                            style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', background: '#020617', color: '#94a3b8', resize: 'vertical' }}
                            value={generateAiGeoTargetingPrompt()}
                          />
                        </div>

                        {/* Auto-Fill Response Importer Box */}
                        <div style={{ background: 'rgba(2, 6, 23, 0.8)', padding: '14px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '6px' }}>
                            📥 PASTE AI RESPONSE & AUTO-FILL MASTER KEYWORDS
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                            Paste the AI output (JSON array or line-by-line list) below. Our portal will automatically parse the response, add new geo-targeted variations, remove competitor brands, and update your master dataset!
                          </div>

                          <textarea
                            rows={4}
                            className="input-field"
                            placeholder="Paste AI response here (e.g. JSON array or list of clean geo-targeted terms)..."
                            value={step9AiPastedResponse}
                            onChange={(e) => setStep9AiPastedResponse(e.target.value)}
                            style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', marginBottom: '10px' }}
                          />

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <Button
                              variant="primary"
                              icon={<Sparkles size={14} />}
                              onClick={() => handleImportStep9AiGeoResponse()}
                            >
                              ⚡ Parse AI Response & Auto-Fill Master Keywords
                            </Button>

                            {step9AiImportStatus && (
                              <div style={{ fontSize: '12px', fontWeight: 700, color: step9AiImportStatus.startsWith('✓') ? '#34d399' : '#f87171' }}>
                                {step9AiImportStatus}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SECTION 1: MASTER KEYWORD REVIEW & EDITING (STEPS 1-8 ACCUMULATED UNIQUE LIST) */}
                    <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CheckCircle2 size={16} color="var(--accent-blue)" />
                          <span>📌 SECTION 1: MASTER KEYWORD REVIEW & EDITING ({uniqueList.length} ACCUMULATED UNIQUE TERMS)</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button
                            variant="primary"
                            size="sm"
                            icon={<Wrench size={14} color="#ffffff" />}
                            onClick={() => setShowStep9GeoModal(!showStep9GeoModal)}
                          >
                            ✨ AI Geo-Targeting & Competitor Cleaning Prompt
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<Sparkles size={14} color="var(--accent-green)" />}
                            onClick={handleDeduplicateStep9Keywords}
                            title="Scan and auto-delete any duplicate keywords created during manual editing"
                          >
                            ⚡ Clean Duplicate Keywords
                          </Button>
                          <Badge variant="tool">Raw Gathered: {rawList.length}</Badge>
                          <Badge variant="complete">Accumulated Unique: {uniqueList.length}</Badge>
                          {duplicatesCount > 0 && (
                            <Badge variant="lecture">⚡ {duplicatesCount} Repetitive Terms Excluded</Badge>
                          )}
                        </div>
                      </div>

                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px', marginTop: 0 }}>
                        Review and manually edit any keyword phrase below. Any edits or deletions made here automatically update Section 2 below!
                      </p>

                      <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', width: '45px' }}>#</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', width: '180px' }}>Source Step</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Keyword Phrase (Click to Edit)</th>
                              <th style={{ padding: '8px 12px', textAlign: 'center', width: '70px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uniqueList.length > 0 ? (
                              uniqueList.map((item, idx) => (
                                <tr
                                  key={`sec1-row-${idx}-${item.keyword}`}
                                  style={{
                                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                                    background: idx % 2 === 0 ? 'rgba(15, 23, 42, 0.6)' : 'rgba(15, 23, 42, 0.3)',
                                  }}
                                >
                                  <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                    {idx + 1}
                                  </td>
                                  <td style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-blue)' }}>
                                    {item.stepName}
                                  </td>
                                  <td style={{ padding: '6px 12px' }}>
                                    <input
                                      key={`sec1-kw-in-${idx}-${item.keyword}`}
                                      type="text"
                                      className="input-field"
                                      style={{
                                        width: '100%',
                                        minWidth: '220px',
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        color: '#ffffff',
                                        background: 'rgba(15, 23, 42, 0.8)',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                      }}
                                      defaultValue={item.keyword}
                                      onBlur={(e) => {
                                        const val = e.target.value.trim();
                                        if (val && val.toLowerCase() !== item.keyword.toLowerCase()) {
                                          handleEditKeywordPhrase(item.keyword, val);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          (e.target as HTMLInputElement).blur();
                                        }
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      title={`Delete keyword "${item.keyword}"`}
                                      onClick={() => handleDeleteKeyword(item.keyword)}
                                      style={{
                                        background: 'rgba(239, 68, 68, 0.15)',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        color: 'var(--accent-red)',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>
                                  No keywords accumulated yet across Steps 1–8.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* GLOWING CONNECTOR DIVIDER */}
                    <div style={{ textAlign: 'center', padding: '6px 0', background: 'rgba(56, 189, 248, 0.08)', borderRadius: 'var(--radius-sm)', border: '1px dashed rgba(56, 189, 248, 0.3)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        ⬇ SECTION 2 SHOWS FILTERED & DEDUPLICATED KEYWORDS FOR SEARCH VOLUME COLLECTION ({uniqueList.length} UNIQUE TERMS) ⬇
                      </span>
                    </div>

                    {/* SECTION 2: FINALIZED UNIQUE KEYWORDS & SEARCH VOLUME WORKSTATION */}
                    <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                      
                      {/* BATCH COPY & KEYWORDS EVERYWHERE IMPORTER PANEL */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        
                        {/* LEFT: 3-KEYWORD BATCH COPY CONTROLLER */}
                        <div style={{ background: '#020617', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-blue)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Copy size={16} color="var(--accent-blue)" />
                              <span>📋 1. COPY 3 KEYWORDS (FOR KEYWORDS EVERYWHERE)</span>
                            </div>
                            <Badge variant="tool">Batch {validBatchIndex + 1} of {totalBatches}</Badge>
                          </div>

                          <label style={{ fontSize: '11px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '6px' }}>
                            Batch {validBatchIndex + 1} (Terms {batchStart + 1}–{batchStart + currentBatchItems.length} of {uniqueList.length}):
                          </label>
                          <textarea
                            readOnly
                            rows={3}
                            className="input-field"
                            style={{ fontFamily: 'monospace', fontSize: '12px', width: '100%', background: 'rgba(15, 23, 42, 0.9)', color: 'var(--accent-green)', fontWeight: 700, resize: 'none', marginBottom: '10px' }}
                            value={currentBatchText || 'No terms in batch'}
                          />

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <Button
                                variant="primary"
                                size="sm"
                                icon={<Copy size={14} />}
                                disabled={currentBatchItems.length === 0}
                                onClick={() => {
                                  if (!currentBatchText) return;
                                  navigator.clipboard.writeText(currentBatchText);
                                  handleMarkBatchCopied(currentBatchItems.map(item => item.keyword));
                                  setCopyBatchStatus(`✓ Copied 3 terms & set default volume 0 (Batch ${validBatchIndex + 1})!`);
                                  setTimeout(() => setCopyBatchStatus(''), 2500);
                                }}
                              >
                                Copy This Batch (3 Terms)
                              </Button>

                              <Button
                                variant="success"
                                size="sm"
                                icon={<Copy size={14} />}
                                disabled={currentBatchItems.length === 0}
                                onClick={() => {
                                  if (!currentBatchText) return;
                                  navigator.clipboard.writeText(currentBatchText);
                                  handleMarkBatchCopied(currentBatchItems.map(item => item.keyword));
                                  setCopyBatchStatus(`✓ Copied Batch ${validBatchIndex + 1} & set default volume 0! Advanced to Batch ${Math.min(validBatchIndex + 2, totalBatches)}.`);
                                  if (validBatchIndex + 1 < totalBatches) {
                                    setCopyBatchIndex(validBatchIndex + 1);
                                  }
                                  setTimeout(() => setCopyBatchStatus(''), 2500);
                                }}
                              >
                                Copy 3 & Advance Next ➔
                              </Button>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={validBatchIndex === 0}
                                onClick={() => setCopyBatchIndex(Math.max(0, validBatchIndex - 1))}
                              >
                                ⬅ Prev 3
                              </Button>
                              <select
                                className="input-field"
                                style={{ padding: '4px 8px', fontSize: '11px', flex: 1 }}
                                value={validBatchIndex}
                                onChange={(e) => setCopyBatchIndex(parseInt(e.target.value, 10))}
                              >
                                {Array.from({ length: totalBatches }).map((_, bIdx) => {
                                  const bStart = bIdx * 3;
                                  const bEnd = Math.min(bStart + 3, uniqueList.length);
                                  return (
                                    <option key={bIdx} value={bIdx}>
                                      Batch {bIdx + 1}: Terms {bStart + 1}–{bEnd}
                                    </option>
                                  );
                                })}
                              </select>
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={validBatchIndex + 1 >= totalBatches}
                                onClick={() => setCopyBatchIndex(Math.min(totalBatches - 1, validBatchIndex + 1))}
                              >
                                Next 3 ➔
                              </Button>
                            </div>

                            {copyBatchStatus && (
                              <div style={{ fontSize: '11px', color: 'var(--accent-green)', fontWeight: 700 }}>
                                {copyBatchStatus}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* RIGHT: KEYWORDS EVERYWHERE RESULTS IMPORTER */}
                        <div style={{ background: '#020617', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-green)', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Sparkles size={16} color="var(--accent-green)" />
                            <span>📥 2. PASTE KEYWORDS EVERYWHERE RESULTS</span>
                          </div>

                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                            Paste copied table output from Keywords Everywhere or SEO tool (tab-separated text):
                          </label>
                          <textarea
                            rows={3}
                            className="input-field"
                            placeholder="e.g. Truck Parts Trinidad and Tobago	0	$ 0.00	Low (0%)"
                            style={{ fontFamily: 'monospace', fontSize: '11px', width: '100%', resize: 'none', marginBottom: '10px' }}
                            value={kePastedText}
                            onChange={(e) => setKePastedText(e.target.value)}
                          />

                          <Button
                            variant="success"
                            size="sm"
                            style={{ width: '100%' }}
                            icon={<CheckCircle2 size={14} />}
                            disabled={!kePastedText.trim()}
                            onClick={() => handleImportKeywordsEverywhereResults(kePastedText)}
                          >
                            ⚡ Parse & Auto-Fill Search Volumes
                          </Button>

                          {keImportNotice && (
                            <div style={{ fontSize: '11px', color: keImportNotice.startsWith('✓') ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 700, marginTop: '8px' }}>
                              {keImportNotice}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* SECTION 2 DEDUPLICATED KEYWORDS & SEARCH VOLUME SHEET TABLE */}
                      <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CheckCircle2 size={16} color="var(--accent-green)" />
                            <span>DEDUPLICATED UNIQUE KEYWORDS & SEARCH VOLUME SHEET ({uniqueList.length} UNIQUE TERMS)</span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Badge variant="complete">
                              Collected: {uniqueList.filter(u => getVolumeForKeyword(u.keyword).status === 'collected').length} / {uniqueList.length}
                            </Badge>
                            {duplicatesCount > 0 && (
                              <Badge variant="lecture">⚡ {duplicatesCount} Repetitive Terms Excluded</Badge>
                            )}
                          </div>
                        </div>

                        <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                              <tr style={{ background: '#020617', color: 'var(--accent-green)', fontSize: '11px', textTransform: 'uppercase' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '45px' }}>#</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '150px' }}>Source Step</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Finalized Unique Keyword</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '130px' }}>Editable Search Volume</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '120px' }}>Volume Status</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', width: '60px' }}>Delete</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '110px' }}>Batch Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uniqueList.length > 0 ? (
                                uniqueList.map((item, idx) => {
                                  const isCurrentBatch = idx >= batchStart && idx < batchStart + 3;
                                  const isChunkStart = idx % 3 === 0;
                                  const volInfo = getVolumeForKeyword(item.keyword);
                                  const isCopiedOrCollected = volInfo.status === 'collected';

                                  return (
                                    <tr
                                      key={`sec2-row-${idx}-${item.keyword}`}
                                      style={{
                                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        background: isCurrentBatch ? 'rgba(56, 189, 248, 0.15)' : (idx % 2 === 0 ? 'rgba(15, 23, 42, 0.6)' : 'rgba(15, 23, 42, 0.3)'),
                                      }}
                                    >
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: isCurrentBatch ? '#38bdf8' : 'var(--text-muted)', fontWeight: isCurrentBatch ? 700 : 400 }}>
                                        {idx + 1}
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-blue)' }}>
                                        {item.stepName}
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 700, color: isCopiedOrCollected ? '#34d399' : '#ffffff' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          {isCopiedOrCollected ? (
                                            <CheckCircle2 size={15} color="#34d399" style={{ flexShrink: 0 }} />
                                          ) : (
                                            <div style={{ width: '15px', height: '15px', borderRadius: '50%', border: '1.5px solid var(--text-dim)', flexShrink: 0 }} />
                                          )}
                                          <span>{item.keyword}</span>
                                        </div>
                                      </td>
                                      <td style={{ padding: '8px 12px' }}>
                                        <input
                                          key={`sec2-vol-in-${idx}-${item.keyword}-${volInfo.status === 'collected' ? volInfo.volume : 'pending'}`}
                                          type="number"
                                          className="input-field"
                                          style={{ width: '120px', padding: '4px 8px', fontSize: '12px', borderColor: isCopiedOrCollected ? 'rgba(52, 211, 153, 0.4)' : undefined }}
                                          placeholder="0"
                                          defaultValue={volInfo.status === 'collected' ? (volInfo.volume ?? 0) : ''}
                                          onBlur={(e) => {
                                            handleUpdateIndividualVolume(item.keyword, e.target.value.trim());
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              (e.target as HTMLInputElement).blur();
                                            }
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '8px 12px' }}>
                                        <Badge variant={volInfo.status === 'collected' ? 'complete' : 'lecture'}>
                                          {volInfo.status === 'collected' ? `✓ Vol: ${volInfo.volume ?? 0}` : 'Pending Volume'}
                                        </Badge>
                                      </td>
                                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                        <button
                                          type="button"
                                          title={`Delete keyword "${item.keyword}"`}
                                          onClick={() => handleDeleteKeyword(item.keyword)}
                                          style={{
                                            background: 'rgba(239, 68, 68, 0.15)',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                            color: 'var(--accent-red)',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </td>
                                      <td style={{ padding: '8px 12px', textAlign: 'left' }}>
                                        {isChunkStart && (() => {
                                          const chunkItems = uniqueList.slice(idx, idx + 3).map(x => x.keyword);
                                          const isChunkCopied = chunkItems.every(k => getVolumeForKeyword(k).status === 'collected');

                                          return (
                                            <button
                                              type="button"
                                              title={`Copy 3 terms (${idx + 1}–${Math.min(idx + 3, uniqueList.length)})`}
                                              onClick={() => {
                                                const chunk = chunkItems.join('\n');
                                                navigator.clipboard.writeText(chunk);
                                                handleMarkBatchCopied(chunkItems);
                                                setCopyBatchIndex(Math.floor(idx / 3));
                                                setCopyBatchStatus(`✓ Copied terms ${idx + 1}–${Math.min(idx + 3, uniqueList.length)}!`);
                                                setTimeout(() => setCopyBatchStatus(''), 2500);
                                              }}
                                              style={{
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                color: isChunkCopied ? '#34d399' : '#ffffff',
                                                background: isChunkCopied ? 'rgba(52, 211, 153, 0.15)' : 'rgba(56, 189, 248, 0.2)',
                                                border: isChunkCopied ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(56, 189, 248, 0.4)',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                              }}
                                            >
                                              {isChunkCopied ? <Check size={12} color="#34d399" /> : <Copy size={12} />}
                                              <span>{isChunkCopied ? 'Copied 3' : 'Copy 3'}</span>
                                            </button>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                                    No keywords accumulated yet across Steps 1–8. Complete earlier steps to populate the master dataset!
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 10: KEYWORD HIERARCHY & SERP VERIFICATION */}
          {activeStep === 9 && (
            <KeywordStepGuide
              stepNumber={10}
              stepTitle="Step 10: SERP Check & Keyword Hierarchy only homepage"
              whyAreWeDoingThis="Build the initial keyword hierarchy from search volume, then manually verify keyword relationships using Google SERPs. You make all decisions — the system only structures and records the process."
              whatYouNeedBeforeStarting={['Step 9 Candidate Keywords & Search Volume Dataset', 'Google Search open in browser']}
              exactlyWhatToDo={[
                'Stage 1: Establish initial primary, secondary, and tertiary hierarchy.',
                'Stage 2: Validate SERP similarity and reclassify variations vs. separate targets.',
                'Stage 3: Review and finalize the finalized master hierarchy.'
              ]}
              whatToLookFor={['Search volume magnitude', 'SERP URL overlap', 'Search intent similarity']}
              whatToRecord={['Primary, Secondary, Tertiary Target Selections', 'SERP Similarity & Intent Observations', 'Promotion Decisions']}
              lectureExample={{
                scenario: 'Instructor evaluates "Truck Parts Trinidad" (720 vol) vs "Truck Parts in Trinidad" (320 vol).',
                action: 'Searches Google for both keywords, observes identical ranking URLs, and promotes 320 volume term as a Variation of Primary Target.',
                result: 'Refills Tertiary position with next highest-volume unique candidate.'
              }}
              doneWhen="All 3 stages are completed and hierarchy is finalized."
              isStepValidated={true}
              onPrevious={() => handleStepChange(8)}
              onContinue={() => handleStepChange(10)}
            >
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList } = accumulated;

                // Mechanically sort by Search Volume (Highest Volume First)
                const sortedUniqueList = [...uniqueList].sort((a, b) => {
                  const volA = getVolumeForKeyword(a.keyword).status === 'collected' ? (getVolumeForKeyword(a.keyword).volume ?? 0) : -1;
                  const volB = getVolumeForKeyword(b.keyword).status === 'collected' ? (getVolumeForKeyword(b.keyword).volume ?? 0) : -1;
                  return volB - volA;
                });

                // Clean any "Variation: " prefix from target strings
                const cleanKw = (kw: string) => (kw || '').replace(/^Variation:\s*/i, '').trim();

                const filteredCandidates = sortedUniqueList.filter(s =>
                  cleanKw(s.keyword).toLowerCase().includes(step10SearchQuery.toLowerCase())
                );

                // Find current Primary, Secondary, Tertiary targets from local selection state or masterKeywords
                const masterPrimary = cleanKw(step10SelectedPrimary || localProject.masterKeywords.find(m => m.priority === 'Primary')?.keyword || sortedUniqueList[0]?.keyword || '');
                const masterSecondary = cleanKw(step10SelectedSecondary || localProject.masterKeywords.find(m => m.priority === 'Secondary')?.keyword || sortedUniqueList.find(s => cleanKw(s.keyword) !== masterPrimary)?.keyword || '');
                const masterTertiary = cleanKw(step10SelectedTertiary || localProject.masterKeywords.find(m => m.priority === 'Tertiary')?.keyword || sortedUniqueList.find(s => cleanKw(s.keyword) !== masterPrimary && cleanKw(s.keyword) !== masterSecondary)?.keyword || '');

                const isStep10AssignedVar = (v: KeywordVariationItem, targetKw: string, tier?: string) => {
                  if (!targetKw) return false;
                  const kwClean = cleanKw(v.keyword).toLowerCase();
                  const targetClean = cleanKw(targetKw).toLowerCase();
                  if (kwClean === targetClean) return false;

                  const matchesParent = cleanKw(v.variationOf || '').toLowerCase() === targetClean;
                  const matchesTier = (v as any).targetTier && (v as any).targetTier === tier;

                  if (!matchesParent && !matchesTier) return false;

                  const isExplicit = (v as any).step10Assigned === true || matchesTier;
                  const isReasonAssigned = v.reason ? (
                    v.reason.includes('Stage') ||
                    v.reason.includes('AI Workstation') ||
                    v.reason.includes('Promoted') ||
                    v.reason.includes('Assigned') ||
                    v.reason.includes('Step 10')
                  ) : false;

                  return isExplicit || isReasonAssigned;
                };

                const primaryVariations = localProject.variations.filter(v => isStep10AssignedVar(v, masterPrimary, 'Primary'));
                const secondaryVariations = localProject.variations.filter(v => isStep10AssignedVar(v, masterSecondary, 'Secondary'));
                const tertiaryVariations = localProject.variations.filter(v => isStep10AssignedVar(v, masterTertiary, 'Tertiary'));

                const stages = [
                  { id: 1, label: '① Initial Hierarchy' },
                  { id: 2, label: '② SERP Validation & Refinement' },
                  { id: 3, label: '③ Finalize' },
                ];

                // Reset helper to clear all set variation assignments across Primary, Secondary, and Tertiary
                const handleResetAllVariations = () => {
                  if (confirm('Are you sure you want to reset all set variations? This will clear all assigned variation relationships across Primary, Secondary, and Tertiary targets so you can assign them manually from scratch.')) {
                    const clearedVars = (localProject.variations || []).map(v => ({
                      ...v,
                      variationOf: '',
                      targetTier: undefined,
                      step10Assigned: false,
                      reason: ''
                    }));
                    updateProject({
                      ...localProject,
                      variations: clearedVars as any
                    });
                  }
                };

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* TITLE & SUBTITLE */}
                    <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Sparkles size={20} color="var(--accent-blue)" />
                        <span>Keyword Hierarchy & SERP Verification</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        Build the initial keyword hierarchy, validate keyword relationships using Google SERPs, and finalize the Primary, Secondary, and Tertiary structure.
                      </div>
                    </div>

                    {/* STAGE SELECTOR TABS */}
                    <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>GUIDED STAGES:</span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        {stages.map(stg => {
                          const isActive = step10Stage === stg.id;
                          return (
                            <button
                              key={stg.id}
                              onClick={() => setStep10Stage(stg.id as any)}
                              style={{
                                padding: '6px 14px',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isActive ? 'var(--accent-blue)' : 'rgba(15, 23, 42, 0.8)',
                                color: isActive ? '#020617' : '#ffffff',
                                border: isActive ? '1px solid #38bdf8' : '1px solid rgba(255, 255, 255, 0.1)',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {stg.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* STAGE 1 — INITIAL HIERARCHY */}
                    {step10Stage === 1 && (
                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px' }}>
                        
                        <div style={{ background: '#020617', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: '20px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-blue)', marginBottom: '6px' }}>
                            📌 WHAT YOU ARE DOING IN STAGE 1: INITIAL HIERARCHY
                          </div>
                          <div style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: 1.5 }}>
                            Establish the initial keyword hierarchy using the search-volume-sorted candidate list from Step 9.
                            Select Primary, Secondary, and Tertiary targets, and attach potential variations under each target.
                          </div>
                        </div>

                        {/* TARGET SELECTION CARDS SIDE-BY-SIDE WITH ATTACHED VARIATIONS */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                          
                          {/* Primary Card */}
                          <div style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid #facc15', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 800, color: '#facc15', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>🥇 PRIMARY TARGET</span>
                              <span style={{ fontSize: '10px', background: 'rgba(234, 179, 8, 0.25)', padding: '2px 6px', borderRadius: '4px' }}>{primaryVariations.length} Variations</span>
                            </div>
                            {masterPrimary ? (
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>{masterPrimary}</div>
                                <div style={{ fontSize: '11px', color: '#facc15', marginTop: '2px', marginBottom: '8px' }}>Vol: {getVolumeForKeyword(masterPrimary).volume ?? 0}/mo</div>
                                
                                <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 'var(--radius-sm)', padding: '8px' }}>
                                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#facc15', marginBottom: '4px' }}>ATTACHED VARIATIONS:</div>
                                  {primaryVariations.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                      {primaryVariations.map(v => (
                                        <span key={v.id} style={{ background: 'rgba(234, 179, 8, 0.2)', border: '1px solid rgba(234, 179, 8, 0.4)', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', color: '#ffffff', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                          {cleanKw(v.keyword)} ({getVolumeForKeyword(v.keyword).volume ?? 0})
                                          <span
                                            style={{ cursor: 'pointer', color: '#facc15', fontWeight: 800, marginLeft: '2px' }}
                                            onClick={() => {
                                              const updatedVars = localProject.variations.filter(x => x.id !== v.id);
                                              updateProject({ ...localProject, variations: updatedVars as any });
                                            }}
                                          >
                                            ✕
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                      No variations attached. Select "Primary: {masterPrimary}" in dropdown below.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                Select a Primary target from the keyword table below.
                              </div>
                            )}
                          </div>

                          {/* Secondary Card */}
                          <div style={{ background: 'rgba(16, 185, 129, 0.12)', border: '1px solid #34d399', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 800, color: '#34d399', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>🥈 SECONDARY TARGET</span>
                              <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.25)', padding: '2px 6px', borderRadius: '4px' }}>{secondaryVariations.length} Variations</span>
                            </div>
                            {masterSecondary ? (
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>{masterSecondary}</div>
                                <div style={{ fontSize: '11px', color: '#34d399', marginTop: '2px', marginBottom: '8px' }}>Vol: {getVolumeForKeyword(masterSecondary).volume ?? 0}/mo</div>
                                
                                <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 'var(--radius-sm)', padding: '8px' }}>
                                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#34d399', marginBottom: '4px' }}>ATTACHED VARIATIONS:</div>
                                  {secondaryVariations.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                      {secondaryVariations.map(v => (
                                        <span key={v.id} style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', color: '#ffffff', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                          {cleanKw(v.keyword)} ({getVolumeForKeyword(v.keyword).volume ?? 0})
                                          <span
                                            style={{ cursor: 'pointer', color: '#34d399', fontWeight: 800, marginLeft: '2px' }}
                                            onClick={() => {
                                              const updatedVars = localProject.variations.filter(x => x.id !== v.id);
                                              updateProject({ ...localProject, variations: updatedVars as any });
                                            }}
                                          >
                                            ✕
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                      No variations attached. Select "Secondary: {masterSecondary}" in dropdown below.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                Select a Secondary target from the keyword table below.
                              </div>
                            )}
                          </div>

                          {/* Tertiary Card */}
                          <div style={{ background: 'rgba(168, 85, 247, 0.12)', border: '1px solid #c084fc', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>🥉 TERTIARY TARGET</span>
                              <span style={{ fontSize: '10px', background: 'rgba(168, 85, 247, 0.25)', padding: '2px 6px', borderRadius: '4px' }}>{tertiaryVariations.length} Variations</span>
                            </div>
                            {masterTertiary ? (
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>{masterTertiary}</div>
                                <div style={{ fontSize: '11px', color: '#c084fc', marginTop: '2px', marginBottom: '8px' }}>Vol: {getVolumeForKeyword(masterTertiary).volume ?? 0}/mo</div>
                                
                                <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 'var(--radius-sm)', padding: '8px' }}>
                                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#c084fc', marginBottom: '4px' }}>ATTACHED VARIATIONS:</div>
                                  {tertiaryVariations.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                      {tertiaryVariations.map(v => (
                                        <span key={v.id} style={{ background: 'rgba(168, 85, 247, 0.2)', border: '1px solid rgba(168, 85, 247, 0.4)', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', color: '#ffffff', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                          {cleanKw(v.keyword)} ({getVolumeForKeyword(v.keyword).volume ?? 0})
                                          <span
                                            style={{ cursor: 'pointer', color: '#c084fc', fontWeight: 800, marginLeft: '2px' }}
                                            onClick={() => {
                                              const updatedVars = localProject.variations.filter(x => x.id !== v.id);
                                              updateProject({ ...localProject, variations: updatedVars as any });
                                            }}
                                          >
                                            ✕
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                      No variations attached. Select "Tertiary: {masterTertiary}" in dropdown below.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                Select a Tertiary target from the keyword table below.
                              </div>
                            )}
                          </div>

                        </div>

                        {/* SORTED CANDIDATE KEYWORDS TABLE WITH LIVE SEARCH BAR */}
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginTop: '16px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                          <span>Candidate Keywords (Sorted by Search Volume — Highest First)</span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#020617', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                              <Search size={13} color="#94a3b8" />
                              <input
                                type="text"
                                placeholder="Search candidate keywords..."
                                style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '12px', width: '180px', outline: 'none' }}
                                value={step10SearchQuery}
                                onChange={(e) => setStep10SearchQuery(e.target.value)}
                              />
                              {step10SearchQuery && (
                                <span
                                  style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '11px', fontWeight: 800 }}
                                  onClick={() => setStep10SearchQuery('')}
                                >
                                  ✕
                                </span>
                              )}
                            </div>

                            <Button
                              variant="secondary"
                              size="sm"
                              icon={<RotateCcw size={14} color="#f87171" />}
                              style={{ border: '1px solid rgba(239, 68, 68, 0.4)', color: '#f87171', background: 'rgba(239, 68, 68, 0.1)' }}
                              onClick={handleResetAllVariations}
                              title="Reset all set variation assignments to 0 so you can assign them manually"
                            >
                              🔄 Reset All Variations
                            </Button>

                            <Badge variant="tool">Showing {filteredCandidates.length} of {sortedUniqueList.length} Terms</Badge>
                          </div>
                        </div>

                        <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', marginBottom: '20px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                              <tr style={{ background: '#020617', color: 'var(--accent-blue)', fontSize: '11px', textTransform: 'uppercase' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '40px' }}>#</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Candidate Keyword</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '120px' }}>Search Volume</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', width: '310px' }}>Assign Target Tier</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', width: '230px' }}>Potential Variation Of</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredCandidates.length > 0 ? (
                                filteredCandidates.map((item, idx) => {
                                  const kwClean = cleanKw(item.keyword);
                                  const volInfo = getVolumeForKeyword(kwClean);
                                  const isPrimary = kwClean.toLowerCase() === masterPrimary.toLowerCase();
                                  const isSecondary = kwClean.toLowerCase() === masterSecondary.toLowerCase();
                                  const isTertiary = kwClean.toLowerCase() === masterTertiary.toLowerCase();
                                  const currentVar = localProject.variations.find(v => cleanKw(v.keyword).toLowerCase() === kwClean.toLowerCase());

                                  const isExplicitlyAssignedInStep10 = currentVar?.step10Assigned || (currentVar?.reason && (currentVar.reason.includes('Stage') || currentVar.reason.includes('AI Workstation') || currentVar.reason.includes('Promoted') || currentVar.reason.includes('Step 10')));
                                  const selectValue = isExplicitlyAssignedInStep10 ? cleanKw(currentVar?.variationOf || '') : '';

                                  return (
                                    <tr key={`stg1-${idx}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: isPrimary ? 'rgba(234, 179, 8, 0.15)' : isSecondary ? 'rgba(16, 185, 129, 0.15)' : isTertiary ? 'rgba(168, 85, 247, 0.15)' : idx % 2 === 0 ? 'rgba(15, 23, 42, 0.6)' : 'rgba(15, 23, 42, 0.3)' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700 }}>{idx + 1}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>{kwClean}</td>
                                      <td style={{ padding: '8px 12px' }}>
                                        <Badge variant={volInfo.status === 'collected' ? 'complete' : 'lecture'}>
                                          {volInfo.status === 'collected' ? `🔥 ${volInfo.volume ?? 0}` : 'Pending'}
                                        </Badge>
                                      </td>
                                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                          <button
                                            type="button"
                                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: 'none', cursor: 'pointer', background: isPrimary ? '#facc15' : 'rgba(234, 179, 8, 0.2)', color: isPrimary ? '#020617' : '#facc15' }}
                                            onClick={() => {
                                              setStep10SelectedPrimary(kwClean);
                                              let updatedMaster = localProject.masterKeywords.filter(m => m.priority !== 'Primary' && cleanKw(m.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updatedMaster.push({ id: `master-${Date.now()}`, keyword: kwClean, searchVolume: volInfo.volume ?? 0, intent: 'Commercial', priority: 'Primary', type: 'Target', source: item.stepName, targetPage: 'Homepage / Primary Target Page', action: 'Primary Keyword', status: 'active' });
                                              const updatedVars = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                            }}
                                          >
                                            {isPrimary ? '✓ Primary' : 'Set Primary'}
                                          </button>

                                          <button
                                            type="button"
                                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: 'none', cursor: 'pointer', background: isSecondary ? '#34d399' : 'rgba(16, 185, 129, 0.2)', color: isSecondary ? '#020617' : '#34d399' }}
                                            onClick={() => {
                                              setStep10SelectedSecondary(kwClean);
                                              let updatedMaster = localProject.masterKeywords.filter(m => m.priority !== 'Secondary' && cleanKw(m.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updatedMaster.push({ id: `master-${Date.now()}`, keyword: kwClean, searchVolume: volInfo.volume ?? 0, intent: 'Commercial', priority: 'Secondary', type: 'Target', source: item.stepName, targetPage: 'Core Service Page', action: 'Secondary Keyword', status: 'active' });
                                              const updatedVars = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                            }}
                                          >
                                            {isSecondary ? '✓ Secondary' : 'Set Secondary'}
                                          </button>

                                          <button
                                            type="button"
                                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: 'none', cursor: 'pointer', background: isTertiary ? '#c084fc' : 'rgba(168, 85, 247, 0.2)', color: isTertiary ? '#020617' : '#c084fc' }}
                                            onClick={() => {
                                              setStep10SelectedTertiary(kwClean);
                                              let updatedMaster = localProject.masterKeywords.filter(m => cleanKw(m.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updatedMaster.push({ id: `master-${Date.now()}`, keyword: kwClean, searchVolume: volInfo.volume ?? 0, intent: 'Commercial', priority: 'Tertiary', type: 'Target', source: item.stepName, targetPage: 'Supporting Service Page', action: 'Tertiary Keyword', status: 'active' });
                                              const updatedVars = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                            }}
                                          >
                                            {isTertiary ? '✓ Tertiary' : 'Set Tertiary'}
                                          </button>
                                        </div>
                                      </td>
                                      <td style={{ padding: '8px 12px' }}>
                                        {!isPrimary && !isSecondary && !isTertiary && (
                                          <select
                                            className="input-field"
                                            style={{ padding: '4px 8px', fontSize: '11px', width: '100%' }}
                                            value={selectValue}
                                            onChange={(e) => {
                                              const targetParent = e.target.value;
                                              let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              if (targetParent) {
                                                const resolvedTier = targetParent.toLowerCase() === masterPrimary.toLowerCase() ? 'Primary' : targetParent.toLowerCase() === masterSecondary.toLowerCase() ? 'Secondary' : 'Tertiary';
                                                updatedVars.push({
                                                  id: `var-${Date.now()}-${Math.random()}`,
                                                  keyword: kwClean,
                                                  variationOf: targetParent,
                                                  searchVolume: volInfo.volume ?? 0,
                                                  searchVolumeStatus: 'collected',
                                                  intent: 'Commercial',
                                                  serpSimilarity: ('High' as const),
                                                  separateTarget: false,
                                                  reason: `Assigned Variation (${resolvedTier} Target - Step 10)`,
                                                  verificationStatus: 'user_verified',
                                                  step10Assigned: true,
                                                  targetTier: resolvedTier as any,
                                                } as any);
                                              }
                                              let updatedMaster = localProject.masterKeywords.filter(m => cleanKw(m.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                            }}
                                          >
                                            <option value="">-- Potential Variation Of --</option>
                                            {masterPrimary && <option value={masterPrimary}>🥇 Primary: {masterPrimary}</option>}
                                            {masterSecondary && <option value={masterSecondary}>🥈 Secondary: {masterSecondary}</option>}
                                            {masterTertiary && <option value={masterTertiary}>🥉 Tertiary: {masterTertiary}</option>}
                                          </select>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '12px' }}>
                                    No candidate keywords match &quot;{step10SearchQuery}&quot;. Clear search filter to view all terms.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* STAGE 1 SUMMARY & CONTINUE WITH VARIATIONS VALIDATION */}
                        {(() => {
                          const attachedVarsCount = primaryVariations.length + secondaryVariations.length + tertiaryVariations.length;
                          const isStage1Valid = masterPrimary && masterSecondary && masterTertiary && (attachedVarsCount >= 1 || localProject.variations.length >= 1);

                          return (
                            <div style={{ background: '#020617', border: isStage1Valid ? '1px solid var(--accent-green)' : '1px solid #f87171', borderRadius: 'var(--radius-md)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {!isStage1Valid && (
                                <div style={{ fontSize: '12px', color: '#fca5a5', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <AlertTriangle size={15} color="#f87171" />
                                  <span>
                                    <strong>Requirement to Proceed:</strong> You must select Primary, Secondary, and Tertiary targets AND have <strong>at least 1 variation</strong> attached (Current attached variations: {attachedVarsCount}).
                                  </span>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                <div>
                                  <div style={{ fontSize: '13px', fontWeight: 800, color: isStage1Valid ? 'var(--accent-green)' : '#f87171', marginBottom: '4px' }}>
                                    {isStage1Valid ? '✓ Initial Hierarchy Assigned' : '⚠️ Target &amp; Variation Selection Incomplete'}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#e2e8f0' }}>
                                    Primary: <strong>{masterPrimary || 'Not Selected'}</strong> | Secondary: <strong>{masterSecondary || 'Not Selected'}</strong> | Tertiary: <strong>{masterTertiary || 'Not Selected'}</strong> | Variations: <strong>{attachedVarsCount} Attached</strong>
                                  </div>
                                </div>
                                <Button
                                  variant="success"
                                  size="md"
                                  disabled={!isStage1Valid}
                                  onClick={() => setStep10Stage(2)}
                                >
                                  Continue to Stage 2 — SERP Validation &amp; Refinement ➔
                                </Button>
                              </div>
                            </div>
                          );
                        })()}

                      </div>
                    )}

                    {/* STAGE 2 — SERP VALIDATION & REFINEMENT */}
                    {step10Stage === 2 && (
                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px' }}>
                        
                        {/* INSTRUCTION PANEL */}
                        <div style={{ background: '#020617', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: '20px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-blue)', marginBottom: '6px' }}>
                            📌 STAGE 2: SERP COMPARISON WORKSTATION
                          </div>
                          <div style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: 1.5 }}>
                            Compare Secondary and Tertiary candidates against your fixed <strong>Primary Target</strong> ({masterPrimary}).<br />
                            For each candidate, open the Google search links to inspect if ranking websites match Primary. If SERPs match, click <strong>Promote to Primary Variation</strong>.
                          </div>
                        </div>

                        {/* 2-COLUMN COMPARISON WORKSTATION — SECTION 1: PRIMARY TARGET */}
                        <div style={{ marginBottom: '24px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#facc15', marginBottom: '10px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🥇 SECTION 1: PRIMARY TARGET COMPARISON</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'none', fontWeight: 400 }}>(Compare Secondary & Tertiary candidates against Primary)</span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                            
                            {/* LEFT COLUMN: PRIMARY TARGET (FIXED & UNCHANGEABLE) */}
                            <div style={{ background: '#020617', border: '2px solid #facc15', borderRadius: 'var(--radius-md)', padding: '18px', height: 'fit-content' }}>
                              <div style={{ fontSize: '11px', fontWeight: 800, color: '#facc15', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>🥇 PRIMARY TARGET (FIXED)</span>
                                <Badge variant="lecture">Master Target</Badge>
                              </div>
                              <div style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', marginBottom: '4px' }}>
                                {masterPrimary || 'No Primary Selected'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#facc15', marginBottom: '14px', fontWeight: 700 }}>
                                Search Volume: {getVolumeForKeyword(masterPrimary).volume ?? 0}/mo
                              </div>
                              
                              <a
                                href={`https://www.google.com/search?q=${encodeURIComponent(masterPrimary)}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(234, 179, 8, 0.2)', border: '1px solid #facc15', color: '#facc15', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '12px', fontWeight: 800, textDecoration: 'none', width: '100%', justifyContent: 'center' }}
                              >
                                Search Primary Target on Google ↗
                              </a>

                              <div style={{ marginTop: '16px', background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.2)', borderRadius: 'var(--radius-sm)', padding: '12px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 800, color: '#facc15', marginBottom: '6px' }}>
                                  CONFIRMED PRIMARY VARIATIONS ({primaryVariations.length})
                                </div>
                                {primaryVariations.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px', color: '#ffffff' }}>
                                    {primaryVariations.map(v => (
                                      <li key={v.id} style={{ marginBottom: '4px' }}>
                                        <strong>"{cleanKw(v.keyword)}"</strong> ({getVolumeForKeyword(v.keyword).volume ?? 0}/mo)
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                    No variations promoted to Primary yet.
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* RIGHT COLUMN: SECONDARY & TERTIARY COMPARISON CONTAINERS */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              
                              {/* BOX 1: SECONDARY TARGET & VARIATIONS COMPARISON */}
                              <div style={{ background: '#020617', border: '1px solid #34d399', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px', borderBottom: '1px solid rgba(16, 185, 129, 0.3)', paddingBottom: '10px' }}>
                                  <div>
                                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#34d399', textTransform: 'uppercase' }}>
                                      🥈 SECONDARY TARGET & VARIATIONS COMPARISON
                                    </div>
                                    <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', marginTop: '2px' }}>
                                      {masterSecondary} <span style={{ fontSize: '11px', color: '#34d399', fontWeight: 600 }}>(Vol: {getVolumeForKeyword(masterSecondary).volume ?? 0}/mo)</span>
                                    </div>
                                  </div>
                                  <a
                                    href={`https://www.google.com/search?q=${encodeURIComponent(masterSecondary)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid #34d399', color: '#34d399', padding: '6px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 800, textDecoration: 'none' }}
                                  >
                                    Search Secondary on Google ↗
                                  </a>
                                </div>

                                {/* Secondary Items List */}
                                {(() => {
                                  const secItems = [
                                    { keyword: masterSecondary, isTarget: true },
                                    ...secondaryVariations.map(v => ({ keyword: v.keyword, isTarget: false }))
                                  ];

                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {secItems.map((item, idx) => {
                                        const kwClean = cleanKw(item.keyword);
                                        const vol = getVolumeForKeyword(kwClean).volume ?? 0;

                                        return (
                                          <div key={`sec-item-${idx}-${kwClean}`} style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                            <div>
                                              <div style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>"{kwClean}"</span>
                                                {item.isTarget ? <Badge variant="tool">Main Secondary Target</Badge> : <Badge variant="complete">Secondary Variation</Badge>}
                                              </div>
                                              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>Search Vol: {vol}/mo</div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                              <a
                                                href={`https://www.google.com/search?q=${encodeURIComponent(kwClean)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#38bdf8', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, textDecoration: 'none' }}
                                              >
                                                Search Candidate on Google ↗
                                              </a>
                                              <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => {
                                                  let updatedMaster = [...localProject.masterKeywords];
                                                  if (item.isTarget) {
                                                    updatedMaster = updatedMaster.filter(m => cleanKw(m.keyword).toLowerCase() !== masterSecondary.toLowerCase());
                                                    setStep10SelectedSecondary('');
                                                  }
                                                  let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                                  updatedVars.push({
                                                    id: `var-${Date.now()}-${Math.random()}`,
                                                    keyword: kwClean,
                                                    variationOf: masterPrimary,
                                                    searchVolume: vol,
                                                    searchVolumeStatus: 'collected',
                                                    intent: 'Commercial',
                                                    serpSimilarity: 'High',
                                                    separateTarget: false,
                                                    reason: `Promoted from Secondary to Primary (${masterPrimary}) Variation via SERP Check`,
                                                    verificationStatus: 'user_verified'
                                                  });
                                                  updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                                }}
                                              >
                                                ⬆ Promote to Primary
                                              </Button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* BOX 2: TERTIARY TARGET & VARIATIONS COMPARISON */}
                              <div style={{ background: '#020617', border: '1px solid #c084fc', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px', borderBottom: '1px solid rgba(168, 85, 247, 0.3)', paddingBottom: '10px' }}>
                                  <div>
                                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase' }}>
                                      🥉 TERTIARY TARGET & VARIATIONS COMPARISON
                                    </div>
                                    <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', marginTop: '2px' }}>
                                      {masterTertiary} <span style={{ fontSize: '11px', color: '#c084fc', fontWeight: 600 }}>(Vol: {getVolumeForKeyword(masterTertiary).volume ?? 0}/mo)</span>
                                    </div>
                                  </div>
                                  <a
                                    href={`https://www.google.com/search?q=${encodeURIComponent(masterTertiary)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(168, 85, 247, 0.2)', border: '1px solid #c084fc', color: '#c084fc', padding: '6px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 800, textDecoration: 'none' }}
                                  >
                                    Search Tertiary on Google ↗
                                  </a>
                                </div>

                                {/* Tertiary Items List */}
                                {(() => {
                                  const tertItems = [
                                    { keyword: masterTertiary, isTarget: true },
                                    ...tertiaryVariations.map(v => ({ keyword: v.keyword, isTarget: false }))
                                  ];

                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {tertItems.map((item, idx) => {
                                        const kwClean = cleanKw(item.keyword);
                                        const vol = getVolumeForKeyword(kwClean).volume ?? 0;

                                        return (
                                          <div key={`tert-item-${idx}-${kwClean}`} style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                            <div>
                                              <div style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>"{kwClean}"</span>
                                                {item.isTarget ? <Badge variant="lecture">Main Tertiary Target</Badge> : <Badge variant="complete">Tertiary Variation</Badge>}
                                              </div>
                                              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>Search Vol: {vol}/mo</div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                              <a
                                                href={`https://www.google.com/search?q=${encodeURIComponent(kwClean)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#38bdf8', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, textDecoration: 'none' }}
                                              >
                                                Search Candidate on Google ↗
                                              </a>
                                              <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => {
                                                  let updatedMaster = [...localProject.masterKeywords];
                                                  if (item.isTarget) {
                                                    updatedMaster = updatedMaster.filter(m => cleanKw(m.keyword).toLowerCase() !== masterTertiary.toLowerCase());
                                                    setStep10SelectedTertiary('');
                                                  }
                                                  let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                                  updatedVars.push({
                                                    id: `var-${Date.now()}-${Math.random()}`,
                                                    keyword: kwClean,
                                                    variationOf: masterSecondary,
                                                    searchVolume: vol,
                                                    searchVolumeStatus: 'collected',
                                                    intent: 'Commercial',
                                                    serpSimilarity: 'High',
                                                    separateTarget: false,
                                                    reason: `Promoted from Tertiary to Secondary (${masterSecondary}) Variation via SERP Check`,
                                                    verificationStatus: 'user_verified'
                                                  });
                                                  updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                                }}
                                              >
                                                Promote to Secondary
                                              </Button>
                                              <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => {
                                                  let updatedMaster = [...localProject.masterKeywords];
                                                  if (item.isTarget) {
                                                    updatedMaster = updatedMaster.filter(m => cleanKw(m.keyword).toLowerCase() !== masterTertiary.toLowerCase());
                                                    setStep10SelectedTertiary('');
                                                  }
                                                  let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                                  updatedVars.push({
                                                    id: `var-${Date.now()}-${Math.random()}`,
                                                    keyword: kwClean,
                                                    variationOf: masterPrimary,
                                                    searchVolume: vol,
                                                    searchVolumeStatus: 'collected',
                                                    intent: 'Commercial',
                                                    serpSimilarity: 'High',
                                                    separateTarget: false,
                                                    reason: `Promoted from Tertiary to Primary (${masterPrimary}) Variation via SERP Check`,
                                                    verificationStatus: 'user_verified'
                                                  });
                                                  updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                                }}
                                              >
                                                ⬆ Promote to Primary
                                              </Button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>

                            </div>

                          </div>
                        </div>

                        {/* SECTION 2: SECONDARY TARGET COMPARISON (SECONDARY vs TERTIARY) */}
                        <div style={{ marginBottom: '24px', background: '#020617', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#34d399', marginBottom: '10px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🥈 SECTION 2: SECONDARY TARGET COMPARISON</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'none', fontWeight: 400 }}>(Compare Tertiary candidates against Secondary)</span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                            
                            {/* LEFT SIDE: MAIN SECONDARY TARGET (FIXED) */}
                            <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '2px solid #34d399', borderRadius: 'var(--radius-sm)', padding: '14px', height: 'fit-content' }}>
                              <div style={{ fontSize: '10px', fontWeight: 800, color: '#34d399', textTransform: 'uppercase', marginBottom: '4px' }}>MAIN SECONDARY TARGET</div>
                              <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', marginBottom: '2px' }}>{masterSecondary || 'No Secondary Selected'}</div>
                              <div style={{ fontSize: '11px', color: '#34d399', marginBottom: '10px', fontWeight: 600 }}>Vol: {getVolumeForKeyword(masterSecondary).volume ?? 0}/mo</div>
                              <a
                                href={`https://www.google.com/search?q=${encodeURIComponent(masterSecondary)}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid #34d399', color: '#34d399', padding: '8px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 800, textDecoration: 'none', width: '100%', justifyContent: 'center' }}
                              >
                                Search Secondary Target on Google ↗
                              </a>
                            </div>

                            {/* RIGHT SIDE: TERTIARY TARGET & VARIATIONS */}
                            <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid #c084fc', borderRadius: 'var(--radius-sm)', padding: '14px' }}>
                              <div style={{ fontSize: '11px', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase', marginBottom: '8px' }}>
                                TERTIARY TARGET & VARIATIONS TO TEST VS SECONDARY
                              </div>

                              {(() => {
                                const tertItems = [
                                  { keyword: masterTertiary, isTarget: true },
                                  ...tertiaryVariations.map(v => ({ keyword: v.keyword, isTarget: false }))
                                ];

                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {tertItems.map((item, idx) => {
                                      const kwClean = cleanKw(item.keyword);
                                      const vol = getVolumeForKeyword(kwClean).volume ?? 0;

                                      return (
                                        <div key={`sec-comp-tert-${idx}-${kwClean}`} style={{ background: '#020617', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                          <div>
                                            <div style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                              <span>"{kwClean}"</span>
                                              {item.isTarget ? <Badge variant="lecture">Tertiary Target</Badge> : <Badge variant="complete">Tertiary Var</Badge>}
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>Vol: {vol}/mo</div>
                                          </div>

                                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <a
                                              href={`https://www.google.com/search?q=${encodeURIComponent(kwClean)}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#38bdf8', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, textDecoration: 'none' }}
                                            >
                                              Search Candidate ↗
                                            </a>
                                            <Button
                                              variant="secondary"
                                              size="sm"
                                              onClick={() => {
                                                let updatedMaster = [...localProject.masterKeywords];
                                                if (item.isTarget) {
                                                  updatedMaster = updatedMaster.filter(m => cleanKw(m.keyword).toLowerCase() !== masterTertiary.toLowerCase());
                                                  setStep10SelectedTertiary('');
                                                }
                                                let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                                updatedVars.push({
                                                  id: `var-${Date.now()}-${Math.random()}`,
                                                  keyword: kwClean,
                                                  variationOf: masterSecondary,
                                                  searchVolume: vol,
                                                  searchVolumeStatus: 'collected',
                                                  intent: 'Commercial',
                                                  serpSimilarity: 'High',
                                                  separateTarget: false,
                                                  reason: `Promoted to Secondary (${masterSecondary}) Variation via SERP Check`,
                                                  verificationStatus: 'user_verified'
                                                });
                                                updateProject({ ...localProject, masterKeywords: updatedMaster, variations: updatedVars as any });
                                              }}
                                            >
                                              ⬆ Promote to Secondary
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>

                          </div>
                        </div>

                        {/* SECTION 3: SUPPORTING KEYWORDS WORKSTATION */}
                        {(() => {
                          const assignedKwSet = new Set([
                            cleanKw(masterPrimary).toLowerCase(),
                            cleanKw(masterSecondary).toLowerCase(),
                            cleanKw(masterTertiary).toLowerCase(),
                            ...localProject.variations.map(v => cleanKw(v.keyword).toLowerCase())
                          ]);

                          const supportingKeywords = sortedUniqueList.filter(item => {
                            const k = cleanKw(item.keyword).toLowerCase();
                            return !assignedKwSet.has(k);
                          });

                          return (
                            <div style={{ marginBottom: '24px', background: '#020617', border: '1px solid rgba(56, 189, 248, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-blue)', marginBottom: '4px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>💬 SECTION 3: SUPPORTING KEYWORDS ({supportingKeywords.length})</span>
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                                Candidate terms from your dataset that are not yet assigned as Primary, Secondary, or Tertiary targets/variations. Inspect on Google and promote to target tier.
                              </div>

                              {supportingKeywords.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                                  {supportingKeywords.map((item, idx) => {
                                    const kwClean = cleanKw(item.keyword);
                                    const vol = getVolumeForKeyword(kwClean).volume ?? 0;

                                    return (
                                      <div key={`supp-kw-${idx}-${kwClean}`} style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                        <div>
                                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff' }}>"{kwClean}"</div>
                                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>Search Vol: {vol}/mo</div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                          <a
                                            href={`https://www.google.com/search?q=${encodeURIComponent(kwClean)}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#38bdf8', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, textDecoration: 'none' }}
                                          >
                                            Search Candidate on Google ↗
                                          </a>
                                          
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => {
                                              let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updatedVars.push({
                                                id: `var-${Date.now()}-${Math.random()}`,
                                                keyword: kwClean,
                                                variationOf: masterPrimary,
                                                searchVolume: vol,
                                                searchVolumeStatus: 'collected',
                                                intent: 'Commercial',
                                                serpSimilarity: 'High',
                                                separateTarget: false,
                                                reason: `Promoted supporting keyword to Primary (${masterPrimary}) Variation via SERP Check`,
                                                verificationStatus: 'user_verified'
                                              });
                                              updateProject({ ...localProject, variations: updatedVars as any });
                                            }}
                                          >
                                            + Primary
                                          </Button>

                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                              let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updatedVars.push({
                                                id: `var-${Date.now()}-${Math.random()}`,
                                                keyword: kwClean,
                                                variationOf: masterSecondary,
                                                searchVolume: vol,
                                                searchVolumeStatus: 'collected',
                                                intent: 'Commercial',
                                                serpSimilarity: 'High',
                                                separateTarget: false,
                                                reason: `Promoted supporting keyword to Secondary (${masterSecondary}) Variation via SERP Check`,
                                                verificationStatus: 'user_verified'
                                              });
                                              updateProject({ ...localProject, variations: updatedVars as any });
                                            }}
                                          >
                                            + Secondary
                                          </Button>

                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            style={{ border: '1px solid #c084fc', color: '#c084fc' }}
                                            onClick={() => {
                                              let updatedVars: KeywordVariationItem[] = localProject.variations.filter(v => cleanKw(v.keyword).toLowerCase() !== kwClean.toLowerCase());
                                              updatedVars.push({
                                                id: `var-${Date.now()}-${Math.random()}`,
                                                keyword: kwClean,
                                                variationOf: masterTertiary,
                                                searchVolume: vol,
                                                searchVolumeStatus: 'collected',
                                                intent: 'Commercial',
                                                serpSimilarity: 'High',
                                                separateTarget: false,
                                                reason: `Promoted supporting keyword to Tertiary (${masterTertiary}) Variation via SERP Check`,
                                                verificationStatus: 'user_verified'
                                              });
                                              updateProject({ ...localProject, variations: updatedVars as any });
                                            }}
                                          >
                                            + Tertiary
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ fontSize: '11px', color: 'var(--accent-green)', fontStyle: 'italic' }}>
                                  ✓ All candidate keywords have been classified under Primary, Secondary, or Tertiary targets!
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* CONTINUE TO STAGE 3 BUTTON */}
                        <div style={{ background: '#020617', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-green)', marginBottom: '4px' }}>
                              ✓ SERP Validation & Refinement Completed
                            </div>
                            <div style={{ fontSize: '11px', color: '#e2e8f0' }}>
                              Primary: <strong>{masterPrimary}</strong> ({primaryVariations.length} Variations) | Secondary: <strong>{masterSecondary}</strong> ({secondaryVariations.length} Variations) | Tertiary: <strong>{masterTertiary}</strong> ({tertiaryVariations.length} Variations)
                            </div>
                          </div>
                          <Button
                            variant="success"
                            size="md"
                            onClick={() => setStep10Stage(3)}
                          >
                            Continue to Stage 3 — Finalize Keyword Hierarchy ➔
                          </Button>
                        </div>

                      </div>
                    )}

                    {/* STAGE 3 — FINALIZE */}
                    {step10Stage === 3 && (
                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px' }}>
                        
                        <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Sparkles size={18} color="var(--accent-green)" />
                          <span>STAGE 3 — FINAL KEYWORD HIERARCHY REVIEW</span>
                        </div>

                        <div style={{ background: '#020617', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: '20px', fontSize: '12px', color: '#e2e8f0' }}>
                          Review your finalized Primary, Secondary, and Tertiary target hierarchy with confirmed variations and separate targets. Once confirmed, click below to continue directly to Page Mapping.
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                          
                          {/* PRIMARY GROUP */}
                          <div style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid #facc15', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: '#facc15', marginBottom: '8px' }}>
                              🥇 PRIMARY KEYWORD TARGET: &quot;{masterPrimary}&quot; (Vol: {getVolumeForKeyword(masterPrimary).volume ?? 0}/mo)
                            </div>
                            <div style={{ fontSize: '11px', color: '#e2e8f0' }}>
                              <div style={{ fontWeight: 800, marginBottom: '6px', color: '#fef08a' }}>Confirmed Target &amp; Variations:</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                <KeywordCapsule
                                  keyword={masterPrimary}
                                  volume={getVolumeForKeyword(masterPrimary).volume ?? 0}
                                  tier="primary"
                                  tag="Primary Target"
                                />
                                {localProject.variations.filter(v => v.variationOf.toLowerCase() === masterPrimary.toLowerCase() && v.serpSimilarity === 'High').map(v => (
                                  <KeywordCapsule
                                    key={v.id}
                                    keyword={v.keyword}
                                    volume={getVolumeForKeyword(v.keyword).volume ?? 0}
                                    tier="variation"
                                    tag="Verified SERP Overlap"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* SECONDARY GROUP */}
                          <div style={{ background: 'rgba(16, 185, 129, 0.12)', border: '1px solid #34d399', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: '#34d399', marginBottom: '8px' }}>
                              🥈 SECONDARY KEYWORD TARGET: &quot;{masterSecondary}&quot; (Vol: {getVolumeForKeyword(masterSecondary).volume ?? 0}/mo)
                            </div>
                            <div style={{ fontSize: '11px', color: '#e2e8f0' }}>
                              <div style={{ fontWeight: 800, marginBottom: '6px', color: '#a7f3d0' }}>Confirmed Target &amp; Variations:</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                <KeywordCapsule
                                  keyword={masterSecondary}
                                  volume={getVolumeForKeyword(masterSecondary).volume ?? 0}
                                  tier="secondary"
                                  tag="Secondary Target"
                                />
                                {localProject.variations.filter(v => v.variationOf.toLowerCase() === masterSecondary.toLowerCase() && v.serpSimilarity === 'High').map(v => (
                                  <KeywordCapsule
                                    key={v.id}
                                    keyword={v.keyword}
                                    volume={getVolumeForKeyword(v.keyword).volume ?? 0}
                                    tier="variation"
                                    tag="Verified SERP Overlap"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* TERTIARY GROUP */}
                          <div style={{ background: 'rgba(168, 85, 247, 0.12)', border: '1px solid #c084fc', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: '#c084fc', marginBottom: '8px' }}>
                              🥉 TERTIARY KEYWORD TARGET: &quot;{masterTertiary}&quot; (Vol: {getVolumeForKeyword(masterTertiary).volume ?? 0}/mo)
                            </div>
                            <div style={{ fontSize: '11px', color: '#e2e8f0' }}>
                              <div style={{ fontWeight: 800, marginBottom: '6px', color: '#e9d5ff' }}>Confirmed Target &amp; Variations:</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                <KeywordCapsule
                                  keyword={masterTertiary}
                                  volume={getVolumeForKeyword(masterTertiary).volume ?? 0}
                                  tier="tertiary"
                                  tag="Tertiary Target"
                                />
                                {localProject.variations.filter(v => v.variationOf.toLowerCase() === masterTertiary.toLowerCase() && v.serpSimilarity === 'High').map(v => (
                                  <KeywordCapsule
                                    key={v.id}
                                    keyword={v.keyword}
                                    volume={getVolumeForKeyword(v.keyword).volume ?? 0}
                                    tier="variation"
                                    tag="Verified SERP Overlap"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* REMAINING / SUPPORTING KEYWORD TARGETS */}
                          {(() => {
                            const assignedKwSet = new Set([
                              cleanKw(masterPrimary).toLowerCase(),
                              cleanKw(masterSecondary).toLowerCase(),
                              cleanKw(masterTertiary).toLowerCase(),
                              ...localProject.variations.filter(v => v.serpSimilarity === 'High').map(v => cleanKw(v.keyword).toLowerCase())
                            ]);

                            const remainingKeywords = sortedUniqueList.filter(item => {
                              const k = cleanKw(item.keyword).toLowerCase();
                              return !assignedKwSet.has(k);
                            });

                            return (
                              <div style={{ background: 'rgba(56, 189, 248, 0.12)', border: '1px solid #38bdf8', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>📄 SUPPORTING &amp; REMAINING KEYWORD TARGETS ({remainingKeywords.length})</span>
                                </div>
                                {remainingKeywords.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '450px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {remainingKeywords.map((item, idx) => {
                                      const kwClean = cleanKw(item.keyword);
                                      const vol = getVolumeForKeyword(kwClean).volume ?? 0;
                                      const currentVar = localProject.variations.find(v => cleanKw(v.keyword).toLowerCase() === kwClean.toLowerCase());
                                      const isLowSerp = currentVar?.serpSimilarity === 'Low' || currentVar?.separateTarget;

                                      return (
                                        <KeywordCapsule
                                          key={`rem-kw-${idx}-${kwClean}`}
                                          keyword={kwClean}
                                          volume={vol}
                                          tier={isLowSerp ? 'variation' : 'supporting'}
                                          tag={isLowSerp ? 'Separate Page Target' : 'Supporting Candidate'}
                                        />
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '11px', color: 'var(--accent-green)', fontStyle: 'italic' }}>
                                    ✓ All candidate keywords have been classified into Primary, Secondary, or Tertiary target groups!
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* STAGE 3 SUMMARY & CONTINUE WITH VARIATIONS VALIDATION */}
                        {(() => {
                          const totalVariationsCount = localProject.variations.length;
                          const isStage3Valid = masterPrimary && masterSecondary && masterTertiary && totalVariationsCount >= 1;

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {!isStage3Valid && (
                                <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #f87171', borderRadius: '6px', padding: '10px 14px', fontSize: '12px', color: '#fca5a5', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <AlertTriangle size={16} color="#f87171" />
                                  <span>
                                    <strong>Requirement to Proceed:</strong> Minimum <strong>1 variation</strong> is required to proceed. (Current variations recorded: {totalVariationsCount}).
                                  </span>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <Button
                                  variant="success"
                                  size="md"
                                  disabled={!isStage3Valid}
                                  onClick={() => handleStepChange(10)}
                                >
                                  ✓ Confirm Step 10 &amp; Continue to Page Mapping ➔
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="md"
                                  onClick={() => setStep10Stage(2)}
                                >
                                  ◀ Back to SERP Validation
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                  </div>
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 11: UNIFIED SERVICE PAGES KEYWORD WORKSTATION */}
          {activeStep === 10 && (
            <KeywordStepGuide
              stepNumber={11}
              stepTitle="Service Pages Unified Keyword Research Workstation"
              whyAreWeDoingThis="Instructor Methodology (Lecture 6 & 11): Steps 1–10 established the keyword strategy strictly for the Homepage. Step 11 merges all 10 discovery, volume, variation, and SERP hierarchy steps into a single unified workstation screen for every Service Page offered by the client."
              whatYouNeedBeforeStarting={['Finalized Step 10 Homepage Keywords', 'Client Service Portfolio List']}
              exactlyWhatToDo={[
                'Add and manage all Service Pages offered by the client (e.g. Engine Repair, Brake Components).',
                'Use the integrated Discovery Engine (Autocomplete, Competitors, Related, Modifiers) to populate keyword candidates.',
                'Assign 🥇 Primary, 🥈 Secondary, and 🥉 Tertiary keyword targets for each Service Page.',
                'Use the live Google SERP comparison links to test SERP overlap.',
                'Verify zero keyword cannibalization with the Homepage or sibling Service Pages.'
              ]}
              whatToLookFor={['Clean URL structure per service page', 'Distinct primary keyword target per page', 'Zero cannibalization with homepage']}
              whatToRecord={['Service Page Name', 'Mapped URL', 'Primary Keyword', 'Secondary Keyword', 'Tertiary Keyword', 'Research Methods Used']}
              lectureExample={{
                scenario: 'Instructor maps keywords for multi-service commercial business.',
                action: 'Sets Homepage to "Truck Parts Trinidad", Engine Page to "Engine Repair Trinidad", Brake Page to "Truck Brakes Trinidad".',
                result: 'Each service page ranks independently for its specific commercial cluster.'
              }}
              doneWhen="All client Service Pages have assigned clean URLs, Primary/Secondary/Tertiary keywords, and completed research checklists."
              isStepValidated={true}
              onPrevious={() => handleStepChange(9)}
              onContinue={() => handleStepChange(11)}
            >
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList } = accumulated;
                const sortedUniqueList = [...uniqueList].sort((a, b) => {
                  const volA = getVolumeForKeyword(a.keyword).status === 'collected' ? (getVolumeForKeyword(a.keyword).volume ?? 0) : -1;
                  const volB = getVolumeForKeyword(b.keyword).status === 'collected' ? (getVolumeForKeyword(b.keyword).volume ?? 0) : -1;
                  return volB - volA;
                });

                const cleanKw = (kw: string) => (kw || '').replace(/^Variation:\s*/i, '').trim();

                const hpMasterPrimary = localProject.masterKeywords?.find(m => m.priority === 'Primary')?.keyword;
                const hpMasterSecondary = localProject.masterKeywords?.find(m => m.priority === 'Secondary')?.keyword;
                const hpMasterTertiary = localProject.masterKeywords?.find(m => m.priority === 'Tertiary')?.keyword;

                const masterPrimary = cleanKw(step10SelectedPrimary || hpMasterPrimary || sortedUniqueList[0]?.keyword || '');
                const masterSecondary = cleanKw(step10SelectedSecondary || hpMasterSecondary || sortedUniqueList.find(s => cleanKw(s.keyword).toLowerCase() !== masterPrimary.toLowerCase())?.keyword || '');
                const masterTertiary = cleanKw(step10SelectedTertiary || hpMasterTertiary || sortedUniqueList.find(s => cleanKw(s.keyword).toLowerCase() !== masterPrimary.toLowerCase() && cleanKw(s.keyword).toLowerCase() !== masterSecondary.toLowerCase())?.keyword || '');

                const isStep10AssignedVar = (v: KeywordVariationItem, targetKw: string, tier?: string) => {
                  if (!targetKw) return false;
                  const kwClean = cleanKw(v.keyword).toLowerCase();
                  const targetClean = cleanKw(targetKw).toLowerCase();
                  if (kwClean === targetClean) return false;

                  const matchesParent = cleanKw(v.variationOf || '').toLowerCase() === targetClean;
                  const matchesTier = (v as any).targetTier && (v as any).targetTier === tier;

                  if (!matchesParent && !matchesTier) return false;

                  const isExplicit = (v as any).step10Assigned === true || matchesTier;
                  const isReasonAssigned = v.reason ? (
                    v.reason.includes('Stage') ||
                    v.reason.includes('AI Workstation') ||
                    v.reason.includes('Promoted') ||
                    v.reason.includes('Assigned') ||
                    v.reason.includes('Step 10')
                  ) : false;

                  return isExplicit || isReasonAssigned;
                };

                const primaryVariations = localProject.variations.filter(v => isStep10AssignedVar(v, masterPrimary, 'Primary'));
                const secondaryVariations = localProject.variations.filter(v => isStep10AssignedVar(v, masterSecondary, 'Secondary'));
                const tertiaryVariations = localProject.variations.filter(v => isStep10AssignedVar(v, masterTertiary, 'Tertiary'));

                return (
                  <ServicePageWorkstation
                    project={localProject}
                    masterPrimary={masterPrimary}
                    primaryVariations={primaryVariations}
                    masterSecondary={masterSecondary}
                    secondaryVariations={secondaryVariations}
                    masterTertiary={masterTertiary}
                    tertiaryVariations={tertiaryVariations}
                    onUpdateProject={(updated) => updateProject(updated)}
                  />
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 12: PAGE-BY-PAGE SEO METADATA WORKSTATION (TITLES & DESCRIPTIONS MERGED) */}
          {activeStep === 11 && (
            <KeywordStepGuide
              stepNumber={12}
              stepTitle="Page-by-Page SEO Meta Data Optimization (Titles & Descriptions)"
              whyAreWeDoingThis="Instructor Methodology (Lecture 11): Primary Keyword + Secondary Keyword | Brand/Location for SEO Title (50–60 chars). Primary Keyword Variation + Secondary Keyword Variation + Call to Action for Meta Description (140–160 chars). Every page (Homepage + Service Pages) requires optimized title and meta tags."
              whatYouNeedBeforeStarting={['Finalized Homepage Keywords (Step 10)', 'Finalized Service Page Keywords (Step 11)', 'Target Business Location & Brand Name']}
              exactlyWhatToDo={[
                'Review the Primary, Secondary, and Tertiary keywords assigned for each page (Homepage + Service Pages).',
                'Apply the Instructor Formula to generate 50–60 character SEO Titles.',
                'Apply the Instructor Formula to generate 140–160 character Meta Descriptions.',
                'Inspect live Google SERP Desktop Snippet Previews to ensure zero text truncation.'
              ]}
              whatToLookFor={['Primary Keyword at front of title', 'Variation keywords in meta description', '50–60 char Title limit', '140–160 char Meta limit']}
              whatToRecord={['Final Page SEO Title Tags', 'Final Meta Description Tags']}
              lectureExample={{
                scenario: 'Instructor optimizes metadata for homepage and service pages.',
                action: 'Homepage Title: "Commercial Cleaning Services | Professional Cleaners", Engine Page Meta: "Looking for Commercial Window Cleaning in Scottsdale? We specialize in exterior glass washing. Call us today!"',
                result: 'Achieves ideal length limits across all pages with zero Google SERP truncation.'
              }}
              doneWhen="All site pages (Homepage + Service Pages) have optimized SEO Titles (50-60 chars) and Meta Descriptions (140-160 chars)."
              isStepValidated={true}
              onPrevious={() => handleStepChange(10)}
              onContinue={() => handleStepChange(12)}
            >
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList } = accumulated;
                const sortedUniqueList = [...uniqueList].sort((a, b) => {
                  const volA = getVolumeForKeyword(a.keyword).status === 'collected' ? (getVolumeForKeyword(a.keyword).volume ?? 0) : -1;
                  const volB = getVolumeForKeyword(b.keyword).status === 'collected' ? (getVolumeForKeyword(b.keyword).volume ?? 0) : -1;
                  return volB - volA;
                });

                const cleanKw = (kw: string) => (kw || '').replace(/^Variation:\s*/i, '').trim();

                const masterPrimary = cleanKw(
                  step10SelectedPrimary || 
                  localProject.masterKeywords?.find(m => m.priority === 'Primary')?.keyword || 
                  sortedUniqueList[0]?.keyword || 
                  localProject.seedKeywords[0]?.seedKeyword ||
                  'Primary Target Service'
                );

                const masterSecondary = cleanKw(
                  step10SelectedSecondary || 
                  localProject.masterKeywords?.find(m => m.priority === 'Secondary')?.keyword || 
                  sortedUniqueList.find(s => cleanKw(s.keyword) !== masterPrimary)?.keyword || 
                  'Secondary Target Service'
                );

                const masterTertiary = cleanKw(
                  step10SelectedTertiary || 
                  localProject.masterKeywords?.find(m => m.priority === 'Tertiary')?.keyword || 
                  sortedUniqueList.find(s => cleanKw(s.keyword) !== masterPrimary && cleanKw(s.keyword) !== masterSecondary)?.keyword || 
                  'Heavy Duty Truck Accessories'
                );

                const primaryVariations = (localProject.variations || [])
                  .filter(v => cleanKw(v.variationOf).toLowerCase() === masterPrimary.toLowerCase() && cleanKw(v.keyword).toLowerCase() !== masterPrimary.toLowerCase())
                  .map(v => cleanKw(v.keyword));

                const secondaryVariations = (localProject.variations || [])
                  .filter(v => cleanKw(v.variationOf).toLowerCase() === masterSecondary.toLowerCase() && cleanKw(v.keyword).toLowerCase() !== masterSecondary.toLowerCase())
                  .map(v => cleanKw(v.keyword));

                const tertiaryVariations = (localProject.variations || [])
                  .filter(v => cleanKw(v.variationOf).toLowerCase() === masterTertiary.toLowerCase() && cleanKw(v.keyword).toLowerCase() !== masterTertiary.toLowerCase())
                  .map(v => cleanKw(v.keyword));

                return (
                  <SeoMetadataWorkstation
                    project={localProject}
                    masterPrimary={masterPrimary}
                    primaryVariations={primaryVariations}
                    masterSecondary={masterSecondary}
                    secondaryVariations={secondaryVariations}
                    masterTertiary={masterTertiary}
                    tertiaryVariations={tertiaryVariations}
                    onUpdateProject={(updated) => updateProject(updated)}
                  />
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 13: CONTENT KEYWORD PLACEMENT & DENSITY WORKSTATION */}
          {activeStep === 12 && (
            <KeywordStepGuide
              stepNumber={13}
              stepTitle="Content Keyword Placement & Density Workstation (500w / 1,000w Baseline)"
              whyAreWeDoingThis="Instructor Methodology (Lecture 11 & Transcript): Baseline 500 words content. Primary Keyword used 3x (Intro Hook 1x, H2/Body 1x, Conclusion 1x). Secondary Keyword used 2x (H2 1x, Body 1x). Tertiary Keyword used 1x (H3/Body 1x). Maintain ~0.5% keyword density to avoid Google Keyword Stuffing penalties."
              whatYouNeedBeforeStarting={['Finalized Homepage Keywords (Step 10)', 'Finalized Service Page Keywords (Step 11)']}
              exactlyWhatToDo={[
                'Select Homepage or a Service Page to audit content keyword placement.',
                'Select 500 Words or 1,000 Words content baseline target.',
                'Verify Primary Keyword is placed 3x (Intro hook 1x, Middle body 1x, Conclusion 1x).',
                'Verify Secondary Keyword is placed 2x (H2 1x, Body 1x) and Tertiary is placed 1x.',
                'Ensure Keyword Density stays around ~0.5% (Warning alert if > 1.5%).'
              ]}
              whatToLookFor={['3x Primary Keyword placement', '2x Secondary Keyword placement', '1x Tertiary placement', '~0.5% Keyword Density']}
              whatToRecord={['Page Content Draft', 'Live Density Score', 'Keyword Placement Verification']}
              lectureExample={{
                scenario: 'Instructor audits page content keyword placements.',
                action: 'Places Primary 3x (Intro, H2, Conclusion) in 500w content with 0.52% density.',
                result: 'Avoids keyword stuffing penalties while signaling maximum topical relevance to Google.'
              }}
              doneWhen="All pages have verified keyword placements and healthy ~0.5% density."
              isStepValidated={true}
              onPrevious={() => handleStepChange(11)}
              onContinue={() => handleStepChange(13)}
            >
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList } = accumulated;
                const sortedUniqueList = [...uniqueList].sort((a, b) => {
                  const volA = getVolumeForKeyword(a.keyword).status === 'collected' ? (getVolumeForKeyword(a.keyword).volume ?? 0) : -1;
                  const volB = getVolumeForKeyword(b.keyword).status === 'collected' ? (getVolumeForKeyword(b.keyword).volume ?? 0) : -1;
                  return volB - volA;
                });

                const cleanKw = (kw: string) => (kw || '').replace(/^Variation:\s*/i, '').trim();

                const masterPrimary = cleanKw(
                  step10SelectedPrimary || 
                  localProject.masterKeywords?.find(m => m.priority === 'Primary')?.keyword || 
                  sortedUniqueList[0]?.keyword || 
                  'Truck Parts Trinidad'
                );

                const masterSecondary = cleanKw(
                  step10SelectedSecondary || 
                  localProject.masterKeywords?.find(m => m.priority === 'Secondary')?.keyword || 
                  sortedUniqueList.find(s => cleanKw(s.keyword) !== masterPrimary)?.keyword || 
                  'Truck Spare Parts'
                );

                const masterTertiary = cleanKw(
                  step10SelectedTertiary || 
                  localProject.masterKeywords?.find(m => m.priority === 'Tertiary')?.keyword || 
                  sortedUniqueList.find(s => cleanKw(s.keyword) !== masterPrimary && cleanKw(s.keyword) !== masterSecondary)?.keyword || 
                  'Heavy Duty Truck Accessories'
                );

                const primaryVariations = (localProject.variations || [])
                  .filter(v => cleanKw(v.variationOf).toLowerCase() === masterPrimary.toLowerCase() && cleanKw(v.keyword).toLowerCase() !== masterPrimary.toLowerCase())
                  .map(v => cleanKw(v.keyword));

                const secondaryVariations = (localProject.variations || [])
                  .filter(v => cleanKw(v.variationOf).toLowerCase() === masterSecondary.toLowerCase() && cleanKw(v.keyword).toLowerCase() !== masterSecondary.toLowerCase())
                  .map(v => cleanKw(v.keyword));

                const tertiaryVariations = (localProject.variations || [])
                  .filter(v => cleanKw(v.variationOf).toLowerCase() === masterTertiary.toLowerCase() && cleanKw(v.keyword).toLowerCase() !== masterTertiary.toLowerCase())
                  .map(v => cleanKw(v.keyword));

                return (
                  <ContentPlacementWorkstation
                    project={localProject}
                    masterPrimary={masterPrimary}
                    primaryVariations={primaryVariations}
                    masterSecondary={masterSecondary}
                    secondaryVariations={secondaryVariations}
                    masterTertiary={masterTertiary}
                    tertiaryVariations={tertiaryVariations}
                    onUpdateProject={(updated) => updateProject(updated)}
                  />
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 14: QUESTION AEO WORKSTATION */}
          {activeStep === 13 && (
            <KeywordStepGuide
              stepNumber={14}
              stepTitle="Answer Engine Optimization (AEO) & Conversational Question Headings"
              whyAreWeDoingThis="Instructor Methodology (Transcript): Format conversational question queries into <h2> Subheadings followed immediately by a concise 40-50 word Direct Answer (no fluff) to rank in Google AI Overviews, Voice Search & AI Chatbots."
              whatYouNeedBeforeStarting={['Finalized Primary Keywords', 'Google PAA & AnswerThePublic Questions']}
              exactlyWhatToDo={[
                'Select Homepage or a Service Page to manage AEO question headings.',
                'Identify conversational queries from Google PAA, AnswerThePublic, or Reddit.',
                'Format each question as an <h2> subheading.',
                'Write a direct, conversational 40-50 word answer without fluff underneath.'
              ]}
              whatToLookFor={['Conversational <h2> Question headings', '40-50 word Direct Answer', 'Zero fluff or rambling filler']}
              whatToRecord={['Question Headings List', 'Direct Answer Copy List']}
              lectureExample={{
                scenario: 'Instructor optimizes content for Voice Search and Google AI Overviews.',
                action: 'Adds <h2>How to find the best general contractor in Calgary?</h2> with 42-word direct answer.',
                result: 'Gets featured as the primary source answer in Google Voice Search & AI Overviews.'
              }}
              doneWhen="All pages have formatted AEO H2 Question Headings and Direct Answers."
              isStepValidated={true}
              onPrevious={() => handleStepChange(12)}
              onContinue={() => handleStepChange(14)}
            >
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList } = accumulated;
                const sortedUniqueList = [...uniqueList].sort((a, b) => {
                  const volA = getVolumeForKeyword(a.keyword).status === 'collected' ? (getVolumeForKeyword(a.keyword).volume ?? 0) : -1;
                  const volB = getVolumeForKeyword(b.keyword).status === 'collected' ? (getVolumeForKeyword(b.keyword).volume ?? 0) : -1;
                  return volB - volA;
                });

                const cleanKw = (kw: string) => (kw || '').replace(/^Variation:\s*/i, '').trim();
                const hpMasterPrimary = localProject.masterKeywords?.find(m => m.priority === 'Primary')?.keyword;
                const hpMasterSecondary = localProject.masterKeywords?.find(m => m.priority === 'Secondary')?.keyword;

                const masterPrimary = cleanKw(step10SelectedPrimary || hpMasterPrimary || sortedUniqueList[0]?.keyword || '');
                const masterSecondary = cleanKw(step10SelectedSecondary || hpMasterSecondary || sortedUniqueList.find(s => cleanKw(s.keyword).toLowerCase() !== masterPrimary.toLowerCase())?.keyword || '');

                return (
                  <QuestionAeoWorkstation
                    project={localProject}
                    masterPrimary={masterPrimary}
                    masterSecondary={masterSecondary}
                    onUpdateProject={(updated) => updateProject(updated)}
                  />
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 15: FAQ DIRECT ANSWER SCHEMA WORKSTATION */}
          {activeStep === 14 && (
            <KeywordStepGuide
              stepNumber={15}
              stepTitle="FAQ Direct Answer Schema & Featured Snippets"
              whyAreWeDoingThis="Instructor Methodology (Lecture 11): Generate valid JSON-LD FAQ Schema code from your finalized Q&A pairs to trigger rich snippet accordion dropdowns directly on Google Search Results Pages."
              whatYouNeedBeforeStarting={['Finalized Q&A Pairs from Step 14']}
              exactlyWhatToDo={[
                'Review the Q&A pairs for Homepage and Service Pages.',
                'Generate structured JSON-LD FAQ Schema code.',
                'Inspect live Google SERP Accordion Dropdown Preview.',
                'Copy JSON-LD code into website <head> section.'
              ]}
              whatToLookFor={['Valid JSON-LD schema markup', 'Clean Google SERP Accordion Dropdowns']}
              whatToRecord={['JSON-LD Schema Script Code']}
              lectureExample={{
                scenario: 'Instructor builds FAQ block for client site.',
                action: 'Generates JSON-LD schema script and previews SERP accordions.',
                result: 'Triggers FAQ accordion dropdowns directly on Google SERP.'
              }}
              doneWhen="JSON-LD FAQ Schema is generated and verified."
              isStepValidated={true}
              onPrevious={() => handleStepChange(13)}
              onContinue={() => handleStepChange(15)}
            >
              {(() => {
                const accumulated = getAccumulatedKeywords(8);
                const { uniqueList } = accumulated;
                const sortedUniqueList = [...uniqueList].sort((a, b) => {
                  const volA = getVolumeForKeyword(a.keyword).status === 'collected' ? (getVolumeForKeyword(a.keyword).volume ?? 0) : -1;
                  const volB = getVolumeForKeyword(b.keyword).status === 'collected' ? (getVolumeForKeyword(b.keyword).volume ?? 0) : -1;
                  return volB - volA;
                });

                const cleanKw = (kw: string) => (kw || '').replace(/^Variation:\s*/i, '').trim();
                const hpMasterPrimary = localProject.masterKeywords?.find(m => m.priority === 'Primary')?.keyword;
                const hpMasterSecondary = localProject.masterKeywords?.find(m => m.priority === 'Secondary')?.keyword;

                const masterPrimary = cleanKw(step10SelectedPrimary || hpMasterPrimary || sortedUniqueList[0]?.keyword || '');
                const masterSecondary = cleanKw(step10SelectedSecondary || hpMasterSecondary || sortedUniqueList.find(s => cleanKw(s.keyword).toLowerCase() !== masterPrimary.toLowerCase())?.keyword || '');

                return (
                  <FaqSchemaWorkstation
                    project={localProject}
                    masterPrimary={masterPrimary}
                    masterSecondary={masterSecondary}
                  />
                );
              })()}
            </KeywordStepGuide>
          )}

          {/* STEP 16: DUAL MASTER REPORT EXPORTS */}
          {activeStep === 15 && (
            <KeywordStepGuide
              stepNumber={16}
              stepTitle="Review Master Strategy & Export Client Executive & Worker Technical Reports"
              whyAreWeDoingThis="Generate 2 specialized deliverable reports: 1) Client Executive Presentation PDF (high-level, elegant, impressive 'What & Why' without leaking agency secret recipes), and 2) Internal Worker Technical Operations PDF (complete operational manual with density logs, Q&A copy, schema code & prompt logs)."
              whatYouNeedBeforeStarting={['Completed research dataset across Steps 1-15']}
              exactlyWhatToDo={[
                'Review final project dataset & page mappings.',
                'Export Client Executive Presentation PDF to send to the client.',
                'Export Internal Worker Technical Operations PDF for agency review.'
              ]}
              whatToLookFor={['Client-ready executive presentation', 'Complete internal technical operations manual']}
              whatToRecord={['Client Executive Presentation PDF', 'Internal Worker Operations PDF']}
              lectureExample={{
                scenario: 'Instructor finishes keyword strategy project.',
                action: 'Exports elegant Client PDF and internal technical operations manual.',
                result: 'Delivers impressive report to client and keeps detailed records for workers.'
              }}
              doneWhen="Both Client Presentation PDF and Internal Technical PDF are exported."
              isStepValidated={true}
              onPrevious={() => handleStepChange(14)}
              onContinue={() => KeywordPdfService.exportToPdf(localProject)}
            >
              <DualReportExportWorkstation project={localProject} />
            </KeywordStepGuide>
          )}
        </div>

        {/* RIGHT COLUMN: Live Continuous Report Side Panel */}
        {showLiveReport && (
          <div style={{ position: 'sticky', top: '20px', maxHeight: 'calc(100vh - 120px)' }}>
            <LiveReportPanel project={localProject} />
          </div>
        )}
      </div>
    </div>
  );
};
